import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import IconVipCrown from '../../components/IconVipCrown'
import { aiPhotoPaywallCustomContent } from '../utils/aiPhotoPaywallContent'
import { AI_PHOTO_KING_SOURCE_DETAIL } from '../constants'
import ReactDOM from 'react-dom'
import {
  Input,
  Popover,
  Spin,
  Tooltip,
  message as antdMessage,
  notification
} from 'antd'
import {
  ExclamationCircleFilled,
  InfoCircleOutlined,
  PaperClipOutlined,
  ReloadOutlined
} from '@ant-design/icons'
import MarkdownIt from 'markdown-it'
import { v4 as uuid } from 'uuid'
import {
  useAppDispatch,
  useAppSelector,
  useAgentCustomerDrawer,
  usePayment,
  useVipKind
} from '../../../app/hooks'
import CONFIG from '../../../constants/config'
import PointsStatusCard from '../../components/PointsStatusCard'
import { computeUsageState } from '../../components/PointsStatusCard/config'
import PointsPackModal from '../../components/PointsPackModal'
import paymentConfig from '../../../constants/payment'
import { getOSSToken, uploadToOSS } from '../../../actions/main'
import { consumeMapTrial } from '../../../actions/aiPhotoMap'
import desktopLib from '../../../utils/desktopLib'
import {
  ProgressStep,
  resolveProgressDetail,
  resolveProgressLabel
} from './aiPhotoJunLabels'
// 复用 aiClerk 的文件预览卡片：对话里展示 publish_artifact xlsx 的内联预览 + 全屏。
import { UniversalFilePreviewCard } from './UniversalFilePreviewCard'
import type { FileItem } from '../../aiClerk/components/blocks/types'
import {
  IconChat,
  IconClipboard,
  IconHourglass,
  IconLightbulb,
  IconRunningDot,
  IconClock,
  IconCloseLine,
  IconDownloadLine,
  IconHistoryLine,
  IconNewChatLine,
  IconPhone,
  IconWechat,
  IconSearch,
  IconSearchDoc,
  IconThinking,
  IconThumbDown,
  IconThumbUp,
  IconAiStar,
  MedalIcon
} from './aiPhotoJunIcons'
import PhotoMapDrawer from './PhotoMapDrawer'
import OnboardingCarousel from './OnboardingCarousel'
import OnboardingShortcutRow from './OnboardingShortcutRow'
import {
  OnboardingCardBackendItem,
  OnboardingCardItem,
  mergeOnboardingCards
} from './onboardingCardsCatalog'
import './AiPhotoJunPanel.scss'
import {
  trackAiB8112Click,
  trackAiB8112Completed,
  AiB8112SourceType,
  trackAiB8112Exposure,
  trackAiC8145Exposure,
  trackAiC8145Click,
  trackAiC8146Exposure,
  trackAiC8146Click,
  trackAiC8132Exposure,
  trackAiC8132Click,
  trackVipB8148Exposure,
  trackVipB8148Click,
  usageStateToB8112,
  usageStateToC8145,
  fileNameToTaskType,
  AiC8146Trigger,
  VipB8148SourceType,
  aggregateToolCalls
} from '../utils/aiPhotoJunTrack'
import AiPhotoBetaModal from './AiPhotoBetaModal'
import { AI_PHOTO_BETA_PRODUCT_KEY } from '../aiPhotoBetaGoods'
// 下载抽屉 file 类任务的彩色文件 icon: 按扩展名映射 7 种 (word/excel/csv/pdf/zip/image/other)
import iconFileWord from './icon_wiki-word_colorful.png'
import iconFileExcel from './icon_wiki-excel_colorful.png'
import iconFileCsv from './icon_file-csv_colorful.png'
import iconFilePdf from './icon_wiki-pdf_colorful.png'
import iconFileZip from './icon_file-zip_colorful.png'
import iconFileImage from './icon_wiki-image_colorful.png'
import iconFileUnknown from './icon_wiki-unknow_colorful.png'

const MAX_UPLOAD_BYTES = 500 * 1024 * 1024
/** 附件上传限制(照片王对话):单次选择数 / 会话累计数 / 上传并发数。数值可按需调整。 */
const MAX_ATTACH_PER_PICK = 9
const MAX_ATTACH_TOTAL = 20
const MAX_UPLOAD_CONCURRENCY = 3

/** 并发池:items 逐个经 fn 处理,同时最多 limit 个在跑(附件上传限并发,避免一次性全发) */
async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<unknown>
): Promise<void> {
  let cursor = 0
  const workers = Array.from(
    { length: Math.min(Math.max(1, limit), items.length) },
    async () => {
      while (cursor < items.length) {
        const idx = cursor
        cursor += 1
        await fn(items[idx])
      }
    }
  )
  await Promise.all(workers)
}
const OSS_BUCKET_NAME = 'net-cloud'
const IMAGE_EXT_RE = /\.(png|jpe?g|gif|bmp|webp|heic|heif|svg)$/i

/**
 * 下载抽屉 file 类任务的"彩色文件 icon + 底色"映射。
 * - 仅作用于 t.kind='file' 的 done 状态;t.kind='image' 走真实 url 缩略图。
 * - 扩展名优先取自 filename,无则 fallback 到 url 末段。
 * - 图片扩展名(png/jpg 等) 同样走真实 url 缩略图(产品要求),不走本表;只有 url 缺失或加载失败兜底显示 image icon。
 */
type FileThumbKind =
  | 'word'
  | 'excel'
  | 'csv'
  | 'pdf'
  | 'zip'
  | 'image'
  | 'other'
interface FileThumbStyle {
  kind: FileThumbKind
  iconSrc: string
  bgColor: string
  borderColor: string
}
const FILE_THUMB_STYLES: Record<FileThumbKind, FileThumbStyle> = {
  word: {
    kind: 'word',
    iconSrc: iconFileWord,
    bgColor: '#f5f9ff',
    borderColor: '#ecf3fd'
  },
  excel: {
    kind: 'excel',
    iconSrc: iconFileExcel,
    bgColor: '#f5fff7',
    borderColor: '#ecfdec'
  },
  csv: {
    kind: 'csv',
    iconSrc: iconFileCsv,
    bgColor: '#f5fff7',
    borderColor: '#ecfdec'
  },
  pdf: {
    kind: 'pdf',
    iconSrc: iconFilePdf,
    bgColor: '#fff5f5',
    borderColor: '#fdecec'
  },
  zip: {
    kind: 'zip',
    iconSrc: iconFileZip,
    bgColor: '#f5f9ff',
    borderColor: '#ecf3fd'
  },
  image: {
    kind: 'image',
    iconSrc: iconFileImage,
    bgColor: '#fff8e6',
    borderColor: '#fef5d6'
  },
  other: {
    kind: 'other',
    iconSrc: iconFileUnknown,
    bgColor: '#fafafa',
    borderColor: '#f4f4f4'
  }
}
function resolveFileThumbStyle(name: string): FileThumbStyle {
  const lower = (name || '').toLowerCase()
  if (/\.docx?($|\?)/.test(lower)) return FILE_THUMB_STYLES.word
  if (/\.xlsx?($|\?)/.test(lower)) return FILE_THUMB_STYLES.excel
  if (/\.(csv|tsv)($|\?)/.test(lower)) return FILE_THUMB_STYLES.csv
  if (/\.pdf($|\?)/.test(lower)) return FILE_THUMB_STYLES.pdf
  if (/\.(zip|tar|gz|7z|rar)($|\?)/.test(lower)) return FILE_THUMB_STYLES.zip
  if (/\.(png|jpe?g|webp|gif|bmp|heic|heif|svg)($|\?)/.test(lower))
    return FILE_THUMB_STYLES.image
  return FILE_THUMB_STYLES.other
}
function isImageByName(name: string): boolean {
  return /\.(png|jpe?g|webp|gif|bmp|heic|heif|svg)($|\?)/i.test(name || '')
}

declare const __API_SERVER_HOST__: string

const { TextArea } = Input
const md = new MarkdownIt({
  linkify: true,
  breaks: true
})

const AI_CLAW_BASE_URL = __API_SERVER_HOST__ + '/web/xiaohei-claw'

/** 浮窗尺寸约束:默认 420×640,min 360×480,max 560×min(840, 90vh) */
const PANEL_DEFAULT_WIDTH = 380
const PANEL_DEFAULT_HEIGHT = 640
const PANEL_MIN_WIDTH = 360
const PANEL_MIN_HEIGHT = 480
const PANEL_MAX_WIDTH = 900
const PANEL_MAX_HEIGHT_CAP = 840

type ChatRole = 'user' | 'assistant'

interface ContextMessage {
  role: string
  content: string
  user_name?: string
  created_at?: string
  /** 后端 assistant 消息持久化的结构化产物 JSON 字符串,包含 cards/ask/suggestions/page_filter */
  attachments?: string
  /** 后端稳定 message_id (UUID), assistant 消息用它向 /feedback 提交点赞/评论。
   * 历史回填行格式 "legacy-{db_id}", 这种消息前端不展示反馈控件。 */
  message_id?: string
}

/** 用户对一条 assistant 消息的反馈, 与后端 /feedback 接口对齐。 */
type FeedbackRating = 0 | 1 | 2
interface MessageFeedbackState {
  rating: FeedbackRating
  comment?: string
}

interface AskQuestionOption {
  label: string
  description?: string
}
interface AskQuestion {
  question: string
  header: string
  options: AskQuestionOption[]
  multi_select?: boolean
}
interface AskPayload {
  questions: AskQuestion[]
  /** 用户已作答的 question.question → label(s) 映射,作答后置为只读 */
  answered?: Record<string, string>
}
interface RankingItem {
  rank: number
  name: string
  value: string
  unit?: string
  hint?: string
  /** 老字段:成员排行专用,等价于 filter_payload={user_ids:[user_id]}。新代码统一用 filter_payload */
  user_id?: string
  /** 通用维度跳转: 后端按任意 V5 filter 维度生成的 ranking 都能在 item 级带上,
   * 前端检测到就让 value 可点击,点击 = 把 card 顶层口径 + 本 item 的 filter_payload 合并后跳主页过滤。
   * 例: 按城市排行 → filter_payload={locations:[{name:"北京"}], location_key:"city"} */
  filter_payload?: FilterStructured
}
/**
 * 完整 V5 filter 维度 —— PageFilterPayload / CardStat / CardRanking 共用。
 * 字段语义跟 photoMcp 工具入参 / apply_page_filter / V5SearchPhoto.prepareSearchFilter 一一对齐。
 * 父组件拿到 → 1:1 dispatch 到主页 FilterState,**不再走 by_nlp 反推**。
 */
interface FilterWatermark {
  watermark_base_id?: string
  group_watermark_id?: string
}
interface FilterLocation {
  name: string
  detail?: string
  type?: number
  poi_type?: number
}
interface FilterStructured {
  keywords?: string[]
  user_ids?: string[]
  exclude_user_ids?: string[]
  department_ids?: string[]
  start_time?: string
  end_time?: string
  source_types?: number[]
  media_types?: number[]
  watermarks?: FilterWatermark[]
  locations?: FilterLocation[]
  location_key?: string
  exclude_keywords?: string[]
  exist_watermark?: number // 0=不限 1=仅有 2=仅无
  label_ids?: string[]
  label_match_all?: boolean
}

/**
 * 把 AI 的结构化筛选(snake_case)转成 map_agg 接口形状(camelCase,与 v5 同款),
 * 让排行榜等聚合卡把"这张卡的范围"直接丢给照片地图(PhotoMapDrawer 的 mapFilter)。
 * start_time/end_time 已是 "2006-01-02 15:04:05" 格式,直接进 times[{start,end}];
 * user_ids/department_ids 交后端按 ID 过滤,canViewUsers 兜底看照片权限。
 */
const structuredToMapFilter = (fs: FilterStructured): Record<string, any> => ({
  searchKeywords: fs.keywords || [],
  mediaType:
    fs.media_types && fs.media_types.length > 0 ? fs.media_types[0] : null,
  sourceType:
    fs.source_types && fs.source_types.length > 0 ? fs.source_types[0] : null,
  times:
    fs.start_time && fs.end_time
      ? [{ start: fs.start_time, end: fs.end_time }]
      : [],
  userIDList: fs.user_ids || [],
  excludeUserIDList: fs.exclude_user_ids || [],
  departmentIDList: fs.department_ids || [],
  watermarks: (fs.watermarks || []).map((w: any) => ({
    watermarkBaseID: w.watermark_base_id || '',
    groupWatermarkID: w.group_watermark_id || ''
  })),
  locations: (fs.locations || []).map((l) => ({
    name: l.name,
    detail: l.detail || '',
    type: l.type || 0
  })),
  useSegment: true,
  useSynonym: false,
  useSuggestedSearch: false,
  existWatermark: fs.exist_watermark || 0,
  excludeKeywords: fs.exclude_keywords || [],
  labelIDs: fs.label_ids || [],
  labelMatchAll: fs.label_match_all || false,
  locationRecommend: fs.location_key
    ? { key: fs.location_key, word: fs.location_key }
    : null
})

const parseStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined
  const arr = value
    .filter((x): x is string => typeof x === 'string')
    .map((x) => String(x))
  return arr.length > 0 ? arr : undefined
}

const parseNumberArray = (value: unknown): number[] | undefined => {
  if (!Array.isArray(value)) return undefined
  const arr = value
    .map((x) =>
      typeof x === 'number'
        ? x
        : typeof x === 'string' && x.trim() !== ''
          ? Number(x)
          : NaN
    )
    .filter((x) => Number.isFinite(x))
  return arr.length > 0 ? arr : undefined
}

const parseWatermarkArray = (value: unknown): FilterWatermark[] | undefined => {
  if (!Array.isArray(value)) return undefined
  const arr = value
    .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
    .map((x) => ({
      watermark_base_id:
        typeof x.watermark_base_id === 'string'
          ? x.watermark_base_id
          : undefined,
      group_watermark_id:
        typeof x.group_watermark_id === 'string'
          ? x.group_watermark_id
          : undefined
    }))
    .filter((x) => x.watermark_base_id || x.group_watermark_id)
  return arr.length > 0 ? arr : undefined
}

const parseLocationArray = (value: unknown): FilterLocation[] | undefined => {
  if (!Array.isArray(value)) return undefined
  const arr = value
    .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
    .filter((x) => typeof x.name === 'string')
    .map((x) => ({
      name: String(x.name),
      detail: typeof x.detail === 'string' ? x.detail : undefined,
      type: typeof x.type === 'number' ? x.type : undefined,
      poi_type: typeof x.poi_type === 'number' ? x.poi_type : undefined
    }))
  return arr.length > 0 ? arr : undefined
}

interface CardRanking extends FilterStructured {
  type: 'ranking'
  title: string
  subtitle?: string
  items: RankingItem[]
  /** 榜单级时间口径(等同 start_time/end_time,旧字段名留兼容,新代码请用 start_time/end_time) */
  time_start?: string
  time_end?: string
}

/**
 * PlanStep / PlanPayload —— propose_plan SSE 事件载荷。
 * agent 调 propose_plan 后 pause,前端气泡里渲染执行计划 + "同意执行 / 我要调整"按钮。
 */
interface PlanStep {
  title: string
  description?: string
}
interface PlanPayload {
  summary: string
  steps: PlanStep[]
  estimated_time?: string
  risk_level?: 'low' | 'medium' | 'high'
  /** 用户已作答(同意/拒绝),作答后卡片转只读 */
  answered?: 'approved' | 'rejected'
}

/**
 * DownloadTask —— 下载侧栏的统一条目。
 * 数据源:
 *   - SSE download_task 事件: 任务创建瞬间推送 (即刻可见)
 *   - /context attachments.downloads: session 内的归档 (跨刷新恢复)
 *   - /download_tasks?refresh=1: 全量 user+group 任务持久化列表 (跨 session 恢复 + pending 状态实时刷新)
 * 状态轮询统一走 /download_tasks?refresh=1, 后端按 subkind 分发到 asynctask.Handler 拿状态。
 */
/**
 * SessionPreview —— GET /sessions 返回的列表项。
 * - title 是 LLM 生成的 ≤16 字短语,可能为空(还在生成中或失败)
 * - first_input 是该 session 的第一条 user 消息(截到 60 字),title 缺失时兜底
 * - last_at ISO 时间字符串,前端按今天/过去 7 天/更早分组
 */
interface SessionPreview {
  session_id: string
  title: string
  first_input: string
  last_at: string
  message_count: number
}

interface DownloadTask {
  task_id: string
  kind: 'image' | 'file'
  /** kind='file' 时的细分类型。新轮询走 /download_tasks?refresh=1,后端按 subkind
   *  分发到 asynctask.Handler 拿状态,前端不再按 subkind 选 endpoint。
   *  - export_photos: 原图 ZIP 打包
   *  - export_watermark_data: 水印台账 Excel
   *  - export_attendance: 考勤导出 Excel
   *  - publish_artifact: Code-first 兜底产物(shell_execute 生成的 Excel/PDF/CSV/PNG)
   *  - generate_image: kind='image' 的细分 */
  subkind?:
    | 'export_photos'
    | 'export_watermark_data'
    | 'export_attendance'
    | 'publish_artifact'
    | 'generate_image'
  /** kind='file' 走 photoMcp 链路,轮询时要带 group_id (后端无状态,每次现查) */
  group_id?: string
  /** kind='image' 用 prompt;kind='file' 用 title;UI 渲染时统一取这个 */
  prompt: string
  status: 'pending' | 'done' | 'error'
  url?: string
  filename?: string
  size_bytes?: number
  provider?: string
  err_msg?: string
  /** 0-100,后端 export_photos 类任务返回的打包进度;新链路不再使用,保留向后兼容 */
  progress?: number
  created_at: number
  // ── 表格类产物(publish_artifact xlsx)的预览数据,用于对话富预览卡片 FilePreviewCard ──
  /** 裁剪后的预览文件 url(未超限时即源文件),点「预览」开全屏 */
  preview_url?: string
  /** 原始总行数,卡片"X 条记录" */
  total_rows?: number
  /** 原始总列数,卡片"Y 列" */
  total_cols?: number
  /** 预览是否为截断子集,全屏顶部提示用 */
  truncated?: boolean
}

/**
 * PageFilterPayload —— apply_page_filter SSE 事件载荷。
 * agent 调 apply_page_filter 工具后,后端立即推,前端:
 *   1. 气泡内小卡回显"已同步到主页 · nl_query · total · preview_thumbs[]"
 *   2. 调 onApplyPageFilter 通知父组件(photov2),父组件 dispatch setSearchFilter+fetchPhotosV2
 */
interface PageFilterPayload extends FilterStructured {
  nl_query: string
  preview_thumb_urls?: string[]
  total?: number
  /** user_ids 同序的展示名,RankingCard 跳转时携带,handleApplyPageFilter 用作 base 找不到的 fallback name。
   * 没这个字段时 fallback 到 user_id 字符串(老行为),但会让"筛选人员"按钮显示一串 ID。 */
  user_display_names?: string[]
  /** AI 搜索 chip 关闭埋点用 (web_workgroup_picspage_click sessionID/msgID 字段, spec 标注
   * "clickItem=点击关闭AI搜索chip 时上报")。从 AiPhotoJunPanel 沿 onApplyPageFilter 流过来,
   * 主页存进 FilterState, 用户点 chip × 时读出来报神策。
   * - source_session_id: 本次 AI 会话 ID
   * - source_msg_id: 产生这个 chip 的 AI 回答消息 ID(后端 SSE start 给的, 不是前端临时 id) */
  source_session_id?: string
  source_msg_id?: string
}
interface CardStat extends FilterStructured {
  type: 'stat'
  title: string
  subtitle?: string
  value: string
  unit?: string
  trend?: { direction: 'up' | 'down' | 'flat'; text: string }
  /**
   * 仅作 chip 文案显示用(给用户看的"陈好近一周拍的照片"这种人话)。
   * 不再用作 NLP 重 parse 入口。结构化筛选字段(继承自 FilterStructured)空时,
   * 才回退到 onApplyNlQuery(nl_query) 走 by_nlp 兜底。
   */
  nl_query?: string
  /**
   * 兼容旧 ranking 命名 — 后端 render_card 一直用 time_start/time_end,
   * 这里读两份(start_time 优先)。后端 schema 字段统一前不要删。
   */
  time_start?: string
  time_end?: string
}
interface CardHtml {
  type: 'html'
  html: string
}
type CardPayload = CardRanking | CardStat | CardHtml

interface ChatMessage {
  id: string
  role: ChatRole
  content: string
  pending?: boolean
  /** 结构化的执行步骤(取代原 progressLog 字符串数组),折叠/展开两态渲染 */
  progressSteps?: ProgressStep[]
  /** 活动区手动展开态。undefined=默认折叠 */
  activityOpen?: boolean
  /** 起始/结束时间戳(ms),用于完成态展示 "用时 X.Xs" */
  runStartedAt?: number
  runDoneAt?: number
  /** 后端 SSE start 事件携带的 trace_id,本轮调用全链路唯一标识。隐藏入口的"调用链路"弹窗用得上。 */
  traceID?: string
  /** 流式下模型 partial 输出,仅在展开态展示 */
  streamPreview?: string
  /** 后端 ask_user_question 事件挂载,在气泡里渲染按钮组 */
  ask?: AskPayload
  /** 后端 render_card 事件挂载 + 前端兜底自动合成的可视化卡片 */
  cards?: CardPayload[]
  /** publish_artifact 产物的富预览卡片(按消息挂载:live SSE download_task 挂 /
   *  历史 attachments.downloads 恢复;仅 subkind=publish_artifact 且带 preview_url 的 xlsx) */
  previewFiles?: FileItem[]
  /** 前端把 assistant 文本里的 markdown 表格兜底转成卡片后的提示 */
  fallbackNotice?: string
  /** 后端 suggest_questions 事件挂载,"猜你想问" 3 条按钮 */
  suggestions?: string[]
  /** 后端 apply_page_filter 事件挂载:气泡内"已同步到主页"小卡 */
  pageFilter?: PageFilterPayload
  /** 后端 propose_plan 事件挂载:气泡内执行计划卡,用户点"同意/调整"决定下一轮 */
  plan?: PlanPayload
  /** 用户消息携带的附件(只在本条消息渲染缩略图/文件条,不参与后端) */
  messageAttachments?: Array<{
    id: string
    name: string
    url: string
    isImage: boolean
  }>
  /** 后端 assistant 消息的稳定 message_id, SSE start 事件推下来 / /context 历史回填得到。
   * 仅 role='assistant' 才会有; legacy-* 开头表示历史回填消息, 不展示反馈控件。 */
  messageId?: string
  /** 用户对这条 assistant 消息的反馈状态 */
  feedback?: MessageFeedbackState
  /** 本轮 /run 的 AI 思考轮数, 由 SSE progress.kind='round' 累加得到。
   * 仅 role='assistant'; 用于神策埋点 ai_b8112_completed.number2。 */
  roundCount?: number
  /** 本轮 /run 后端 SSE done.cost_saving (积分扣费)。0 = 没扣 / 后端未拿到。
   * 仅 role='assistant'; 用于神策埋点 ai_b8112_completed.costSaving。 */
  costSaving?: number
  /** 本条用户消息触发的 /run 输入文本, 落在对应 assistant 消息上,
   * 神策埋点 ai_b8112_completed.content 用。SSE assistant 消息从空 pending 起步,
   * 这里冗余存一份方便完成时拿到。 */
  userQuestion?: string
  /** 本条 assistant 消息以"中断"形态收场 (用户停止 / 静默超时 / 服务端错 / SSE 流意外结束),
   * 渲染时在气泡底部展示一行提示 + "重试"按钮; 点击按钮用 userQuestion 重新发起 sendMessage。
   *
   * 旧逻辑只在用户主动停 / 服务端报错时把 pending 关掉, "后端发 done 然后正常关流"
   * 这条路径漏了关 pending, UI 会一直显示 "正在 xxx X.Xs" 假装在跑 — 即 1m46s 卡死 bug。
   * 现在 finally 兜底强制把 pending 关掉, interrupted 字段额外标记是哪种异常结束 (成功完成时不挂)。 */
  interrupted?: {
    /** aborted=用户主动停 / timeout=静默 120s 无新数据 / error=服务端报错 /
     *  unexpected_close=流跑完但没收到 done 事件 /
     *  empty_response=收到 done 但 message 没有任何实际产出
     *  (无文本/卡片/page filter/plan/ask/suggestions, AI 跑了一通什么都没回,
     *  典型场景: 后端 agent 兜底失败 / 工具调用空结果 / LLM 返回空字符串)。 */
    reason:
      | 'aborted'
      | 'timeout'
      | 'error'
      | 'unexpected_close'
      | 'empty_response'
    /** 渲染在重试按钮旁的提示文案 */
    hint: string
  }
}

interface RecentQuery {
  query: string
  created_at: string
}

interface AttachmentItem {
  id: string
  name: string
  size: number
  url?: string
  previewUrl?: string
  uploading: boolean
  error?: string
  /** 原始 File 引用,失败重试时直接复用,无需用户重新选择 */
  file?: File
}

function isImageFile(name: string) {
  return IMAGE_EXT_RE.test(name)
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  if (bytes < 1024 * 1024 * 1024)
    return (bytes / 1024 / 1024).toFixed(1) + ' MB'
  return (bytes / 1024 / 1024 / 1024).toFixed(2) + ' GB'
}

interface AiPhotoJunPanelProps {
  userId: string
  userName: string
  groupId: string
  /** true=浮窗显示,false=用 display:none 隐藏(不销毁状态,保持 SSE 流) */
  open: boolean
  /**
   * 本次打开面板的入口来源 (ai_b8112_exposure.fromPlace, 平台枚举只有这两个值)。
   * 父组件在每个打开调用点先写 ref 再切 open, 引导气泡=新功能弹窗, 其余=按钮。
   */
  openFromPlace?: 'AI问照片新功能弹窗' | '点击AI问照片按钮'
  /** 主页当前展示的团队照片总数 (ai_b8112_exposure.photoNumber) */
  photoNumber?: number
  /** 点击 × 或"最小化"时回调,由父组件切到 FAB 态 */
  onMinimize?: () => void
  /**
   * 主页照片筛选联动入口。有两处触发:
   *   1. agent 调 apply_page_filter → SSE apply_page_filter 事件
   *   2. 用户点 RankingCard 里 value(127 张) → 构造一个"看这个人的照片"的 payload
   * 父组件(photov2/index.tsx)应 dispatch setSearchFilter + fetchPhotosV2 做主页同步。
   */
  onApplyPageFilter?: (payload: PageFilterPayload) => void
  /**
   * StatCard 大数字点击 → 把 stat.nl_query 灌回主页搜索框,走主页自己的 NLP 链路(by_nlp 接口)重搜。
   * 等价于"用户手动在搜索框敲完这句话再按回车"。
   * 父组件应:调 fetchNlpFilterResult + dispatch setSearchFilter + fetchPhotosV2。
   */
  onApplyNlQuery?: (text: string) => void
  /** hover 卡片快捷问题: 父组件传入后自动发送,发完置空 */
  pendingQuestion?: string
  onPendingQuestionSent?: () => void
}

function buildMessageId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

/** 短格式数字: 999 → "999", 1234 → "1.2k", 12345 → "1.23万", 9998000 → "999.8万", 1234567890 → "12.3亿"。
 *
 * 万级保留 2 位小数(去掉尾零), 让用户能看出小额扣减 —— 否则像 1000万 / 999.8万
 * 都被舍入成 "1000万", 用户感觉积分没变。`replace(/\.?0+$/, '')` 在小数末尾全 0 时
 * 同时把小数点也吃掉(如 "10.00万" → "10万"), 避免视觉噪音。 */
function shortNumber(n: number): string {
  if (!isFinite(n)) return '--'
  if (n < 0) return '0'
  if (n < 1000) return String(n)
  if (n < 10000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  if (n < 1_0000_0000)
    return (n / 10000).toFixed(2).replace(/\.?0+$/, '') + '万'
  return (n / 1_0000_0000).toFixed(1).replace(/\.0$/, '') + '亿'
}

/** 每次查询约消耗的积分 (用于"约可查询 X 次"的估算)。
 *  photo / sandbox_storage 的积分消耗已按 5 倍计费, 故单次约 25 万 (原 5 万 × 5)。 */
const CREDITS_PER_QUERY = 250000

type VipLevel = 'nonvip' | 'vip' | 'svip' | 'vvip' | 'evip'

/** 各会员等级对应的月赠送额度
 *  nonvip=非会员(50w), vip=基础(50w), svip=高级(600w), vvip=超级(3000w) */
function getMonthlyQuota(vipLevel: VipLevel): number {
  switch (vipLevel) {
    case 'nonvip':
      return 500_000
    case 'vip':
      return 500_000
    case 'svip':
      return 6_000_000
    case 'vvip':
      return 30_000_000
    default:
      return 500_000
  }
}

/** 各会员等级对应的升级 CTA 文案；超级会员用增购
 *  vip=基础, svip=高级, vvip=超级 */
function getUpgradeCta(vipLevel: VipLevel): {
  text: string
  action: 'upgrade' | 'purchase'
} {
  switch (vipLevel) {
    case 'nonvip':
      return { text: '升级会员50万积分/月起', action: 'upgrade' }
    case 'vip':
      return { text: '升级会员600万积分/月起', action: 'upgrade' }
    case 'svip':
      return { text: '升级会员3000万积分/月', action: 'upgrade' }
    case 'vvip':
      return { text: '增购积分(永久有效)', action: 'purchase' }
    default:
      return { text: '升级会员50万积分/月起', action: 'upgrade' }
  }
}

/** 是否高级会员(VVIP)及以上 —— 文件下载 / 照片地图等会员专属能力的门槛(VVIP 以下拦截)。
 *  vip_kind 数值不代表等级高低,故显式列举可用档位(VVIP/SVIP/EVIP)。 */
const isAtLeastVvip = (vipKind: number): boolean =>
  vipKind === CONFIG.VIP_KIND.VVIP ||
  vipKind === CONFIG.VIP_KIND.SVIP ||
  vipKind === CONFIG.VIP_KIND.EVIP

/** VVIP 以下点照片地图入口 → 打开开通页(定位高级会员档),复用 AI 问照片的会员卡点文案。 */
const openMapPaywall = (
  vipKind: number,
  payment: { openVipByKind: (...args: any[]) => void }
): void => {
  payment.openVipByKind(
    CONFIG.VIP_KIND.VVIP,
    AI_PHOTO_KING_SOURCE_DETAIL,
    false,
    // trackSource → web_click_page_workgroup_open_vip string2='照片地图入口'(范 2026-06-23 spec)
    aiPhotoPaywallCustomContent({
      vipKind,
      usedCredits: 0,
      trackSource: '照片地图入口'
    })
  )
}

/**
 * 照片地图入口门(两个入口共用):VVIP+ 直接开;VVIP 以下走后端「免费体验一次」——
 * firstTime 放行并标记本次为体验,否则弹开通页;关闭时若本次是体验,提示「体验结束」。
 * - openMapGuarded(allow):allow() 里真正 setMapVisible(true)。
 * - handleMapClose(close):close() 里真正 setMapVisible(false)。
 */
function useMapEntryGate() {
  const dispatch = useAppDispatch()
  const groupId = useAppSelector((s: any) => s.group.groupId)
  const vipKind = useVipKind()
  const payment = usePayment()
  const trialOpenRef = useRef(false)
  const canUseMap = isAtLeastVvip(vipKind)

  const openMapGuarded = useCallback(
    async (allow: () => void) => {
      if (canUseMap) {
        allow()
        return
      }
      let firstTime = false
      try {
        const res: any = await dispatch(consumeMapTrial(groupId) as any)
        firstTime = !!(res && res.firstTime)
      } catch {
        // 网络错保守按「非首次」处理 → 走开通页(后端 DB 错已 fail-open 返 firstTime=true)
        firstTime = false
      }
      if (firstTime) {
        trialOpenRef.current = true
        allow()
      } else {
        openMapPaywall(vipKind, payment)
      }
    },
    [canUseMap, dispatch, groupId, vipKind, payment]
  )

  const handleMapClose = useCallback((close: () => void) => {
    close()
    if (trialOpenRef.current) {
      trialOpenRef.current = false
      antdMessage.info('地图展示功能体验结束,开通高级会员可继续使用')
    }
  }, [])

  return { openMapGuarded, handleMapClose }
}

/** 当前会员档位 → 升级目标档位 vipKind(打开会员弹窗时默认定位到「升一档」,
 *  而不是永远落在基础会员 —— 刘洪 2026-06 反馈)。
 *  等级排序:非会员 < VIP(基础) < VVIP(高级) < SVIP(超级) < EVIP(企业);
 *  注意 vip_kind 数值不代表等级高低(VIP=1,SVIP=2,VVIP=3,EVIP=4)。
 *  超级/企业会员的 CTA 是「增购积分」(走积分加量包,不到这里),所以实际入参只会是
 *  非会员 / 基础 / 高级 三种,兜底 case 仅为健壮性。 */
function getUpgradeTargetVipKind(currentVipKind: number): number {
  switch (currentVipKind) {
    case CONFIG.VIP_KIND.VIP: // 基础 → 高级
      return CONFIG.VIP_KIND.VVIP
    case CONFIG.VIP_KIND.VVIP: // 高级 → 超级
      return CONFIG.VIP_KIND.SVIP
    case CONFIG.VIP_KIND.SVIP: // 超级 → 企业(兜底,正常走增购到不了这)
    case CONFIG.VIP_KIND.EVIP: // 企业(已最高,兜底)
      return CONFIG.VIP_KIND.EVIP
    default:
      // 非会员 → 基础
      return CONFIG.VIP_KIND.VIP
  }
}

/** 积分不足弹窗里展示的会员三档积分权益(写死,与设计稿一致)。
 *  vipKind 用于把用户「当前」所在档位标出来。 */
const INSUFFICIENT_TIERS: Array<{
  key: string
  vipKind: number
  name: string
  desc: string
  credit: string
  token: string
}> = [
  {
    key: 'basic',
    vipKind: CONFIG.VIP_KIND.VIP,
    name: '基础会员',
    desc: '团队轻量级查询',
    credit: '50万 积分',
    token: '500,000 token'
  },
  {
    key: 'advanced',
    vipKind: CONFIG.VIP_KIND.VVIP,
    name: '高级会员',
    desc: '适合中小团队',
    credit: '600万 积分',
    token: '6,000,000 token'
  },
  {
    key: 'super',
    vipKind: CONFIG.VIP_KIND.SVIP,
    name: '超级会员',
    desc: '适合大型团队经常性查询',
    credit: '3000万 积分',
    token: '30,000,000 token'
  }
]

interface CreditTipSegment {
  text: string
  style: 'normal' | 'bold-orange' | 'bold-blue'
  clickAction?: 'upgrade' | 'purchase'
}

interface CreditTipInfo {
  segments: CreditTipSegment[]
}

/**
 * 根据会员类型和积分余额/总额度计算底部积分提示文案（分段带颜色）。
 *
 * 4 种状态（优先级从高到低判断）:
 *   4. 积分 < 5w (不足 1 次查询)
 *   3. 已用 >= 90%
 *   1. 积分 = 总额度 (满额/未使用)
 *   2. 其他 (部分使用)
 */
function getCreditTipInfo(
  vipLevel: VipLevel,
  available: number,
  totalLimit: number
): CreditTipInfo {
  const queryCount = Math.floor(available / CREDITS_PER_QUERY)
  const usedRatio = totalLimit > 0 ? (totalLimit - available) / totalLimit : 0
  const cta = getUpgradeCta(vipLevel)

  // 状态 4: 不足 1 次查询 (积分 < 5w)
  if (available < CREDITS_PER_QUERY) {
    return {
      segments: [
        { text: '剩', style: 'normal' },
        { text: shortNumber(available), style: 'bold-orange' },
        { text: '积分，不足1次查询，', style: 'normal' },
        { text: `${cta.text} >`, style: 'bold-blue', clickAction: cta.action }
      ]
    }
  }

  // 状态 3: 已用 >= 90%
  if (usedRatio >= 0.9) {
    return {
      segments: [
        { text: '剩', style: 'normal' },
        { text: shortNumber(available), style: 'bold-orange' },
        { text: '积分，已用', style: 'normal' },
        { text: `${Math.round(usedRatio * 100)}%`, style: 'bold-orange' },
        { text: '，', style: 'normal' },
        { text: `${cta.text} >`, style: 'bold-blue', clickAction: cta.action }
      ]
    }
  }

  // 状态 1: 积分 = 总额度 (满额/未使用)
  if (totalLimit > 0 && available >= totalLimit) {
    if (vipLevel === 'nonvip') {
      return {
        segments: [
          { text: '赠送', style: 'normal' },
          { text: shortNumber(available), style: 'bold-orange' },
          { text: `积分，约可查询${queryCount}次`, style: 'normal' }
        ]
      }
    }
    return {
      segments: [
        { text: shortNumber(getMonthlyQuota(vipLevel)), style: 'bold-orange' },
        { text: `积分/月，约可查询${queryCount}次`, style: 'normal' }
      ]
    }
  }

  // 状态 2: 部分使用
  return {
    segments: [
      { text: '剩', style: 'normal' },
      { text: shortNumber(available), style: 'bold-orange' },
      { text: `积分，约可查询${queryCount}次`, style: 'normal' }
    ]
  }
}

function buildSessionId(userId: string, groupId: string) {
  return `ai-photo-jun-${groupId}-${userId}-${Date.now()}`
}

/**
 * normalizeMessages —— 把 /context 的 messages[] 拉回成前端 ChatMessage[]。
 *
 * 关键工作:解析 attachments JSON,把 assistant 消息在上次 SSE 里推过的 cards / ask / suggestions /
 * page_filter 恢复上来,让页面刷新后不只剩一堆纯文本。
 *
 * 特殊处理:
 *   - ask:如果后面跟着 user 消息(即用户已经答过),标记成 answered(只读态)
 *   - suggestions:只保留在**最后一条 assistant** 上,老消息的"猜你想问"早过期了
 *   - page_filter:回显卡片照常展示,但**不自动再 dispatch** 去改主页(刷新时用户没预期主页联动)
 */
function normalizeMessages(
  messages: ContextMessage[] | undefined
): ChatMessage[] {
  if (!Array.isArray(messages)) return []
  const valid = messages.filter(
    (item) =>
      item &&
      (item.role === 'user' || item.role === 'assistant') &&
      typeof item.content === 'string' &&
      item.content.trim()
  )
  const hydrated: ChatMessage[] = valid.map((item, idx) => {
    const base: ChatMessage = {
      id: buildMessageId(item.role),
      role: item.role as ChatRole,
      content: item.content
    }
    if (item.role === 'assistant' && typeof item.message_id === 'string') {
      base.messageId = item.message_id
    }
    if (item.role !== 'assistant' || !item.attachments) return base
    const parsed = safeParseAttachments(item.attachments)
    if (!parsed) return base
    // cards:结构和 SSE 推过来时一样,直接挂
    if (Array.isArray(parsed.cards)) {
      base.cards = parsed.cards as CardPayload[]
    }
    // ask:标记是否已答 —— ask 会 pause loop,只要后面还有任何 user 消息,就是用户答过了
    if (parsed.ask && typeof parsed.ask === 'object') {
      const askRaw = parsed.ask as { questions?: AskQuestion[] }
      const hasFollowUpUser = valid
        .slice(idx + 1)
        .some((m) => m.role === 'user')
      const answered: Record<string, string> = {}
      if (hasFollowUpUser && Array.isArray(askRaw.questions)) {
        // 没有原始选中 label,也不需要展示 "已选:xxx",这里用空串:
        //   - 保持 answered 非空 → hasAnswered=true → 按钮只读
        //   - 同时 {answered && <...>} 短路,不渲染具体 label
        askRaw.questions.forEach((q) => {
          if (q && q.question) answered[q.question] = ''
        })
      }
      if (askRaw.questions && askRaw.questions.length > 0) {
        base.ask = { questions: askRaw.questions, answered }
      }
    }
    // page_filter:仅还原小卡回显,不触发主页联动
    if (parsed.page_filter && typeof parsed.page_filter === 'object') {
      base.pageFilter = parsed.page_filter as PageFilterPayload
    }
    // plan:恢复执行计划卡。如果后面还有 user 消息,说明用户已经响应过,锁成 approved(按"同意"常态)
    if (parsed.plan && typeof parsed.plan === 'object') {
      const planRaw = parsed.plan as PlanPayload
      if (Array.isArray(planRaw.steps) && planRaw.steps.length > 0) {
        const hasFollowUpUser = valid
          .slice(idx + 1)
          .some((m) => m.role === 'user')
        base.plan = {
          summary: planRaw.summary || '',
          steps: planRaw.steps,
          estimated_time: planRaw.estimated_time,
          risk_level: planRaw.risk_level,
          answered: hasFollowUpUser ? 'approved' : undefined
        }
      }
    }
    // downloads:publish_artifact 产物里带 preview_url 的 xlsx,恢复成富预览卡片(按消息挂载,
    // 与 SSE 当时推送一致;普通下载条目不在这里渲染,仍走右上角下载列表)
    if (Array.isArray(parsed.downloads)) {
      const files = (parsed.downloads as unknown[])
        .map((d) =>
          d && typeof d === 'object'
            ? previewFileFromDownload(d as Record<string, unknown>)
            : null
        )
        .filter((f): f is FileItem => f !== null)
      if (files.length > 0) base.previewFiles = files
    }
    return base
  })
  // suggestions:只挂到最后一条 assistant(早已过期的不再展示)
  for (let i = hydrated.length - 1; i >= 0; i--) {
    if (hydrated[i].role !== 'assistant') continue
    const raw = valid[i]?.attachments
    if (!raw) break
    const parsed = safeParseAttachments(raw)
    if (parsed && Array.isArray(parsed.suggestions)) {
      const qs = (parsed.suggestions as unknown[]).filter(
        (s): s is string => typeof s === 'string'
      )
      if (qs.length > 0) {
        hydrated[i].suggestions = qs
      }
    }
    break
  }
  return hydrated
}

/**
 * mergeFeedbackIntoMessages —— 拉 /feedback 把已有的赞/踩/评论挂回 normalized ChatMessage[]。
 * 直接 mutate 入参数组(就地写, 不复制), 因为 normalize 之后还没 setMessages。
 * 对 legacy- 开头的 messageId 不发请求(后端也会拒); 静默吞错。
 */
async function mergeFeedbackIntoMessages(
  msgs: ChatMessage[],
  userID: string,
  sessionID: string
): Promise<void> {
  if (!msgs.length || !userID || !sessionID) return
  const resp = await fetch(
    `${AI_CLAW_BASE_URL}/feedback?user_id=${encodeURIComponent(
      userID
    )}&session_id=${encodeURIComponent(sessionID)}`,
    { method: 'GET', credentials: 'include' }
  )
  if (!resp.ok) return
  const data = await resp.json()
  const items = Array.isArray(data?.items) ? data.items : []
  if (!items.length) return
  const byMsgID = new Map<string, MessageFeedbackState>()
  items.forEach((it: any) => {
    if (!it || typeof it.message_id !== 'string') return
    const r =
      it.rating === 1 || it.rating === 2 ? (it.rating as FeedbackRating) : 0
    const c = typeof it.comment === 'string' ? it.comment : ''
    if (r === 0 && !c) return
    byMsgID.set(it.message_id, { rating: r, comment: c })
  })
  if (byMsgID.size === 0) return
  msgs.forEach((m) => {
    if (m.role !== 'assistant' || !m.messageId) return
    const fb = byMsgID.get(m.messageId)
    if (fb) m.feedback = fb
  })
}

/**
 * parsePreviewFields —— 从 download_task 原始对象抽出表格预览元信息(供对话卡片)。
 * SSE handler 与 /context 历史两处共用。卡片内联表由 FilePreviewCard 读取 preview_url 解析。
 */
function parsePreviewFields(
  obj: Record<string, unknown>
): Pick<
  DownloadTask,
  'preview_url' | 'total_rows' | 'total_cols' | 'truncated'
> {
  return {
    preview_url:
      typeof obj.preview_url === 'string' ? obj.preview_url : undefined,
    total_rows: typeof obj.total_rows === 'number' ? obj.total_rows : undefined,
    total_cols: typeof obj.total_cols === 'number' ? obj.total_cols : undefined,
    truncated: typeof obj.truncated === 'boolean' ? obj.truncated : undefined
  }
}

/**
 * previewFileFromDownload —— 把一条 download_task 原始对象转成富预览卡片用的 FileItem。
 *
 * 识别 publish_artifact（所有文件类型）和 generate_image，转成 FileItem 供
 * FilePreviewCard 渲染富预览卡片。export_photos / export_watermark_data / export_attendance
 * 等批量导出任务不走卡片（仍走下载列表）。
 *
 * 字段兜底:后端事件里 task_id=url=文件链接、文件名在 title 字段;都做兼容。
 */
function previewFileFromDownload(
  obj: Record<string, unknown>
): FileItem | null {
  const subkind = obj.subkind as string | undefined
  // DEBUG: 查看 download_task 原始数据
  // 只处理 publish_artifact 和 generate_image，其余走下载列表
  if (subkind !== 'publish_artifact' && subkind !== 'generate_image') {
    return null
  }
  const url =
    typeof obj.url === 'string' && obj.url
      ? obj.url
      : typeof obj.task_id === 'string'
        ? obj.task_id
        : ''
  if (!url) return null
  const title =
    typeof obj.title === 'string' && obj.title
      ? obj.title
      : typeof obj.filename === 'string'
        ? obj.filename
        : typeof obj.prompt === 'string'
          ? obj.prompt
          : ''
  const name = title || filenameFromUrl(url)
  const pv = parsePreviewFields(obj)
  const status = typeof obj.status === 'string' ? obj.status : 'pending'
  return {
    name,
    url,
    // generate_image 标记 type='image'，让卡片按图片渲染
    type: subkind === 'generate_image' ? 'image' : undefined,
    // desc 携带 status，让卡片知道任务是否完成
    desc: status,
    size:
      typeof obj.size_bytes === 'number'
        ? formatFileSize(obj.size_bytes)
        : undefined,
    preview_url: pv.preview_url || undefined,
    total_rows: pv.total_rows,
    total_cols: pv.total_cols,
    truncated: pv.truncated
  }
}

/**
 * extractDownloadsFromContext —— 从 /context 的 messages[] 扫出所有 download_task,
 * 按 created_at DESC 返回。供下载侧栏在页面刷新后也能展示历史任务。
 *
 * 后端 AttachmentCollector(internal/tools/tools.go) 把 download_task event 整 clone
 * 落到 messages[i].attachments.downloads[],字段跟 emit 时一致:
 *   { task_id, kind, subkind, title, prompt, group_id, status, url, created_at, ... }
 *
 * 历史 bug: 之前这里硬写 kind='image',导致 export_photos / export_watermark_data 类
 * file 任务被错认成 image,轮询时去查 /image_task 永远 404 → 刷新后看不到状态。
 * 现在严格按 raw 字段还原 kind/subkind/group_id,跟 SSE handler 处理逻辑一致。
 */
function extractDownloadsFromContext(
  messages: ContextMessage[] | undefined
): DownloadTask[] {
  if (!Array.isArray(messages)) return []
  const tasks: DownloadTask[] = []
  const seen = new Set<string>()
  for (const m of messages) {
    if (!m || m.role !== 'assistant' || !m.attachments) continue
    const parsed = safeParseAttachments(m.attachments)
    if (!parsed || !Array.isArray(parsed.downloads)) continue
    for (const raw of parsed.downloads as unknown[]) {
      if (!raw || typeof raw !== 'object') continue
      const obj = raw as Record<string, unknown>
      const id = typeof obj.task_id === 'string' ? obj.task_id : ''
      if (!id || seen.has(id)) continue
      seen.add(id)
      const kind: 'image' | 'file' = obj.kind === 'file' ? 'file' : 'image'
      const subkind =
        obj.subkind === 'export_photos' ||
        obj.subkind === 'export_watermark_data' ||
        obj.subkind === 'export_attendance' ||
        obj.subkind === 'publish_artifact' ||
        obj.subkind === 'generate_image'
          ? (obj.subkind as DownloadTask['subkind'])
          : undefined
      // kind=image 用 prompt 当标题; kind=file 用 title 字段
      const promptText =
        kind === 'file'
          ? typeof obj.title === 'string'
            ? obj.title
            : '文件导出'
          : typeof obj.prompt === 'string'
            ? obj.prompt
            : '生成图片'
      tasks.push({
        task_id: id,
        kind,
        subkind,
        group_id: typeof obj.group_id === 'string' ? obj.group_id : undefined,
        prompt: promptText,
        status:
          obj.status === 'done' || obj.status === 'error'
            ? obj.status
            : 'pending',
        url: typeof obj.url === 'string' ? obj.url : undefined,
        filename: typeof obj.filename === 'string' ? obj.filename : undefined,
        size_bytes:
          typeof obj.size_bytes === 'number' ? obj.size_bytes : undefined,
        provider: typeof obj.provider === 'string' ? obj.provider : undefined,
        err_msg: typeof obj.err_msg === 'string' ? obj.err_msg : undefined,
        created_at:
          typeof obj.created_at === 'number'
            ? obj.created_at
            : Math.floor(Date.now() / 1000),
        ...parsePreviewFields(obj)
      })
    }
  }
  tasks.sort((a, b) => b.created_at - a.created_at)
  return tasks
}

function safeParseAttachments(raw: string): Record<string, unknown> | null {
  if (!raw) return null
  try {
    const o = JSON.parse(raw)
    if (o && typeof o === 'object' && !Array.isArray(o)) {
      return o as Record<string, unknown>
    }
  } catch {
    // 静默:attachments 解析失败只是回显丢失,不影响文本内容
  }
  return null
}

function parseSSEChunk(chunk: string) {
  const blocks = chunk.split('\n\n').filter(Boolean)
  return blocks
    .map((block) => {
      let event = 'message'
      const dataLines: string[] = []
      block.split('\n').forEach((line) => {
        if (!line || line.startsWith(':')) return
        if (line.startsWith('event:')) {
          event = line.slice(6).trim()
        }
        if (line.startsWith('data:')) {
          dataLines.push(line.slice(5).trim())
        }
      })
      return {
        event,
        data: dataLines.join('\n')
      }
    })
    .filter((item) => item.data)
}

/**
 * Motion 风:思考过程 chip 永远只有一行,通过文案动态变化体现进度。
 *   运行中:显示当前 / 最近一步的 label(running 或 done 形态),没有步骤就显示 "正在思考…"
 *   已完成:用最后一个有意义步骤的 done 文案,让用户在折叠态就能看到 AI 帮做了什么;
 *          没有步骤(空 reply)才回到通用兜底,不再统一展示"思考完毕"这种空泛词
 */
/**
 * formatStepDuration —— 步骤耗时人类可读格式。
 *   < 1s    → "320ms"
 *   < 60s   → "3.2s"
 *   ≥ 60s   → "1m12s"
 * 入参为毫秒,负数 / 异常都按 0 处理。
 */
function formatStepDuration(ms: number): string {
  if (!isFinite(ms) || ms < 0) ms = 0
  if (ms < 1000) return `${Math.round(ms)}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  const m = Math.floor(ms / 60_000)
  const s = Math.floor((ms % 60_000) / 1000)
  return `${m}m${s}s`
}

function activitySummary(msg: ChatMessage): string {
  const steps = msg.progressSteps || []
  // 文案策略:进行中用 running 形态(带省略号、动词进行时);完成后用 done 形态(完成时态如"照片找好了")。
  // 历史曾把 done 文案统一回退成 running 去省略号 → chip 显示"正在搜「红石支路」的照片",
  // 用户看不出"完成了没"(2026-04-29 张璇反馈)。改回 done 优先,fallback 才用 running。
  const stepText = (s: ProgressStep): string => {
    if (s.status === 'running') {
      return s.label.running || s.label.done || ''
    }
    // 完成态(ok/fail):优先用 done 文案,它本来就是"找好了 / 已挑好"这种完成时态;
    // fallback 到 running 去尾部省略号(老逻辑保底,适用于只有 running 文案的工具)
    if (s.label.done) return s.label.done
    return (s.label.running || '').replace(/[…….\s]+$/, '')
  }
  if (msg.pending) {
    const last = steps[steps.length - 1]
    if (!last) return '正在思考…'
    return stepText(last)
  }
  // 已完成:用最后一个完成态步骤的文案
  for (let i = steps.length - 1; i >= 0; i--) {
    if (steps[i].status === 'ok' || steps[i].status === 'fail') {
      return stepText(steps[i])
    }
  }
  return '已完成'
}

/**
 * extractFallbackCards —— 兜底将 assistant 文本里的 markdown 表格就地转成 CardHtml 卡片。
 *
 * 场景:agent 违反"结构化数据 1:1 render_card"约束,直接 markdown 表格输出。
 * 前端这里扫到 `| ... | ... |` 连续 ≥3 行(header + 分隔 + 数据),把这块切走,
 * 合成一张 html 表格卡片追加到 message.cards,文本里替换成一句提示。
 *
 * 只处理**标准 markdown 表格**。模糊的 bullet ranked list 不做,避免误伤正文。
 */
const TABLE_BLOCK_RE =
  /(?:^\|.+\|[ \t]*\n)(?:^\|[\s\-:|]+\|[ \t]*\n)(?:^\|.+\|[ \t]*\n?)+/gm

function extractFallbackCards(text: string): {
  cleaned: string
  extraCards: CardPayload[]
  hadFallback: boolean
} {
  const extraCards: CardPayload[] = []
  if (!text) return { cleaned: text, extraCards, hadFallback: false }
  let cleaned = text
  // 收集所有匹配,位置倒序删除以不破坏原始 index
  const matches: Array<{ start: number; end: number; html: string }> = []
  const re = new RegExp(TABLE_BLOCK_RE.source, TABLE_BLOCK_RE.flags)
  let m: RegExpExecArray | null
  while ((m = re.exec(cleaned)) !== null) {
    const html = markdownTableToHtml(m[0])
    if (html) {
      matches.push({ start: m.index, end: m.index + m[0].length, html })
    }
  }
  if (matches.length === 0) return { cleaned, extraCards, hadFallback: false }
  for (let i = matches.length - 1; i >= 0; i--) {
    const { start, end, html } = matches[i]
    extraCards.unshift({ type: 'html', html })
    cleaned =
      cleaned.slice(0, start) +
      '(以上数据已转为卡片显示)\n' +
      cleaned.slice(end)
  }
  return { cleaned: cleaned.trim(), extraCards, hadFallback: true }
}

function markdownTableToHtml(block: string): string | null {
  const lines = block
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.startsWith('|'))
  if (lines.length < 3) return null
  const headerCells = splitTableRow(lines[0])
  const separator = lines[1]
  if (!/^\|[\s\-:|]+\|$/.test(separator)) return null
  const dataRows = lines.slice(2).map(splitTableRow)
  if (headerCells.length === 0 || dataRows.length === 0) return null
  const style = [
    'table{border-collapse:collapse;width:100%;',
    'font-family:-apple-system,system-ui,sans-serif;',
    'font-size:13px;color:#1f2329;background:#fff}',
    'th,td{border:1px solid #e4e7ec;padding:8px 10px;text-align:left}',
    'th{background:#f6f9ff;color:#475467;font-weight:600}',
    'tr:nth-child(even) td{background:#fafbfc}',
    'body{margin:0;padding:0}'
  ].join('')
  const thead =
    '<tr>' +
    headerCells.map((c) => `<th>${escapeHtml(c)}</th>`).join('') +
    '</tr>'
  const tbody = dataRows
    .map(
      (r) =>
        '<tr>' + r.map((c) => `<td>${escapeHtml(c)}</td>`).join('') + '</tr>'
    )
    .join('')
  return `<style>${style}</style><table>${thead}${tbody}</table>`
}

function splitTableRow(line: string): string[] {
  const t = line.trim().replace(/^\|/, '').replace(/\|$/, '')
  return t.split('|').map((c) => c.trim())
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    if (c === '&') return '&amp;'
    if (c === '<') return '&lt;'
    if (c === '>') return '&gt;'
    if (c === '"') return '&quot;'
    return '&#39;'
  })
}

/**
 * enhanceImagesInHtml —— 在 md.render 后,把**指向图片的链接 / `<img>`** 统一改成缩略图。
 *
 * 规则:
 * 1. 已是 markdown-it 输出的 `<img src="...">`(来自 ![](url)) → 套 <a target=_blank> 外壳,加 thumb class
 * 2. `<a href="...jpg/png/webp/gif/bmp/heic">文本</a>` → 替换成 thumb(缩略图 + 点开大图)
 * 3. 其他非图片链接(如 zip 下载链接) → 不动
 *
 * 用纯 regex 处理;URL 里**只能出现不带引号**的 href,配合 markdown-it 的输出格式足够稳。
 * img 扩展名判断支持 ?query 后缀,覆盖 OSS 签名链接的情况。
 */
const IMG_EXT_HREF_RE =
  /<a\s+([^>]*?)href="(https?:\/\/[^"]+?\.(?:jpe?g|png|webp|gif|bmp|heic)(?:\?[^"]*)?)"([^>]*)>([\s\S]*?)<\/a>/gi
const BARE_IMG_RE = /<img\s+([^>]*?)src="([^"]+)"([^>]*?)\/?>(?!<\/a>)/gi

/** 可下载的非图片文件扩展;命中后链接展示为"文件名 chip + 下载图标",并同步到下载侧栏。 */
const DL_FILE_EXTS =
  'zip|xlsx?|csv|pdf|docx?|pptx?|txt|json|xml|tsv|tar|gz|7z|rar'
const DL_HREF_RE = new RegExp(
  `<a\\s+([^>]*?)href="(https?:\\/\\/[^"]+?\\.(?:${DL_FILE_EXTS})(?:\\?[^"]*)?)"([^>]*)>([\\s\\S]*?)<\\/a>`,
  'gi'
)
const DL_RAW_URL_RE = new RegExp(
  `https?:\\/\\/[^\\s<>"')]+?\\.(?:${DL_FILE_EXTS})(?:\\?[^\\s<>"')]*)?`,
  'gi'
)

/** 从 URL path 取最后一段做文件名,decode 中文等转义,失败回落到末段 */
function filenameFromUrl(url: string): string {
  try {
    const u = new URL(url)
    const seg = u.pathname.split('/').filter(Boolean).pop() || 'download'
    return decodeURIComponent(seg)
  } catch {
    return url.split('?')[0].split('/').pop() || 'download'
  }
}

function enhanceImagesInHtml(html: string): string {
  if (!html) return html
  // 1. markdown 原生 <img ...> 外面套一层 <a> 点开大图(避免已在 <a> 里的重复包裹:简单 heuristic)
  const wrapped = html.replace(BARE_IMG_RE, (m, pre, src, post) => {
    // 粗略判断:如果紧跟 </a> 或上一段已在 <a> 里则跳过(不精确,但基本够用)
    if (/class="[^"]*ai-claw-thumb/.test(m)) return m
    const cleanedPre = String(pre).replace(/class="[^"]*"/, '')
    const cleanedPost = String(post).replace(/class="[^"]*"/, '')
    return `<a href="${src}" target="_blank" rel="noopener noreferrer"><img ${cleanedPre}src="${src}"${cleanedPost} class="ai-claw-thumb" loading="lazy" /></a>`
  })
  // 2. 指向图片文件的 <a href="...jpg">文本</a> → 缩略图
  return wrapped.replace(IMG_EXT_HREF_RE, (_m, _pre, href) => {
    return `<a href="${href}" target="_blank" rel="noopener noreferrer"><img src="${href}" class="ai-claw-thumb" loading="lazy" alt="" /></a>`
  })
}

/**
 * triggerArtifactDownload —— 统一的文件下载入口。
 *
 * 浏览器:用临时 `<a download>` 标签触发 Chrome 原生下载管理器(快路径,
 * 不读 blob,流式落盘)。
 *
 * PC 客户端:fetch 拿文件转 blob,再用 `<a href="blob:..." download>` 触发下载。
 * 走 blob 是因为客户端 BrowserWindow 的 setWindowOpenHandler 一刀切 deny 了
 * 所有 `target=_blank` / `window.open`,普通 `<a download href>` 也走不通;
 * `blob:` 协议是纯 renderer 内存,不触发新窗口,download 属性正常生效;
 * 客户端的 electron-dl 全局 will-download 接管器仍能正常接住,把文件落到
 * 用户设置的下载目录。
 *
 * 调用方:聊天消息内文件 chip / 侧栏文件图标 / 侧栏"下载"文字链接。
 * 失败兜底:抛 message + console.error,让用户看到链接自行复制。
 */
async function triggerArtifactDownload(
  url: string,
  filename?: string
): Promise<void> {
  if (!url) return
  if (!desktopLib.isDesktop) {
    // 浏览器: 直接走原生 <a download>, 让 Chrome 流式落盘
    const a = document.createElement('a')
    a.href = url
    if (filename) a.download = filename
    a.rel = 'noopener noreferrer'
    document.body.appendChild(a)
    a.click()
    a.remove()
    return
  }
  // 客户端: fetch → blob → <a download blob:>, 绕开 setWindowOpenHandler 拦截
  try {
    const res = await fetch(url, { credentials: 'omit' })
    if (!res.ok) {
      throw new Error(`HTTP ${res.status} ${res.statusText}`)
    }
    const blob = await res.blob()
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    if (filename) a.download = filename
    a.rel = 'noopener noreferrer'
    document.body.appendChild(a)
    a.click()
    a.remove()
    // 延迟 release blob 引用, 给浏览器一帧时间把下载请求拷出去
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
  } catch (e) {
    console.error('[AiPhotoJun] 文件下载失败', e)
    antdMessage.error('下载失败，请复制链接到浏览器手动下载')
  }
}

/**
 * enhanceDownloadLinksInHtml —— 把 `<a href="...zip|xlsx|...">长 url</a>` 变成
 * "📁 文件名" chip。
 *
 * 不再用 `<a download href target=_blank>` 那套(客户端环境下被拦截、点击没反应);
 * 改成只挂 `data-dl-url` / `data-dl-name`,由消息容器的 onClick 事件委托统一调
 * `triggerArtifactDownload` 分流(客户端走 IPC 桥,浏览器走原生 `<a download>`)。
 *
 * 在 enhanceImagesInHtml 之后调用,跟它分开是因为图片要 thumbnail,文件要 chip 文案。
 * 不影响其他普通链接(www.xxx.com / 业务跳转等)。
 */
function enhanceDownloadLinksInHtml(
  html: string,
  /** 这些 url 已渲染成富预览卡片,跳过不转 chip,避免重复 */
  skipUrls?: Set<string>,
  /** 本条消息已有富预览卡片入口时,正文里所有下载链接一律移除
   *  (避免和卡片重复 + 防止绕过卡片上的会员锁) */
  dropAll?: boolean
): string {
  if (!html) return html
  return html.replace(DL_HREF_RE, (_m, _pre, href) => {
    if (dropAll || (skipUrls && skipUrls.has(href))) return '' // 移除该下载链接
    const fn = filenameFromUrl(href)
    const escaped = fn.replace(/"/g, '&quot;')
    const hrefEscaped = href.replace(/"/g, '&quot;')
    return (
      `<a href="${hrefEscaped}" data-dl-url="${hrefEscaped}" data-dl-name="${escaped}" class="ai-claw-download" title="${escaped}">` +
      `<span class="ai-claw-download__icon" aria-hidden></span>` +
      `<span class="ai-claw-download__name">${fn}</span>` +
      `</a>`
    )
  })
}

/** 从 assistant 文本(原 markdown)里扫出所有可下载文件链接,返回去重列表 */
function extractDownloadLinks(
  text: string
): Array<{ url: string; filename: string }> {
  if (!text) return []
  const out: Array<{ url: string; filename: string }> = []
  const seen = new Set<string>()
  let m: RegExpExecArray | null
  DL_RAW_URL_RE.lastIndex = 0
  while ((m = DL_RAW_URL_RE.exec(text))) {
    const url = m[0]
    if (seen.has(url)) continue
    seen.add(url)
    out.push({ url, filename: filenameFromUrl(url) })
  }
  return out
}

/**
 * detectPendingExport —— 识别"AI 已经接单去后台跑了,但 URL 还没出"的中间态文本。
 *
 * 例如 agent 提交了 Excel 报表/zip 打包后,会先回:
 *   "已提交本月拍照 Excel 报表,含 1075 条,文件生成中,你稍后问我'报表好了吗'"
 *
 * 这条没带 URL,extractDownloadLinks 抓不到。但用户期望能在右上角下载侧栏看到一条
 * "正在生成"的占位条目,知道 AI 真的在帮自己做事。返回 null = 不是 pending 提交。
 */
const PENDING_EXPORT_TRIGGER_RE =
  /(已提交|提交了|开始(?:导出|生成|打包)|正在(?:导出|生成|打包))[\s\S]{0,40}(Excel|报表|表格|压缩包|zip|文件|导出)|文件\s*(?:生成|打包|准备)中|生成中.{0,8}(?:链接|稍后|稍等)/
const PENDING_EXPORT_PROMPT_RE =
  /(导出|生成|打包|提交)[^,。;\n]{0,40}(Excel|报表|表格|压缩包|zip|文件)/

function detectPendingExport(text: string): string | null {
  if (!text) return null
  if (!PENDING_EXPORT_TRIGGER_RE.test(text)) return null
  // 取一句话作 prompt:优先匹配"导出 xxx Excel 报表"这种短语
  const promptMatch = text.match(PENDING_EXPORT_PROMPT_RE)
  if (promptMatch) return promptMatch[0]
  // 退而求其次:取首句(到第一个标点)
  const firstSeg = text.split(/[,。;\n]/).find((s) => s.trim()) || text
  return firstSeg.trim().slice(0, 40)
}

/** created_at 归一到秒:前端占位用 Date.now()(毫秒),后端任务用秒,按数量级判断后统一比。 */
const toSecLoose = (v: number): number => (v > 1e11 ? Math.floor(v / 1000) : v)
/** 同一轮判定窗口(秒):占位(AI 措辞文本)与真任务创建时间相差在此内,视为同一次导出。 */
const PENDING_COVER_WINDOW_SEC = 600

/**
 * detectPendingExport 凭 AI 措辞造的 `pending:` 占位,若同一轮已有真实导出任务
 * (带 subkind、非 pending:、非 error),则占位是多余的 —— 这类工具(export_attendance /
 * export_photos / publish_artifact 等)本就会建真 download_task,二者并存会让一次导出在
 * 下载侧栏显示两条(刘乾隆 2026-06 反馈"导出考勤生成两个任务":一条占位"生成本月考勤报表" +
 * 一条真任务"考勤导出-…xlsx")。
 */
function isPlaceholderCovered(ph: DownloadTask, list: DownloadTask[]): boolean {
  if (!ph.task_id.startsWith('pending:')) return false
  const phSec = toSecLoose(ph.created_at)
  return list.some(
    (d) =>
      !d.task_id.startsWith('pending:') &&
      !!d.subkind &&
      d.status !== 'error' &&
      Math.abs(toSecLoose(d.created_at) - phSec) <= PENDING_COVER_WINDOW_SEC
  )
}

/** 从下载列表里剔除被同轮真任务覆盖的正则占位;无可剔除时返回原引用(避免无谓重渲染/抖动)。 */
function stripCoveredPendingExports(list: DownloadTask[]): DownloadTask[] {
  const filtered = list.filter((d) => !isPlaceholderCovered(d, list))
  return filtered.length === list.length ? list : filtered
}

const AiPhotoJunPanel: React.FC<AiPhotoJunPanelProps> = ({
  userId,
  userName,
  groupId,
  open,
  openFromPlace,
  photoNumber,
  onMinimize,
  onApplyPageFilter,
  onApplyNlQuery,
  pendingQuestion,
  onPendingQuestionSent
}) => {
  const dispatch = useAppDispatch()
  const customerAgentDrawer = useAgentCustomerDrawer()
  const payment = usePayment()
  const vipKind = useVipKind()
  const [packVisible, setPackVisible] = useState(false)

  /** 文件下载需高级会员(VVIP)及以上。 */
  const canDownloadFile = isAtLeastVvip(vipKind)

  /** 下载前校验会员：VVIP 以下(非会员 / 基础VIP)拦截并打开开通页(定位高级会员档),返回 false;
   *  VVIP 及以上放行返回 true。
   *  产品 2026-06 调整：全部下载都要会员;本次再收紧为「VVIP 以下直接拦截」(基础VIP 也拦)。 */
  const checkVipBeforeDownload = useCallback(
    (_isExempt: unknown = false) => {
      // 入参 isExempt 仅保留以兼容调用方,不再使用(不再按导出类型豁免)
      if (!canDownloadFile) {
        payment.openVipByKind(
          CONFIG.VIP_KIND.VVIP,
          AI_PHOTO_KING_SOURCE_DETAIL,
          false,
          aiPhotoPaywallCustomContent({ vipKind, usedCredits: 0 })
        )
        return false
      }
      return true
    },
    [canDownloadFile, vipKind, payment]
  )

  // VIP_KIND 映射（对齐公共组件 PointsStatusCard）:
  //   VIP=1 基础会员, VVIP=3 高级会员, SVIP=2/EVIP=4 超级会员
  const vipLevel: VipLevel = (() => {
    switch (vipKind) {
      case CONFIG.VIP_KIND.VIP:
        return 'vip'
      case CONFIG.VIP_KIND.VVIP:
        return 'svip'
      case CONFIG.VIP_KIND.SVIP:
      case CONFIG.VIP_KIND.EVIP:
        return 'vvip'
      default:
        return 'nonvip'
    }
  })()

  // 轮播卡片: 仅从未发送过对话的用户展示，fetchContext 拉到空消息时置 true
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  /**
   * 同步 messages 的 ref, 神策埋点 ai_b8112_completed 在 sendMessage 的 finally 里
   * 需要拿"SSE 全部事件都落完" 后的最终 message 快照(progressSteps/cards/suggestions/
   * messageId/traceID/costSaving/roundCount/content 等), 用 setState 在 finally 拿是
   * stale closure。这条 ref 跟 messages 同步, finally 里直接读 ref.current。
   */
  const messagesRefForTrack = useRef<ChatMessage[]>([])
  useEffect(() => {
    messagesRefForTrack.current = messages
  }, [messages])
  const [inputValue, setInputValue] = useState('')

  // 输入框 placeholder 在 4 条候选里轮播,3.5s 一换;用户开始输入后停掉,聚焦时也停
  const placeholderPool = useMemo(
    () => [
      '本周拍照最多的人',
      '本月团队拍照总数',
      '对比本周和上周的拍照数据',
      '导出本月考勤为 Excel'
    ],
    []
  )
  const [placeholderIdx, setPlaceholderIdx] = useState(0)
  useEffect(() => {
    if (inputValue) return // 用户在输入,别打扰
    const t = window.setInterval(() => {
      setPlaceholderIdx((i) => (i + 1) % placeholderPool.length)
    }, 3500)
    return () => window.clearInterval(t)
  }, [inputValue, placeholderPool.length])
  const [sessionId, setSessionId] = useState('')
  const [loadingContext, setLoadingContext] = useState(false)
  const [sending, setSending] = useState(false)
  const [attachments, setAttachments] = useState<AttachmentItem[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textAreaRef = useRef<any>(null)
  /** "新会话"按钮短暂禁用,防重复点击;后端 compact 走异步,2s 冷却即可 */
  const [compactCoolingDown, setCompactCoolingDown] = useState(false)
  /** 浮窗当前尺寸(仅在 open=true 时有效) */
  const [panelSize, setPanelSize] = useState<{ w: number; h: number }>({
    w: PANEL_DEFAULT_WIDTH,
    h: Math.min(
      PANEL_DEFAULT_HEIGHT,
      Math.max(PANEL_MIN_HEIGHT, window.innerHeight * 0.8)
    )
  })
  /**
   * 浮窗相对默认锚点(右下)的偏移,拖 header 时改写。
   * dx < 0 = 往左移,dy < 0 = 往上移(因锚点在右下)。
   */
  const [panelOffset, setPanelOffset] = useState<{ dx: number; dy: number }>({
    dx: 0,
    dy: 0
  })
  /** 最近查询(跨 session 取自 /context 响应),新会话空态常驻展示前 3 条,"全部"按钮下拉后滚轮看余量 */
  const [recentQueries, setRecentQueries] = useState<RecentQuery[]>([])
  const [recentExpanded, setRecentExpanded] = useState(false)
  /** 「试试问我」开场引导问题，来自 /preset_questions（团队定制，每次随机生成）。
   *  拉取失败/为空时 EmptyStateBlock 用 DEFAULT_PRESETS 兜底 */
  const [presetQuestions, setPresetQuestions] = useState<string[]>([])
  /**
   * OnboardingCarousel + OnboardingShortcutRow 这次该展示的卡列表 (含已合并的 query + 本地素材),
   * 来自 /preset_questions 响应里的 onboarding_cards 字段 (2026-06-22 加的限定逻辑)。
   * 后端按 团队最近一周水印 top 字段 / 是否开考勤 实时裁决并下发个性化 query 文案。
   * - null = 还没拉到; 此时不渲染轮播, 避免 3 张全闪一下再被 gate 掉 1 张
   * - 数组 = 后端 best-effort 裁决结果, 至少含 ranking 一张
   * 失败兜底: 拉取失败保持 null → 轮播不展示 (空态仍有欢迎语+试试问我可用)
   */
  const [onboardingCards, setOnboardingCards] = useState<
    OnboardingCardItem[] | null
  >(null)
  /**
   * 下载任务列表:agent 调 generate_image / export_* / publish_artifact 等异步任务时,
   * 后端推 download_task SSE 事件即时入栏。pending 状态统一通过 /download_tasks?refresh=1 轮询。
   */
  const [downloads, setDownloads] = useState<DownloadTask[]>([])
  /**
   * 已通知过完成的 task_id 集合。规避重复 toast,场景:
   *  1. 刷新后从 /download_tasks 拉回历史 done 任务,如果再被卷进轮询 updates,不该再弹
   *  2. 同一任务因多次 setDownloads 触发 effect 重跑,只弹一次
   *  3. **跨刷新/跨 tab/跨重启去重**: 后端 download_task_poller 30s 才同步 DB 状态;
   *     期间用户刷新或关 tab 重开,拉回的 DB 状态仍是 pending,前端轮询拿到 done 又
   *     会弹 toast。用 localStorage 持久化 — 同设备同账号永远不再弹同一条。
   * 入栈时机:
   *  - fetchContext setDownloads 时把已是 done/error 的预填进去(历史完成不再通知)
   *  - tick 检测 pending→done/error 真正弹完 toast 后入栈(防再次轮询命中)
   * key 按 user_id 分桶,防 cookie 切账号串号。
   * 历史:之前用 sessionStorage 导致关 tab 重开 / 隔天登录 / 关浏览器重开都会再弹一次
   * 老 toast(陈丹阳 2026-06-01 反馈),换成 localStorage 修。
   */
  const NOTIFIED_TASKS_LS_KEY = userId
    ? `ai-photo-jun:notified-tasks:${userId}`
    : ''
  const notifiedTaskIdsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!NOTIFIED_TASKS_LS_KEY) return
    try {
      const raw = localStorage.getItem(NOTIFIED_TASKS_LS_KEY)
      const parsed = raw ? (JSON.parse(raw) as unknown) : null
      notifiedTaskIdsRef.current = new Set(
        Array.isArray(parsed) ? (parsed as string[]) : []
      )
    } catch {
      notifiedTaskIdsRef.current = new Set()
    }
  }, [NOTIFIED_TASKS_LS_KEY])
  /** persistNotifiedTaskIds —— 每次更新 set 后顺手写回 localStorage */
  const persistNotifiedTaskIds = useCallback(() => {
    if (!NOTIFIED_TASKS_LS_KEY) return
    try {
      localStorage.setItem(
        NOTIFIED_TASKS_LS_KEY,
        JSON.stringify(Array.from(notifiedTaskIdsRef.current))
      )
    } catch {
      // 配额满 / 隐私模式禁用 localStorage 等都静默忽略,只是没有跨刷新去重而已
    }
  }, [NOTIFIED_TASKS_LS_KEY])
  /** 下载侧栏 / Plan 抽屉开关 */
  const [downloadsDrawerOpen, setDownloadsDrawerOpen] = useState(false)
  const [planDrawerOpen, setPlanDrawerOpen] = useState(false)
  const [sessionsDrawerOpen, setSessionsDrawerOpen] = useState(false)
  const [sessions, setSessions] = useState<SessionPreview[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  /** "调用链路"诊断 modal 当前展示的 message;null = 关闭。隐藏入口:连击 ActivityBlock summary 5 次 */
  const [traceModalMessage, setTraceModalMessage] =
    useState<ChatMessage | null>(null)
  /** 红点状态:有新事件未被用户查看时 true,点击 header 按钮查看后 false */
  const [unreadDownload, setUnreadDownload] = useState(false)
  // 「已读」基线:unreadDownload 为 false 期间持续同步成当前任务数;一旦进入未读态,
  // 基线冻结,角标 = 任务数 − 基线(即「这次新出现的任务数」);纯状态变化型未读
  // (pending→done,数量不变)至少记 1。修复:角标曾直接用 downloads.length,
  // 把老任务全算进去(刘洪反馈:新建 1 个任务角标却显示 6)。
  const seenDownloadCountRef = useRef(0)
  useEffect(() => {
    if (!unreadDownload) seenDownloadCountRef.current = downloads.length
  }, [unreadDownload, downloads])
  const unreadDownloadCount = unreadDownload
    ? Math.max(1, downloads.length - seenDownloadCountRef.current)
    : 0
  // 执行计划入口已随「···」菜单下线(2026-06-11),unreadPlan 值无人消费,只留 setter
  // 给计划事件处调用,避免大改;后续若彻底移除计划抽屉再一并清理。
  const [, setUnreadPlan] = useState(false)
  const [walletPopoverVisible, setWalletPopoverVisible] = useState(false)
  const [insufficientModal, setInsufficientModal] = useState<{
    available: number
    cost: number
    /** 因何触发本次查询(c8146 埋点 string1) */
    trigger: AiC8146Trigger
  } | null>(null)
  /** 积分加量包弹窗打开来源(vip_b8148 埋点 sourceType);null=未打开 */
  const [packSource, setPackSource] = useState<VipB8148SourceType | null>(null)
  /** 加量包弹窗曝光时的默认套餐名(b8148 点击事件 string1 复用) */
  const packDefaultRef = useRef('')
  /** 内测申请弹窗开关 */
  const [betaModalVisible, setBetaModalVisible] = useState(false)
  // 内测弹窗来源标识(区分 banner / 积分不足弹窗等入口),传给弹窗上报存库区分转化
  const [betaModalSource, setBetaModalSource] = useState('')
  /** 内测申请 banner 是否被用户关闭（本会话内，点 × 关掉，刷新/重开会恢复） */
  const [betaBannerClosed, setBetaBannerClosed] = useState(false)
  // 切换会话 / 开新对话(sessionId 变化)时重置关闭态,让 banner 重新展示(产品要求:× 只临时关当前这次)
  useEffect(() => {
    setBetaBannerClosed(false)
  }, [sessionId])
  /** 内测名额(后端 beta/quota 返回): total 总名额, remaining 剩余 */
  const [betaQuota, setBetaQuota] = useState<{
    total: number
    remaining: number
  }>({ total: 100, remaining: 79 })

  /** 当前钱包用量状态(PointsStatusCard 同款口径),给 c8145/b8112 的 string5 埋点用 */
  const currentUsageState = () =>
    computeUsageState(wallet?.totalLimit ?? 0, wallet?.consumed ?? 0)

  /** 我的积分弹窗 CTA 类型(c8145 string1):超级/企业会员是增购积分包,其余升级会员 */
  const c8145Trigger = (): '升级会员' | '增购积分包' =>
    vipKind === CONFIG.VIP_KIND.SVIP || vipKind === CONFIG.VIP_KIND.EVIP
      ? '增购积分包'
      : '升级会员'

  /** 打开积分加量包弹窗并记录来源(vip_b8148 sourceType) */
  const openPointsPack = (source: VipB8148SourceType) => {
    setPackSource(source)
    setPackVisible(true)
  }
  /** 用户在当前 group 下的积分钱包。null=未拉到/不可用,展示时 chip 隐藏。
   *   - 浮窗打开时拉一次
   *   - 每次 SSE done 后再拉一次(消费完应该立刻反馈余额变化)
   *   - 后端 503 (billing 未配置) → 静默, chip 不出现 */
  const [wallet, setWallet] = useState<{
    available: number
    remainingLimit: number
    /** 已消耗 (后端 current_consumed): 基础包本月 + 充值包累计 */
    consumed: number
    /** 总额度 (后端 total_limit): 已消耗 + 剩余可用; 0 表示后端未返回, UI 走兜底 */
    totalLimit: number
  } | null>(null)
  /** 当前 /run SSE 流的 AbortController。点击发送按钮(此时显示停止图标)时
   *  调 abort() 立即终止 fetch + reader,触发 catch 走 AbortError 静默处理。 */
  const runAbortRef = useRef<AbortController | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  /**
   * 粘性滚动:只有当用户当前"已在底部附近"时才跟随滚到底。
   * 用户往上翻(想回看过去消息 / 点展开看步骤)时 scrollTop 离底部 > 阈值,ref=false,
   * 此时后续 messages 变动(流式打字、展开步骤导致 setMessages)不再强制滚到底。
   */
  const stickToBottomRef = useRef(true)
  const STICK_THRESHOLD = 80

  const handleMessagesScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight
    stickToBottomRef.current = distance <= STICK_THRESHOLD
  }, [])

  const scrollToBottom = useCallback((force = false) => {
    requestAnimationFrame(() => {
      const el = scrollRef.current
      if (!el) return
      if (force || stickToBottomRef.current) {
        el.scrollTop = el.scrollHeight
        stickToBottomRef.current = true
      }
    })
  }, [])

  const replacePendingMessage = useCallback(
    (messageId: string, updater: (prev: ChatMessage) => ChatMessage) => {
      setMessages((prev) =>
        prev.map((item) => (item.id === messageId ? updater(item) : item))
      )
    },
    []
  )

  const setMessageContent = useCallback(
    (messageId: string, content: string, pending: boolean) => {
      replacePendingMessage(messageId, (prev) => ({
        ...prev,
        content,
        pending,
        // 完成态:清掉流式预览;保留 progressSteps 以便展开回看
        ...(!pending ? { streamPreview: undefined } : {})
      }))
    },
    [replacePendingMessage]
  )

  const toggleActivity = useCallback(
    (messageId: string) => {
      // 神策埋点 ai_b8112_click: 展开/收起 进度详情。
      // rankNum 此处暂时算从 messages 数组找当前消息位置, 不阻断主流程。
      const curr = messagesRefForTrack.current.find((m) => m.id === messageId)
      const nextOpen = !curr?.activityOpen
      const idx = messagesRefForTrack.current.indexOf(curr!)
      const assistantRank = messagesRefForTrack.current
        .slice(0, idx + 1)
        .filter((m) => m.role === 'assistant').length
      trackAiB8112Click({
        clickItem: nextOpen ? '展开进度详情' : '收起进度详情',
        sessionID: sessionId,
        msgID: curr?.messageId || '',
        rankNum: assistantRank
      })
      replacePendingMessage(messageId, (prev) => ({
        ...prev,
        activityOpen: !prev.activityOpen
      }))
    },
    [replacePendingMessage, sessionId]
  )

  /**
   * fetchContext —— 拉某个 session 的完整上下文。
   * - 不传 targetSessionId: 取该 user+group 下最新一组(老逻辑)
   * - 传 targetSessionId: 拉那个具体 session(从历史会话抽屉点选时用)
   */
  /**
   * 拉「试试问我」开场引导问题。后端每次随机生成（不缓存）。
   * 触发时机：只在「首次打开 AI 照片王」拉一次（关闭后再次打开会重拉一批）；
   * 开启新会话**不重拉**，沿用本次打开拿到的那批，避免点击后等 3s 网络往返。
   * 独立于 /context，不阻塞历史消息渲染；失败不影响空态（EmptyStateBlock 用 DEFAULT_PRESETS 兜底）。
   */
  const fetchPresetQuestions = useCallback(async () => {
    if (!userId || !groupId) return
    try {
      const resp = await fetch(
        `${AI_CLAW_BASE_URL}/preset_questions?user_id=${encodeURIComponent(
          userId
        )}&group_id=${encodeURIComponent(groupId)}`,
        { method: 'GET', credentials: 'include' }
      )
      if (!resp.ok) throw new Error(`获取试试问我失败(${resp.status})`)
      const data = await resp.json()
      const qs = Array.isArray(data?.preset_questions)
        ? (data.preset_questions as unknown[]).filter(
            (q): q is string => typeof q === 'string' && q.trim().length > 0
          )
        : []
      setPresetQuestions(qs)
      // onboarding_cards: 后端 2026-06-22 加的轮播卡限定。元素形态: { key, query }。
      // 后端按团队最近一周水印 top 字段 + 是否开考勤实时裁决并下发个性化 query 文案 (含 ledger 模板替换)。
      // 前端按 key 匹配本地素材表 (src/icon/label), 找不到本地素材的 key 安全跳过 (前端老版本不认识的新卡)。
      // 字段缺失 / 类型异常时保持 null → 轮播不展示, 走 fail-closed。
      const rawCards = Array.isArray(data?.onboarding_cards)
        ? (data.onboarding_cards as unknown[]).filter(
            (c): c is OnboardingCardBackendItem =>
              !!c &&
              typeof c === 'object' &&
              typeof (c as { key?: unknown }).key === 'string' &&
              typeof (c as { query?: unknown }).query === 'string'
          )
        : null
      setOnboardingCards(rawCards ? mergeOnboardingCards(rawCards) : null)
    } catch (e) {
      console.warn('[AiPhotoJun] 拉 /preset_questions 失败，用默认问题兜底', e)
      setPresetQuestions([])
      setOnboardingCards(null)
    }
  }, [userId, groupId])

  /**
   * 首次打开面板（open 的 false→true 边缘）拉一次；关闭后重置，下次打开再拉。
   *
   * 注意：守卫 !userId || !groupId 必须放在 presetFetchedRef.current = true 之前。
   * 否则等 userId / groupId 异步到位、fetchPresetQuestions 重建触发本 effect 再跑时，
   * ref 已被错误标记为 true，请求永远发不出去。
   */
  const presetFetchedRef = useRef(false)
  useEffect(() => {
    if (!open) {
      presetFetchedRef.current = false
      return
    }
    if (presetFetchedRef.current) return
    if (!userId || !groupId) return
    presetFetchedRef.current = true
    fetchPresetQuestions()
  }, [open, userId, groupId, fetchPresetQuestions])

  const fetchContext = useCallback(
    async (targetSessionId?: string) => {
      if (!userId || !groupId) {
        setMessages([])
        return
      }
      setLoadingContext(true)
      try {
        const sidParam = targetSessionId
          ? `&session_id=${encodeURIComponent(targetSessionId)}`
          : ''
        const resp = await fetch(
          `${AI_CLAW_BASE_URL}/context?user_id=${encodeURIComponent(
            userId
          )}&group_id=${encodeURIComponent(groupId)}${sidParam}`,
          {
            method: 'GET',
            credentials: 'include'
          }
        )
        if (!resp.ok) {
          throw new Error(`获取上下文失败(${resp.status})`)
        }
        const data = await resp.json()
        const normalized = normalizeMessages(data?.messages)
        // 拉取本 session 的反馈记录, 合并到 normalized 上(刷新后恢复 👍/👎/💬 选中态)。
        // 失败不阻断主流程 —— 反馈只是 UX 增强, 真正的对话内容已经渲染了。
        const resolvedSidForFeedback =
          typeof data?.session_id === 'string' && data.session_id.trim()
            ? data.session_id.trim()
            : ''
        if (resolvedSidForFeedback) {
          try {
            await mergeFeedbackIntoMessages(
              normalized,
              userId,
              resolvedSidForFeedback
            )
          } catch (e) {
            console.warn('[AiPhotoJun] 拉反馈失败,忽略', e)
          }
        }
        setMessages(normalized)
        // 无历史对话 = 新用户,展示引导轮播
        if (normalized.length === 0 && !targetSessionId) {
          setShowOnboarding(true)
        }
        // 下载侧栏刷新后恢复 = 两条数据源 merge:
        //   (A) 当前 session 的 attachments.downloads[](来自 /context 已经拉了)
        //   (B) 该 user+group 下所有持久化任务(独立表 xiaohei_claw_download_task,
        //       后端 /download_tasks 接口,跨 session 聚合),覆盖未关联到 session 的导出
        // 用 task_id 去重,A 在前(本 session 字段更全,如 prompt),B 兜底
        const restoredDownloads = extractDownloadsFromContext(data?.messages)
        try {
          // mount 时带 refresh=1: 后端会对 pending 任务并发实时查上游, 比纯读 DB 状态更新
          const dlResp = await fetch(
            `${AI_CLAW_BASE_URL}/download_tasks?user_id=${encodeURIComponent(
              userId
            )}&group_id=${encodeURIComponent(groupId)}&size=50&refresh=1`,
            { method: 'GET', credentials: 'include' }
          )
          if (dlResp.ok) {
            const dlData = await dlResp.json()
            const persisted = Array.isArray(dlData?.tasks)
              ? (dlData.tasks as unknown[])
              : []
            // 用 task_id 索引 A(attachments 来的),供 B(/download_tasks 来的)用同 id
            // 覆盖易变字段。attachments 里的 status 是 LLM 推消息当时冻结的,可能已经
            // 过时(典型:海报生成任务消息推过来时还在 pending,后台早就 done 了);
            // /download_tasks 才是 fresh source of truth。
            const byId = new Map<string, DownloadTask>(
              restoredDownloads.map((d) => [d.task_id, d])
            )
            for (const raw of persisted) {
              if (!raw || typeof raw !== 'object') continue
              const obj = raw as Record<string, unknown>
              const id = typeof obj.task_id === 'string' ? obj.task_id : ''
              if (!id) continue
              // 后端返 sub_kind(蛇形)/status/title/url/created_at(time.Time string),映射成 DownloadTask
              const subKindStr =
                typeof obj.sub_kind === 'string' ? obj.sub_kind : ''
              const subkind =
                subKindStr === 'export_photos' ||
                subKindStr === 'export_watermark_data' ||
                subKindStr === 'export_attendance' ||
                subKindStr === 'publish_artifact' ||
                subKindStr === 'generate_image'
                  ? (subKindStr as DownloadTask['subkind'])
                  : undefined
              const kind: 'image' | 'file' =
                subkind === 'generate_image' ? 'image' : 'file'
              const status: 'pending' | 'done' | 'error' =
                obj.status === 'done' || obj.status === 'error'
                  ? (obj.status as 'done' | 'error')
                  : 'pending'
              const url = typeof obj.url === 'string' ? obj.url : undefined
              const errMsg =
                typeof obj.err_msg === 'string' ? obj.err_msg : undefined
              const createdAtRaw = obj.created_at
              let createdAt = Math.floor(Date.now() / 1000)
              if (typeof createdAtRaw === 'number') {
                createdAt = createdAtRaw
              } else if (typeof createdAtRaw === 'string' && createdAtRaw) {
                const parsed = Date.parse(createdAtRaw)
                if (!Number.isNaN(parsed)) {
                  createdAt = Math.floor(parsed / 1000)
                }
              }
              const existing = byId.get(id)
              if (existing) {
                // 同 task_id 已在 A 里; 用 B 的最新 status/url/err_msg 覆盖,
                // 其他字段(prompt/kind 等)保留 A 的更全版本.
                existing.status = status
                if (url) existing.url = url
                if (errMsg) existing.err_msg = errMsg
                continue
              }
              const fresh: DownloadTask = {
                task_id: id,
                kind,
                subkind,
                prompt:
                  typeof obj.title === 'string' && obj.title
                    ? obj.title
                    : kind === 'image'
                      ? '生成图片'
                      : '文件导出',
                status,
                url,
                err_msg: errMsg,
                created_at: createdAt
              }
              restoredDownloads.push(fresh)
              byId.set(id, fresh)
            }
            // 合并完保持 created_at DESC
            restoredDownloads.sort((a, b) => b.created_at - a.created_at)
          }
        } catch (e) {
          // 拉历史失败只是少了跨 session 的恢复,本 session attachments 已经能用,不阻断
          console.warn(
            '[AiPhotoJun] 拉 /download_tasks 失败,只显示本 session 的下载',
            e
          )
        }
        // 刷新前已完成的任务都标"已通知",防止下一次 tick 拉到 done 状态又弹一次 toast。
        // 合并到 localStorage 里已存在的集合(跨刷新 / 关 tab / 关浏览器去重),不直接覆盖。
        restoredDownloads
          .filter((d) => d.status === 'done' || d.status === 'error')
          .forEach((d) => notifiedTaskIdsRef.current.add(d.task_id))
        persistNotifiedTaskIds()
        setDownloads(restoredDownloads)
        setUnreadDownload(false)
        setUnreadPlan(false)
        setSessionId(
          typeof data?.session_id === 'string' && data.session_id.trim()
            ? data.session_id.trim()
            : buildSessionId(userId, groupId)
        )
        // 最近查询来自新表 xiaohei_claw_user_query_log,跨 session 聚合,不受 compact 影响
        const rq = Array.isArray(data?.recent_queries)
          ? (data.recent_queries as RecentQuery[]).filter(
              (r) => r && typeof r.query === 'string' && r.query.trim()
            )
          : []
        setRecentQueries(rq)
        setRecentExpanded(false)
        // 初始加载时强制滚到底,让用户直接看到最近一条 assistant 回复
        scrollToBottom(true)
      } catch (error) {
        console.error('获取 AI照片王 上下文失败:', error)
        setSessionId(buildSessionId(userId, groupId))
        setMessages([])
        setRecentQueries([])
        setDownloads([])
      } finally {
        setLoadingContext(false)
      }
    },
    [groupId, userId, scrollToBottom, persistNotifiedTaskIds]
  )

  useEffect(() => {
    fetchContext()
  }, [fetchContext])

  /**
   * fetchWallet —— 拉取当前用户在 group 下的积分余额, 给 header chip 用。
   *
   * 失败处理:
   *   - 503 (billing 未配置, 本地开发等环境) → 静默, chip 不出现
   *   - 其他失败 → 静默 + log warn (不弹错误, 余额展示是 nice-to-have)
   *   - 拉到 0 / 负数也正常展示 (前置校验在后端 /run 时拦, 用户能看见 0 才好)
   */
  const fetchWallet = useCallback(async () => {
    if (!groupId) return
    try {
      // 接口已下沉到 WebXCameraAPI: GET /web/agent/wallet?group_id=<短ID或长UUID>
      // user_id 走 cookie (USER), 不再 query 显式传, 防止越权查别人。
      // 响应包了一层 { status, msg, data: {...} }, data 里才是钱包字段。
      const resp = await fetch(
        `${__API_SERVER_HOST__}/web/agent/wallet?group_id=${encodeURIComponent(
          groupId
        )}`,
        { method: 'GET', credentials: 'include' }
      )
      if (resp.status === 401) {
        // cookie 缺失/过期: 隐藏 chip, 不弹错 (现有"未登录"流程会有别的提示)
        setWallet(null)
        return
      }
      if (!resp.ok) throw new Error(`wallet ${resp.status}`)
      const body = await resp.json()
      // middleware.Response 包装: { code: 200, msg: "...", data: {...} }
      // code != 200 视为失败 (cookie 失效会走 401, 这里基本是 RPC 故障)
      if (body?.code !== 200) {
        setWallet(null)
        return
      }
      const data = body?.data || {}
      setWallet({
        available:
          typeof data.available_points === 'number' ? data.available_points : 0,
        remainingLimit:
          typeof data.remaining_limit === 'number' ? data.remaining_limit : 0,
        consumed:
          typeof data.current_consumed === 'number' ? data.current_consumed : 0,
        totalLimit: typeof data.total_limit === 'number' ? data.total_limit : 0
      })
    } catch (e) {
      console.warn('[AiPhotoJun] 拉余额失败,忽略', e)
    }
  }, [groupId])

  useEffect(() => {
    void fetchWallet()
  }, [fetchWallet])

  // 会员等级变化(如升级会员支付成功 → fetchGroupInfo 更新 vipKind)后,积分额度会随档位
  // 变化(基础 50w → 高级 600w…),需要重新拉一次钱包,否则用户得手动刷新页面才更新
  // (刘洪 2026-06 反馈)。用 ref 跳过首次挂载,只在 vipKind 真正变化时刷新。
  const prevVipKindRef = useRef(vipKind)
  useEffect(() => {
    if (prevVipKindRef.current === vipKind) return
    prevVipKindRef.current = vipKind
    void fetchWallet()
  }, [vipKind, fetchWallet])

  /**
   * 文件下载扣费 + 放行下载。调用前调用方需已完成会员校验(checkVipBeforeDownload / onBeforeDownload)。
   * 后端按团队级去重(同一文件只扣一次);余额不足弹 c8146 积分不足弹窗;
   * 扣费接口异常 / 未配置时兜底放行下载,不因计费问题堵死下载。
   */
  const chargeThenDownload = useCallback(
    async (url: string, filename?: string, taskId?: string) => {
      if (!url) return
      try {
        const resp = await fetch(`${AI_CLAW_BASE_URL}/download/charge`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            group_id: groupId,
            user_id: userId,
            url,
            task_id: taskId || ''
          })
        })
        if (resp.ok) {
          const body = await resp.json()
          const data = body?.data ?? body
          if (data?.insufficient) {
            const available =
              typeof data.available === 'number' ? data.available : 0
            const cost = typeof data.cost === 'number' ? data.cost : 0
            // 神策 ai_b8112_c8146_exposure:下载文件积分不足弹窗曝光
            trackAiC8146Exposure({
              sessionID: sessionId || '',
              trigger: '下载文件',
              available,
              cost
            })
            setInsufficientModal({ available, cost, trigger: '下载文件' })
            return
          }
        }
        // charged / 后端放行(计费未配置)/ 非 2xx 兜底:都放行下载
        void triggerArtifactDownload(url, filename)
        void fetchWallet()
      } catch (e) {
        // 网络异常:兜底放行,不因扣费接口故障堵死下载
        console.error('[AiPhotoJun] 下载扣费请求异常, 兜底放行下载', e)
        void triggerArtifactDownload(url, filename)
      }
    },
    [groupId, userId, sessionId, fetchWallet]
  )

  /** 拉内测名额(进面板拉一次, 给内测申请弹窗展示"仅限前 N 名 · 还剩 M 个") */
  const fetchBetaQuota = useCallback(async () => {
    try {
      const resp = await fetch(
        `${AI_CLAW_BASE_URL}/beta/quota?product_key=${encodeURIComponent(
          AI_PHOTO_BETA_PRODUCT_KEY
        )}`,
        { method: 'GET', credentials: 'include' }
      )
      if (!resp.ok) return
      const body = await resp.json()
      // xiaohei-claw 直接返回 { total, remaining };兼容个别网关包一层 data 的情况
      const data =
        body && typeof body.data === 'object' && body.data ? body.data : body
      setBetaQuota({
        total: typeof data?.total === 'number' ? data.total : 100,
        remaining: typeof data?.remaining === 'number' ? data.remaining : 0
      })
    } catch (e) {
      console.warn('[AiPhotoJun] 拉内测名额失败, 忽略', e)
    }
  }, [])

  useEffect(() => {
    void fetchBetaQuota()
  }, [fetchBetaQuota])

  // 粘性滚动:只有在"已在底部附近"时才跟随滚到底。
  // 这样用户往上翻看过去消息 / 展开某一步时,流式或新事件不会把视图强制拖到底部。
  useEffect(() => {
    if (open) scrollToBottom()
  }, [messages, open, scrollToBottom])

  // 浮窗从关闭切到打开时,无论用户上一次翻到哪里,都强制滚到最新一条。
  const prevOpenRef = useRef(open)
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      scrollToBottom(true)
    }
    prevOpenRef.current = open
  }, [open, scrollToBottom])

  // 聚焦输入框的通用方法,延迟一帧确保 DOM 就绪
  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      if (textAreaRef.current && textAreaRef.current.focus) {
        textAreaRef.current.focus()
      }
    })
  }, [])

  // 面板打开时自动聚焦输入框,让光标闪烁提示用户可直接输入
  useEffect(() => {
    if (open) {
      focusInput()
    }
  }, [open, focusInput])

  // 神策埋点 ai_b8112_exposure: 主面板从关闭→打开过渡时上报曝光。
  // list1=「试试问我」实际渲染的问题(团队定制 presetQuestions; 空时回落到 DEFAULT_PRESETS);
  // list2=recentQueries ("历史记录");
  // state=wallet.remainingLimit; sessionID=当前 sessionId;
  // fromPlace=父组件按打开入口传入; photoNumber=主页照片总数(父组件传入)。
  // useRef 跟踪上一次 open, 只在 false→true 边缘触发, 避免在面板已打开时
  // 任何其它依赖变化都重复触发曝光埋点。
  const exposureFiredRef = useRef(false)
  useEffect(() => {
    if (!open) {
      exposureFiredRef.current = false
      return
    }
    if (exposureFiredRef.current) return
    exposureFiredRef.current = true
    const exposedPresets =
      presetQuestions.length > 0 ? presetQuestions : DEFAULT_PRESETS
    // 打开瞬间已有对话(面板常驻不销毁) / 已有 sessionId(恢复过上下文) = 继续旧会话。
    // 历史上下文是异步拉的: 刷新页面后立刻打开可能仍判成"新会话", 接受该边界。
    const continuedSession = messages.length > 0 || !!sessionId
    // list3: 本次曝光实际展示的轮播卡 cardLabel 数组(2026-06-23 spec)。
    // 轮播卡仅在「空会话 + showOnboarding + 有可见卡」时渲染(见下方 OnboardingCarousel 渲染条件),
    // 否则上报空数组(此时展示的是输入框上方快捷按钮, 不算轮播卡曝光)。
    const exposedCarouselLabels =
      messages.length === 0 &&
      showOnboarding &&
      onboardingCards !== null &&
      onboardingCards.length > 0
        ? onboardingCards.map((c) => c.cardLabel)
        : []
    trackAiB8112Exposure({
      sessionID: sessionId || '',
      list1: exposedPresets,
      number1: exposedPresets.length,
      list2: recentQueries.map((q) => q.query),
      number2: recentQueries.length,
      state: wallet?.remainingLimit ?? 0,
      exposureResult: continuedSession ? '继续旧会话' : '新会话',
      fromPlace: openFromPlace ?? '点击AI问照片按钮',
      photoNumber: photoNumber ?? 0,
      // 曝光时底部提示条用量状态 + 搜索画面引导卡(内测 banner)是否同屏展示
      string5: usageStateToB8112(currentUsageState()),
      bool1: !betaBannerClosed,
      list3: exposedCarouselLabels
    })
  }, [
    open,
    sessionId,
    recentQueries,
    wallet,
    presetQuestions,
    messages,
    openFromPlace,
    photoNumber,
    showOnboarding,
    onboardingCards
  ])

  /**
   * 下载任务轮询: 只要 downloads 里还有 pending, 就每 20s **统一**调 /download_tasks?refresh=1
   * 拿所有任务最新状态(后端会对 pending 并发实时查上游,详见 server.go refreshPendingTasks)。
   *
   * 旧实现: 每个 pending task 独立 fetch /export_task 或 /image_task — N 个 pending = N 次 HTTP。
   * 新实现: 1 个列表接口拿所有 task 当前状态, 按 task_id merge 进 downloads。
   *
   * 自动停止: useEffect 依赖 downloads, 任务全 done/error 时 hasPending=false 直接 return,
   *           不再注册 setInterval。pending=0 → 立即停。
   *
   * 状态变化时:
   *   - 亮红点(抽屉关着)
   *   - pending → done/error 转换且未通知过 → antd notification 弹完成提示
   *     已通知的 task 跳过, 防刷新后历史 done 任务重复弹(notifiedTaskIdsRef + localStorage)。
   */
  useEffect(() => {
    if (!userId || !groupId) return
    const hasPending = downloads.some((d) => d.status === 'pending')
    if (!hasPending) return

    let cancelled = false
    const tick = async () => {
      let data: { tasks?: unknown[] } | null = null
      try {
        const url =
          `${AI_CLAW_BASE_URL}/download_tasks` +
          `?user_id=${encodeURIComponent(userId)}` +
          `&group_id=${encodeURIComponent(groupId)}` +
          `&size=50&refresh=1`
        const r = await fetch(url, { credentials: 'include' })
        if (!r.ok) return
        data = (await r.json()) as { tasks?: unknown[] }
      } catch (e) {
        console.warn('[AiPhotoJun] /download_tasks?refresh=1 失败', e)
        return
      }
      if (cancelled) return

      const persisted = Array.isArray(data?.tasks) ? data!.tasks! : []
      if (persisted.length === 0) return

      // 后端 record 字段映射成 partial DownloadTask 用于 merge
      const remoteByID = new Map<string, Partial<DownloadTask>>()
      for (const raw of persisted) {
        if (!raw || typeof raw !== 'object') continue
        const obj = raw as Record<string, unknown>
        const id = typeof obj.task_id === 'string' ? obj.task_id : ''
        if (!id) continue
        const status =
          obj.status === 'done' || obj.status === 'error'
            ? (obj.status as 'done' | 'error')
            : 'pending'
        remoteByID.set(id, {
          status,
          url: typeof obj.url === 'string' ? obj.url : undefined,
          err_msg: typeof obj.err_msg === 'string' ? obj.err_msg : undefined
        })
      }

      // ⚠️ setDownloads callback 必须在"无变化"时 return prev 原引用,
      // 否则 prev.map() 永远产生新数组 → useEffect 依赖 downloads → 重跑 → 注册新 timer
      // → 立即触发 tick(因为 void tick() 在 setInterval 后) → 死循环, 网络面板会出现高频请求
      let statusChanged = false
      const justFinished: DownloadTask[] = []
      setDownloads((prev) => {
        let touched = false
        const next = prev.map((orig) => {
          const remote = remoteByID.get(orig.task_id)
          if (!remote) return orig
          const newStatus = remote.status || orig.status
          const newURL = remote.url || orig.url
          const newErrMsg = remote.err_msg || orig.err_msg
          // 字段全没变 → 保留原引用, 不污染 next 的 element identity
          if (
            newStatus === orig.status &&
            newURL === orig.url &&
            newErrMsg === orig.err_msg
          ) {
            return orig
          }
          touched = true
          if (newStatus !== orig.status) {
            statusChanged = true
            if (orig.status === 'pending' && newStatus !== 'pending') {
              justFinished.push({
                ...orig,
                status: newStatus,
                url: newURL,
                err_msg: newErrMsg
              })
            }
          }
          return {
            ...orig,
            status: newStatus,
            url: newURL,
            err_msg: newErrMsg
          }
        })
        return touched ? next : prev
      })
      if (statusChanged && !downloadsDrawerOpen) {
        setUnreadDownload(true)
      }
      // 任务完成后同步更新消息上的 previewFiles（让预览卡片从"生成中"变成真实图/文件）
      if (justFinished.length > 0) {
        setMessages((prev) =>
          prev.map((msg) => {
            if (!msg.previewFiles || msg.previewFiles.length === 0) return msg
            let touched = false
            const updated = msg.previewFiles.map((pf) => {
              const finished = justFinished.find(
                (t) =>
                  t.task_id === pf.url ||
                  t.url === pf.url ||
                  t.prompt === pf.name
              )
              if (!finished || !finished.url) return pf
              touched = true
              return {
                ...pf,
                url: finished.url,
                desc: finished.status
              }
            })
            return touched ? { ...msg, previewFiles: updated } : msg
          })
        )
      }
      let notifiedSetChanged = false
      justFinished.forEach((task) => {
        if (notifiedTaskIdsRef.current.has(task.task_id)) return
        notifiedTaskIdsRef.current.add(task.task_id)
        notifiedSetChanged = true
        if (task.status === 'done') {
          antdMessage.success(
            `${task.prompt || '导出任务'} 已完成，可在下载列表查看`
          )
        } else if (task.status === 'error') {
          antdMessage.error(
            `${task.prompt || '导出任务'} 失败: ${task.err_msg || '请重试'}`
          )
        }
      })
      if (notifiedSetChanged) {
        persistNotifiedTaskIds()
      }
    }
    const timer = window.setInterval(tick, 20000)
    // 立即先拉一次,别等 20s
    void tick()
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [downloads, downloadsDrawerOpen, groupId, userId, persistNotifiedTaskIds])

  /**
   * 把 assistant 文本里的下载相关信号同步进 downloads 侧栏。两类:
   *   1. 已经给出可下载 URL → 直接 done 条目(用 url 当 task_id 天然去重)
   *   2. 已提交但 URL 还没回(典型如 Excel 导出)→ pending 占位(messageId 当 task_id),
   *      让用户在侧栏看到"AI 正在帮我跑",而不是空着觉得没动静
   * 当后续轮次出现真实 URL,会作为新 done 条目加入,旧 pending 同时被标 done。
   * 后端目前不为这类发 download_task SSE,所以前端在文本落定后扫一遍补上。
   */
  useEffect(() => {
    type Found =
      | { kind: 'done'; key: string; url: string; filename: string; ts: number }
      | { kind: 'pending'; key: string; prompt: string; ts: number }

    const ordered: Found[] = []
    const seen = new Set<string>()
    for (const m of messages) {
      if (m.role !== 'assistant' || m.pending) continue
      const ts = m.runDoneAt || Date.now()
      // (1) URL 类
      const links = extractDownloadLinks(m.content || '')
      for (const link of links) {
        if (seen.has(link.url)) continue
        seen.add(link.url)
        ordered.push({
          kind: 'done',
          key: link.url,
          url: link.url,
          filename: link.filename,
          ts
        })
      }
      // (2) 已提交但还没出 URL 的 pending 提交,只在该消息没有 URL 时算 pending
      if (links.length === 0) {
        const prompt = detectPendingExport(m.content || '')
        if (prompt) {
          const key = `pending:${m.id}`
          if (!seen.has(key)) {
            seen.add(key)
            ordered.push({ kind: 'pending', key, prompt, ts })
          }
        }
      }
    }
    if (ordered.length === 0) return

    // 时间轴对账:URL 出现时,把它之前所有 pending 条目升级为 done(挂同一个 URL),
    // 保证用户看到"先 pending,后 done"切换,不是同时挤两条。
    let touchedUnread = false
    setDownloads((prev) => {
      const next = [...prev]
      const indexById = new Map(next.map((d, i) => [d.task_id, i]))

      for (const item of ordered) {
        if (item.kind === 'pending') {
          if (!indexById.has(item.key)) {
            const newTask: DownloadTask = {
              task_id: item.key,
              kind: 'file',
              prompt: item.prompt,
              status: 'pending',
              created_at: item.ts
            }
            // 同一轮已有真实导出任务(export_attendance 等会建真 download_task)时,不再造正则
            // 占位 —— 否则一次导出在侧栏显示两条(占位 + 真任务)。
            if (!isPlaceholderCovered(newTask, next)) {
              next.push(newTask)
              indexById.set(item.key, next.length - 1)
              touchedUnread = true
            }
          }
          continue
        }
        // done URL:把列表里所有"前端造的、比它早、还在 pending"的占位升级为 done(挂这个 URL)。
        // 只升级 task_id 以 `pending:` 开头的前端占位 —— 它们是 detectPendingExport 凭 AI 措辞
        // 造的(key=`pending:${m.id}`),后端没有这条记录、poller 永远更新不到,不收尾就永远
        // 转圈(刘洪等多人 2026-06 反馈)。真实后端任务(task_id 是任务号/URL)绝不在这里动,
        // 继续走轮询,避免把另一个还在跑的任务误标完成、挂错下载链接。
        // 不再局限本批 pendingKeys —— 跨消息出现的占位也能被任意一个真实 done 收尾。
        for (let i = 0; i < next.length; i++) {
          const d = next[i]
          if (
            d.status === 'pending' &&
            d.task_id.startsWith('pending:') &&
            d.created_at <= item.ts
          ) {
            next[i] = {
              ...d,
              status: 'done',
              url: item.url,
              filename: item.filename,
              prompt: d.prompt || item.filename
            }
            touchedUnread = true
          }
        }
        // 再把 done URL 自己作为一条加入(可能跟已有 pending 重复升级,但 task_id 不同就两条;
        // 如果完全没 pending 兜底,这条独立 done 仍然能展示)
        const exists = indexById.has(item.key)
        const upgradedHere = next.some(
          (d) => d.url === item.url && d.task_id !== item.key
        )
        if (!exists && !upgradedHere) {
          const newTask: DownloadTask = {
            task_id: item.key,
            kind: 'file',
            prompt: item.filename,
            status: 'done',
            url: item.url,
            filename: item.filename,
            created_at: item.ts
          }
          next.push(newTask)
          indexById.set(item.key, next.length - 1)
          touchedUnread = true
        }
      }

      if (next.length === prev.length && !touchedUnread) return prev
      // 新条目时间倒序展示
      next.sort((a, b) => b.created_at - a.created_at)
      return next
    })
    if (touchedUnread && !downloadsDrawerOpen) setUnreadDownload(true)
  }, [messages, downloadsDrawerOpen])

  /**
   * 兜底清理:真实导出任务(export_attendance 等,经 SSE / 轮询 / 跨 session 恢复入列)到达
   * downloads 后,移除被它覆盖的 detectPendingExport 正则占位 —— 覆盖"占位先于真任务到达"的
   * 时序(message-scan 已挡住"真任务先到"那侧)。stripCoveredPendingExports 无可剔除时返回原
   * 引用,React 自动跳过更新,不与 message-scan 形成抖动循环。
   */
  useEffect(() => {
    setDownloads((prev) => stripCoveredPendingExports(prev))
  }, [downloads])

  const animateAssistantReply = useCallback(
    async (messageId: string, fullText: string) => {
      replacePendingMessage(messageId, (prev) => ({
        ...prev,
        content: '',
        streamPreview: undefined,
        pending: true
      }))
      let nextText = ''
      const chars = Array.from(fullText)
      for (let i = 0; i < chars.length; i++) {
        nextText += chars[i]
        setMessageContent(messageId, nextText, true)
        if (i < 24) {
          await new Promise((resolve) => setTimeout(resolve, 14))
        }
      }
      // 打字结束:标记完成态。活动区自动折叠,避免占用可视区;用户点 chip 可再展开
      replacePendingMessage(messageId, (prev) => ({
        ...prev,
        content: fullText,
        pending: false,
        streamPreview: undefined,
        runDoneAt: Date.now(),
        activityOpen: false
      }))
    },
    [setMessageContent, replacePendingMessage]
  )

  /**
   * fetchSessions —— 拉该 user+group 的最近 100 条 session 预览(给历史抽屉用)。
   * 抽屉每次打开时调一次,数据量小(<10KB),不做缓存。
   */
  const fetchSessions = useCallback(async () => {
    if (!userId || !groupId) return
    setSessionsLoading(true)
    try {
      const resp = await fetch(
        `${AI_CLAW_BASE_URL}/sessions?user_id=${encodeURIComponent(
          userId
        )}&group_id=${encodeURIComponent(groupId)}`,
        { credentials: 'include' }
      )
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const data = await resp.json()
      const list = Array.isArray(data?.sessions)
        ? (data.sessions as SessionPreview[])
        : []
      setSessions(list)
    } catch (err) {
      console.error('拉历史会话失败', err)
      setSessions([])
    } finally {
      setSessionsLoading(false)
    }
  }, [userId, groupId])

  /**
   * 切换到指定 session: 把当前 sessionId 换成 picked,清前端 state,然后 fetchContext 拉那个 session。
   * 这是"点历史会话"的入口,跟"新会话"互补。
   */
  const handleSwitchSession = useCallback(
    (picked: string) => {
      if (!picked || picked === sessionId) {
        setSessionsDrawerOpen(false)
        return
      }
      setSessionId(picked)
      setMessages([])
      setInputValue('')
      setDownloads([])
      setUnreadDownload(false)
      setUnreadPlan(false)
      setSessionsDrawerOpen(false)
      // 拉历史
      void fetchContext(picked)
    },
    [sessionId, fetchContext]
  )

  /**
   * 新会话: 立刻把前端 state 清空,换新 session_id,用户无阻塞;
   * 同时 fire-and-forget 调用后端 /compact —— 它会把该 user+group 下历史压成个人画像、清旧消息。
   */
  const handleNewSession = useCallback(() => {
    if (!userId || !groupId || compactCoolingDown) return
    const freshSessionId = buildSessionId(userId, groupId)
    setSessionId(freshSessionId)
    setMessages([])
    setInputValue('')
    setRecentExpanded(false)
    setDownloads([])
    setUnreadDownload(false)
    setUnreadPlan(false)
    setDownloadsDrawerOpen(false)
    setPlanDrawerOpen(false)
    setShowOnboarding(false)
    setCompactCoolingDown(true)
    window.setTimeout(() => setCompactCoolingDown(false), 2000)
    fetch(`${AI_CLAW_BASE_URL}/compact`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, group_id: groupId })
    })
      .then((r) => {
        if (!r.ok) {
          console.warn('[AiPhotoJun] compact 非 200:', r.status)
        }
      })
      .catch((err) => {
        console.warn('[AiPhotoJun] compact 请求失败:', err)
      })
    // 说明:此处不 re-fetch /context,避免 compact 还没落时拉回老消息。
    // recentQueries 保留页面加载时拉的那一次快照,用户本次会话里新发的 query
    // 要等下次开页面才会出现在"最近查询"里——可接受。
    // 「试试问我」也不在这里重拉,沿用本次打开时拿到的那一批,避免点新会话后等 3s 才刷新。
    // 想换一批 → 关闭面板再打开(presetFetchedRef 在 open=false 时重置触发重拉)。
    focusInput()
  }, [userId, groupId, compactCoolingDown, focusInput])

  const handlePickFile = useCallback(() => {
    if (sending) return
    const input = fileInputRef.current
    if (input) input.click()
  }, [sending])

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.id === id)
      if (target?.previewUrl) {
        try {
          URL.revokeObjectURL(target.previewUrl)
        } catch (_) {
          /* noop */
        }
      }
      return prev.filter((a) => a.id !== id)
    })
  }, [])

  const uploadOne = useCallback(
    async (item: AttachmentItem, file: File) => {
      try {
        const tokenMap: any = await dispatch(getOSSToken() as any)
        const bucketToken = tokenMap?.[OSS_BUCKET_NAME]
        if (!bucketToken) {
          throw new Error('获取 OSS 凭证失败')
        }
        const ext = file.name.includes('.')
          ? file.name.slice(file.name.lastIndexOf('.') + 1)
          : 'bin'
        const safeUserId = userId || 'anon'
        const ossKey = `ai-photo-jun/${safeUserId}/${uuid()}.${ext}`
        await uploadToOSS({
          key: ossKey,
          OSSAccessKeyId: bucketToken['accessid'],
          policy: bucketToken['policy'],
          Signature: bucketToken['signature'],
          file
        })
        const url = `https://net-cloud.xhey.top/${ossKey}`
        setAttachments((prev) =>
          prev.map((a) =>
            a.id === item.id ? { ...a, url, uploading: false } : a
          )
        )
      } catch (err) {
        console.error('[AiPhotoJun] 附件上传失败:', err)
        antdMessage.error(`${file.name} 上传失败`)
        const errorMessage = err instanceof Error ? err.message : '上传失败'
        setAttachments((prev) =>
          prev.map((a) =>
            a.id === item.id
              ? { ...a, uploading: false, error: errorMessage }
              : a
          )
        )
      }
    },
    [dispatch, userId]
  )

  /**
   * 失败重试:不让用户重新选文件,直接复用 AttachmentItem 里保存的原始 File 引用,
   * 把状态改回 uploading 后重新跑一次 uploadOne。
   * 没存 file (理论上不会发生)或不是 error 态时短路。
   */
  const retryUpload = useCallback(
    (id: string) => {
      setAttachments((prev) => {
        const target = prev.find((a) => a.id === id)
        if (!target || !target.file || !target.error) return prev
        const file = target.file
        // 在 setAttachments callback 外触发上传,避免同步嵌套
        queueMicrotask(() => {
          void uploadOne({ ...target, uploading: true, error: undefined }, file)
        })
        return prev.map((a) =>
          a.id === id ? { ...a, uploading: true, error: undefined } : a
        )
      })
    },
    [uploadOne]
  )

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || [])
      e.target.value = ''
      if (files.length === 0) return

      // 单次选择数量上限
      let picked = files
      if (picked.length > MAX_ATTACH_PER_PICK) {
        antdMessage.warning(
          `一次最多选 ${MAX_ATTACH_PER_PICK} 个文件,已取前 ${MAX_ATTACH_PER_PICK} 个`
        )
        picked = picked.slice(0, MAX_ATTACH_PER_PICK)
      }
      // 会话累计附件数上限
      const remaining = MAX_ATTACH_TOTAL - attachments.length
      if (remaining <= 0) {
        antdMessage.warning(`本会话最多上传 ${MAX_ATTACH_TOTAL} 个附件`)
        return
      }
      if (picked.length > remaining) {
        antdMessage.warning(
          `本会话最多上传 ${MAX_ATTACH_TOTAL} 个附件,已取前 ${remaining} 个`
        )
        picked = picked.slice(0, remaining)
      }

      const accepted: Array<{ item: AttachmentItem; file: File }> = []
      picked.forEach((file) => {
        if (file.size > MAX_UPLOAD_BYTES) {
          antdMessage.error(`${file.name} 超过 500MB,已跳过`)
          return
        }
        const item: AttachmentItem = {
          id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          name: file.name,
          size: file.size,
          uploading: true,
          previewUrl: isImageFile(file.name)
            ? URL.createObjectURL(file)
            : undefined,
          file
        }
        accepted.push({ item, file })
      })
      if (accepted.length === 0) return
      setAttachments((prev) => [...prev, ...accepted.map((a) => a.item)])
      // 上传并发上限:同时最多 MAX_UPLOAD_CONCURRENCY 个在传(不再一次性全发)
      void runWithConcurrency(
        accepted,
        MAX_UPLOAD_CONCURRENCY,
        ({ item, file }) => uploadOne(item, file)
      )
    },
    [uploadOne, attachments.length]
  )

  // 组件卸载时回收 object URL
  useEffect(() => {
    return () => {
      attachments.forEach((a) => {
        if (a.previewUrl) {
          try {
            URL.revokeObjectURL(a.previewUrl)
          } catch (_) {
            /* noop */
          }
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleAskAnswer = useCallback(
    (
      messageId: string,
      answers: Array<{ question: AskQuestion; labels: string[] }>
    ) => {
      if (sending || answers.length === 0) return
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId || !m.ask) return m
          const answered: Record<string, string> = { ...(m.ask.answered || {}) }
          answers.forEach((a) => {
            answered[a.question.question] = a.labels.join('、')
          })
          return { ...m, ask: { ...m.ask, answered } }
        })
      )
      const lines = answers.map(
        (a) => `- ${a.question.question} → ${a.labels.join('、')}`
      )
      const reply = '以下是我对上面问题的回答:\n' + lines.join('\n')
      void sendMessageInternal(reply)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sending]
  )

  /**
   * 用户点"同意执行":把 plan 卡锁成 approved 态,把"同意,按此计划执行"作为新一轮 user 消息发出去
   * 下一轮 agent 会看到对话历史,继续按计划干活。
   */
  const handlePlanApprove = useCallback(
    (messageId: string) => {
      if (sending) return
      // 神策埋点 ai_b8112_click: 同意执行计划 (warningLevel=plan.risk_level)
      const curr = messagesRefForTrack.current.find((m) => m.id === messageId)
      const idx = curr ? messagesRefForTrack.current.indexOf(curr) : -1
      const assistantRank =
        idx < 0
          ? 0
          : messagesRefForTrack.current
              .slice(0, idx + 1)
              .filter((m) => m.role === 'assistant').length
      trackAiB8112Click({
        clickItem: '同意执行计划',
        sessionID: sessionId,
        msgID: curr?.messageId || '',
        rankNum: assistantRank,
        warningLevel: curr?.plan?.risk_level || 'unknown'
      })
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId || !m.plan) return m
          return { ...m, plan: { ...m.plan, answered: 'approved' } }
        })
      )
      void sendMessageInternal('同意，按此计划执行。')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sending, sessionId]
  )
  /**
   * 用户在 "我要调整" 编辑态点 "提交":把改后的 steps 格式化成 markdown 文本
   * 作为用户消息发回 AI,让 AI 按新版本继续执行。卡片标 answered='rejected' 转只读。
   * 显示文案改为 "已按调整提交"。
   */
  const handlePlanSubmitEdit = useCallback(
    (messageId: string, editedSteps: PlanStep[]) => {
      if (sending) return
      // 神策埋点 ai_b8112_click: 调整执行计划 (warningLevel=plan.risk_level)
      const curr = messagesRefForTrack.current.find((m) => m.id === messageId)
      const idx = curr ? messagesRefForTrack.current.indexOf(curr) : -1
      const assistantRank =
        idx < 0
          ? 0
          : messagesRefForTrack.current
              .slice(0, idx + 1)
              .filter((m) => m.role === 'assistant').length
      trackAiB8112Click({
        clickItem: '调整执行计划',
        sessionID: sessionId,
        msgID: curr?.messageId || '',
        rankNum: assistantRank,
        warningLevel: curr?.plan?.risk_level || 'unknown'
      })
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId || !m.plan) return m
          return { ...m, plan: { ...m.plan, answered: 'rejected' } }
        })
      )
      // 方案 A:把修改后的 plan 格式化成 markdown 文本作为用户消息发回
      const text =
        '我已调整执行计划如下，请按此执行：\n' +
        editedSteps
          .map((s, i) => {
            const title = `${i + 1}. ${s.title}`
            const desc = s.description ? `\n   ${s.description}` : ''
            return title + desc
          })
          .join('\n')
      void sendMessageInternal(text)
    },
    [sending, sessionId]
  )

  const sendMessageRef =
    useRef<(q: string, sourceType?: AiB8112SourceType) => Promise<void>>()
  const sendMessageInternal = useCallback(
    (text: string, sourceType?: AiB8112SourceType) => {
      const fn = sendMessageRef.current
      if (fn) return fn(text, sourceType)
      return Promise.resolve()
    },
    []
  )

  const sendMessage = useCallback(
    async (question?: string, sourceTypeOverride?: AiB8112SourceType) => {
      // sourceType 推断快照: send 前 ref 里 messages 长度
      //   0 = 空会话 (= 首次手动输入); >0 = 续问
      // 必须在 setMessages 前快照,后面 finally 里 messages 已被本轮的 user/assistant 撑满。
      const messagesAtSendStart = messagesRefForTrack.current?.length ?? 0
      const content = (question ?? inputValue).trim()
      // 用户发消息后关闭首次引导轮播
      setShowOnboarding(false)
      // 只有用户本人在输入框触发的 send 才把附件一起带出去;
      // quick-ask / ask-answer 走 sendMessageInternal(传 question) 不带附件
      const useAttachments = question === undefined
      const attached = useAttachments
        ? attachments.filter((a) => a.url && !a.error)
        : []
      const hasAttached = attached.length > 0
      if ((!content && !hasAttached) || sending || !userId || !groupId) return
      if (useAttachments && attachments.some((a) => a.uploading)) return

      const nextSessionId = sessionId || buildSessionId(userId, groupId)
      if (!sessionId) {
        setSessionId(nextSessionId)
      }

      // 气泡里的 content 保留用户原文;附件以缩略图/文件条单独渲染
      const userMessage: ChatMessage = {
        id: buildMessageId('user'),
        role: 'user',
        content,
        messageAttachments: hasAttached
          ? attached.map((a) => ({
              id: a.id,
              name: a.name,
              url: a.url as string,
              isImage: isImageFile(a.name)
            }))
          : undefined
      }
      // 后端 input 额外拼 Markdown,Agent 能从正文看到 URL
      const attachmentLines = attached.map((a) =>
        isImageFile(a.name) ? `![${a.name}](${a.url})` : `[${a.name}](${a.url})`
      )
      const serverInput = [...attachmentLines, content]
        .filter((s) => !!s && s.length > 0)
        .join('\n\n')
      const assistantMessageId = buildMessageId('assistant')
      const runStartedAt = Date.now()
      setMessages((prev) => [
        ...prev,
        userMessage,
        {
          id: assistantMessageId,
          role: 'assistant',
          content: '',
          pending: true,
          progressSteps: [],
          // 默认收起活动区,只展示一行 Motion 风 chip;用户点击 chip 后再展开看每一步
          activityOpen: false,
          runStartedAt,
          // 神策埋点 ai_b8112_completed.content 用,挂在 assistant 上方便完成时拿到
          userQuestion: content
        }
      ])
      setInputValue('')
      if (useAttachments && hasAttached) {
        // 清掉附件 + 回收 object URL
        setAttachments((prev) => {
          prev.forEach((a) => {
            if (a.previewUrl) {
              try {
                URL.revokeObjectURL(a.previewUrl)
              } catch (_) {
                /* noop */
              }
            }
          })
          return []
        })
      }
      setSending(true)
      // 创建本轮 AbortController, 用户点停止按钮时调 abort()
      const controller = new AbortController()
      runAbortRef.current = controller
      // 用户刚发一条,意图就是看新消息 → 强制滚到底,并重置粘性状态
      scrollToBottom(true)

      // 排障原文(错误 message), 只进控制台/Sentry/finally 的 interrupted 判定, 不上报神策
      let caughtErrorMsg = ''
      // 神策 ai_b8112_completed 用的枚举码: errorMsg 平台 in 校验只收枚举值,
      // 原文细节靠 string1=trace_id 反查后端日志。billingInsufficient 已登记,
      // timeout / networkError / serverError 需平台补进 errorMsg 枚举
      let caughtErrorCode:
        | ''
        | 'billingInsufficient'
        | 'timeout'
        | 'userAbort'
        | 'networkError'
        | 'serverError' = ''
      // SSE 终止识别:handleSseItem 收到明确终止事件 (done / error / 余额不足) 时置 true。
      // while 跳出时若仍是 false → 流意外关闭 (后端没发任何终止事件就 close 了),
      // 进 finally 时把消息标记为 'unexpected_close' interrupted, UI 给重试按钮。
      let sseTerminationSeen = false
      // SSE 静默超时检测:reader 每次拿到 chunk / 处理 SSE 事件 (含 heartbeat) 时更新这个时间戳;
      // setInterval 每 SSE_WATCHDOG_INTERVAL_MS 检查一次, 超过 SSE_SILENCE_MS 没收到任何数据
      // → controller.abort(), 进入 catch 的 timeout 分支, 停止转圈并显示"AI 似乎卡住了"+ 重试按钮。
      //
      // 阈值依据后端心跳节奏来定:后端在 /run 主循环里每 10s 必发一个 heartbeat 事件, 这个心跳
      // 由 handler 的 select 循环发出, 跟 agent 在不在忙无关 —— agent 跑在另一个 goroutine,
      // 即便一次 LLM 调用耗时 40s+, 心跳照样每 10s 一个。所以只要连接是通的, 两次"收到数据"的
      // 间隔上限就是这 10s。一旦后端异常断开(连接半开, reader.read() 既不返回 done 也不报错),
      // 数据彻底静默, 这个 watchdog 就是唯一能让前端收场的兜底。
      //
      // 取 3 个心跳周期 = 30s:给网络抖动 / 偶发心跳延迟留了 2 倍余量, 正常请求不会误判;
      // 后端异常断开后 ~30s 内前端就会停止转圈给出提示。
      // (旧值 120s 把"静默"误当成"agent 在忙", 但心跳已经把这两件事解耦了 —— 真断连时用户要
      //  对着假装还在跑的转圈圈干等满 2 分钟, 这正是本次要修的问题。)
      const SSE_HEARTBEAT_INTERVAL_MS = 10_000
      const SSE_SILENCE_MS = SSE_HEARTBEAT_INTERVAL_MS * 3
      const SSE_WATCHDOG_INTERVAL_MS = 5_000
      let lastChunkAt = Date.now()
      let timedOut = false
      const watchdog = window.setInterval(() => {
        if (Date.now() - lastChunkAt > SSE_SILENCE_MS) {
          timedOut = true
          try {
            controller.abort()
          } catch {
            /* abort 异常忽略 */
          }
        }
      }, SSE_WATCHDOG_INTERVAL_MS)

      try {
        const response = await fetch(`${AI_CLAW_BASE_URL}/run`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream'
          },
          body: JSON.stringify({
            input: serverInput,
            session_id: nextSessionId,
            user_name: userName,
            user_id: userId,
            group_id: groupId
          }),
          signal: controller.signal
        })

        if (!response.ok || !response.body) {
          throw new Error(`请求失败(${response.status})`)
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let buffer = ''

        const handleSseItem = async (item: {
          event: string
          data: string
        }): Promise<void> => {
          // 保活心跳:后端每 10s 推一个 event:heartbeat 让 nginx 不把连接当 idle 杀掉,前端无视
          if (item.event === 'heartbeat') return
          // start 事件捕获 trace_id 和 message_id:
          //   trace_id  → 挂到本条 assistant 上,供"调用链路"诊断弹窗
          //   message_id → 挂到本条 assistant 上,前端用它向 /feedback 提交点赞/评论
          if (item.event === 'start') {
            try {
              const startData = JSON.parse(item.data) as {
                trace_id?: string
                message_id?: string
              }
              const traceID = startData?.trace_id
              const backendMsgID = startData?.message_id
              if (traceID || backendMsgID) {
                setMessages((prev) =>
                  prev.map((m) => {
                    if (m.id !== assistantMessageId) return m
                    const next = { ...m }
                    if (traceID) next.traceID = traceID
                    if (backendMsgID) next.messageId = backendMsgID
                    return next
                  })
                )
              }
            } catch {
              // ignore malformed start payload
            }
            return
          }
          if (item.event === 'progress') {
            let parsed: { kind: string; data?: Record<string, any> }
            try {
              parsed = JSON.parse(item.data)
            } catch {
              return
            }
            const d = parsed.data || {}
            if (parsed.kind === 'llm_delta') {
              const piece = typeof d.piece === 'string' ? d.piece : ''
              const cumulative =
                typeof d.cumulative === 'string' ? d.cumulative : null
              if (!piece && cumulative == null) return
              setMessages((prev) =>
                prev.map((m) => {
                  if (m.id !== assistantMessageId) return m
                  const base =
                    cumulative != null
                      ? cumulative
                      : (m.streamPreview || '') + piece
                  const trimmed =
                    base.length > 2000 ? `${base.slice(0, 2000)}…` : base
                  return { ...m, streamPreview: trimmed }
                })
              )
              return
            }
            if (parsed.kind === 'card_render') {
              const card = d as unknown as CardPayload
              if (card && (card as { type?: string }).type) {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMessageId
                      ? { ...m, cards: [...(m.cards || []), card] }
                      : m
                  )
                )
              }
              return
            }
            if (parsed.kind === 'ask_user_question') {
              const questions = Array.isArray(d.questions)
                ? (d.questions as AskQuestion[])
                : []
              if (questions.length > 0) {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMessageId
                      ? { ...m, ask: { questions, answered: {} } }
                      : m
                  )
                )
              }
              return
            }
            if (parsed.kind === 'tool_start') {
              const toolName = d.tool != null ? String(d.tool) : 'tool'
              const argsRaw = d.args != null ? String(d.args) : ''
              const callId =
                d.call_id != null && String(d.call_id)
                  ? String(d.call_id)
                  : `step-${Date.now()}-${Math.random()
                      .toString(36)
                      .slice(2, 6)}`
              // 后端 SSE 在 tool_start.data.label 里直接带业务化文案 {running, done, detail?},
              // 来自 xiaohei-claw 的 LabelProvider(每个工具自己定义,内聚在工具实现里)。
              // 前端这边优先用后端给的;老后端 / 没实现 LabelProvider 的工具回到本地 fallback 表。
              // 重要:后端推了有效 label 时,detail 也完全跟着后端(后端说没 detail 就是没,
              // 前端不能自己再脑补,否则像 mcp_tool_search 这类工具的 query 会被前端 fallback
              // 当 detail 透回去,刘乾隆图2 里 "select:xxx" 就是这种走漏)。
              const rawLabel =
                d.label && typeof d.label === 'object'
                  ? (d.label as {
                      running?: unknown
                      done?: unknown
                      detail?: unknown
                    })
                  : null
              const backendValid =
                !!rawLabel &&
                typeof rawLabel.running === 'string' &&
                typeof rawLabel.done === 'string' &&
                (rawLabel.running.length > 0 || rawLabel.done.length > 0)
              const label = backendValid
                ? {
                    running: rawLabel!.running as string,
                    done: rawLabel!.done as string
                  }
                : resolveProgressLabel(toolName, argsRaw)
              const detail = backendValid
                ? typeof rawLabel!.detail === 'string' && rawLabel!.detail
                  ? rawLabel!.detail
                  : undefined
                : resolveProgressDetail(toolName, argsRaw)
              const now = Date.now()
              const roundNum = typeof d.round === 'number' ? d.round : undefined
              const step: ProgressStep = {
                id: callId,
                label,
                status: 'running',
                detail,
                ts: now,
                tool_name: toolName,
                args_raw: argsRaw,
                started_at: now,
                round: roundNum
              }
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessageId
                    ? {
                        ...m,
                        progressSteps: [...(m.progressSteps || []), step]
                      }
                    : m
                )
              )
              return
            }
            if (parsed.kind === 'propose_plan') {
              // agent 调 propose_plan 工具 → 本轮 pause → 前端气泡里挂执行计划卡
              const steps = Array.isArray(d.steps)
                ? (d.steps as unknown[])
                    .filter(
                      (s): s is PlanStep =>
                        !!s &&
                        typeof s === 'object' &&
                        typeof (s as PlanStep).title === 'string'
                    )
                    .map((s) => ({
                      title: (s as PlanStep).title,
                      description: (s as PlanStep).description
                    }))
                : []
              if (steps.length === 0) return
              const risk =
                d.risk_level === 'high' ||
                d.risk_level === 'medium' ||
                d.risk_level === 'low'
                  ? (d.risk_level as PlanPayload['risk_level'])
                  : undefined
              const plan: PlanPayload = {
                summary: typeof d.summary === 'string' ? d.summary : '',
                steps,
                estimated_time:
                  typeof d.estimated_time === 'string'
                    ? d.estimated_time
                    : undefined,
                risk_level: risk
              }
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessageId ? { ...m, plan } : m
                )
              )
              // 新 plan = 红点亮起
              setUnreadPlan(true)
              return
            }
            if (parsed.kind === 'download_task') {
              // generate_image / export_photos 等异步任务创建瞬间推过来,侧栏立即多一条
              const taskID = typeof d.task_id === 'string' ? d.task_id : ''
              if (!taskID) return
              const kind: 'image' | 'file' =
                d.kind === 'file' ? 'file' : 'image'
              const subkind =
                d.subkind === 'export_photos' ||
                d.subkind === 'export_watermark_data' ||
                d.subkind === 'export_attendance' ||
                d.subkind === 'publish_artifact' ||
                d.subkind === 'generate_image'
                  ? (d.subkind as DownloadTask['subkind'])
                  : undefined
              // kind=image 用 prompt 当标题; kind=file 用 title 字段
              const titleText =
                kind === 'file'
                  ? typeof d.title === 'string'
                    ? d.title
                    : '文件导出'
                  : typeof d.prompt === 'string'
                    ? d.prompt
                    : '生成图片'
              const task: DownloadTask = {
                task_id: taskID,
                kind,
                subkind,
                group_id:
                  typeof d.group_id === 'string' ? d.group_id : undefined,
                prompt: titleText,
                status: 'pending',
                created_at:
                  typeof d.created_at === 'number'
                    ? d.created_at
                    : Math.floor(Date.now() / 1000),
                ...parsePreviewFields(d as Record<string, unknown>)
              }
              setDownloads((prev) => {
                if (prev.some((x) => x.task_id === task.task_id)) return prev
                return [task, ...prev]
              })
              setUnreadDownload(true)
              // publish_artifact 带 preview_url 的 xlsx,额外挂到当前流式 assistant 消息上渲染富预览卡片。
              // 关联靠"产生它的那条消息"(后端 AttachmentCollector 也是这么归档的),
              // 不依赖正文里有没有文件链接 —— 即便正文只说"去右上角下载列表",卡片也稳定渲染。
              const previewFile = previewFileFromDownload(
                d as Record<string, unknown>
              )
              if (previewFile) {
                setMessages((prev) => {
                  // 优先挂到当前流式 assistant 消息；找不到则 fallback 到最近一条 assistant
                  let targetId = assistantMessageId
                  if (!prev.some((m) => m.id === targetId)) {
                    const lastAssistant = [...prev]
                      .reverse()
                      .find((m) => m.role === 'assistant')
                    targetId = lastAssistant?.id || targetId
                  }
                  return prev.map((m) => {
                    if (m.id !== targetId) return m
                    if (
                      (m.previewFiles || []).some(
                        (f) => f.url === previewFile.url
                      )
                    ) {
                      return m
                    }
                    return {
                      ...m,
                      previewFiles: [...(m.previewFiles || []), previewFile]
                    }
                  })
                })
              }
              return
            }
            if (parsed.kind === 'apply_page_filter') {
              // 后端 agent 调 apply_page_filter 工具瞬间推过来,结构化的照片筛选条件。
              // 两件事:(1) 通知父组件联动主页 (2) 气泡内挂一张"已同步到主页"回显小卡。
              // [debug 2026-04-28] 之前这里只解了 7 个字段,把后端 apply_page_filter.go 推的
              // location_key / department_ids / source_types / media_types / watermarks /
              // locations / exclude_keywords / exist_watermark / label_ids / label_match_all /
              // user_display_names 全部静默丢掉。导致"成都最近一个月的照片"这类 NL 路径走 SSE 时
              // location_key='成都' 进不来 → 主页 LocationRecommend 被置空。
              // 卡片点击路径(RankingCard/StatCard.handleClick)在 JS 内直接构造 payload,字段完整,
              // 所以那条路径之前是对的,问题只暴露在"agent 自动调工具"这条 SSE 路径。
              const pickStringArray = (v: unknown): string[] | undefined =>
                Array.isArray(v)
                  ? (v as unknown[])
                      .filter((x) => typeof x === 'string')
                      .map((x) => String(x))
                  : undefined
              const pickNumberArray = (v: unknown): number[] | undefined =>
                Array.isArray(v)
                  ? (v as unknown[])
                      .filter((x) => typeof x === 'number')
                      .map((x) => Number(x))
                  : undefined
              const watermarks = Array.isArray(d.watermarks)
                ? (d.watermarks as unknown[])
                    .filter(
                      (x): x is Record<string, unknown> =>
                        !!x && typeof x === 'object'
                    )
                    .map((x) => ({
                      watermark_base_id:
                        typeof x.watermark_base_id === 'string'
                          ? x.watermark_base_id
                          : undefined,
                      group_watermark_id:
                        typeof x.group_watermark_id === 'string'
                          ? x.group_watermark_id
                          : undefined
                    }))
                : undefined
              const locations = Array.isArray(d.locations)
                ? (d.locations as unknown[])
                    .filter(
                      (x): x is Record<string, unknown> =>
                        !!x && typeof x === 'object'
                    )
                    .map((x) => ({
                      name: typeof x.name === 'string' ? x.name : '',
                      detail:
                        typeof x.detail === 'string' ? x.detail : undefined,
                      type: typeof x.type === 'number' ? x.type : undefined,
                      poi_type:
                        typeof x.poi_type === 'number' ? x.poi_type : undefined
                    }))
                    .filter((x) => x.name)
                : undefined
              const payload: PageFilterPayload = {
                nl_query: typeof d.nl_query === 'string' ? d.nl_query : '照片',
                keywords: pickStringArray(d.keywords),
                user_ids: pickStringArray(d.user_ids),
                user_display_names: pickStringArray(d.user_display_names),
                department_ids: pickStringArray(d.department_ids),
                exclude_user_ids: parseStringArray(d.exclude_user_ids),
                start_time:
                  typeof d.start_time === 'string' ? d.start_time : undefined,
                end_time:
                  typeof d.end_time === 'string' ? d.end_time : undefined,
                source_types: pickNumberArray(d.source_types),
                media_types: pickNumberArray(d.media_types),
                watermarks,
                locations,
                location_key:
                  typeof d.location_key === 'string'
                    ? d.location_key
                    : undefined,
                exclude_keywords: pickStringArray(d.exclude_keywords),
                exist_watermark:
                  typeof d.exist_watermark === 'number'
                    ? d.exist_watermark
                    : undefined,
                label_ids: pickStringArray(d.label_ids),
                label_match_all:
                  typeof d.label_match_all === 'boolean'
                    ? d.label_match_all
                    : undefined,
                preview_thumb_urls: pickStringArray(d.preview_thumb_urls),
                total: typeof d.total === 'number' ? d.total : undefined
              }
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessageId
                    ? { ...m, pageFilter: payload }
                    : m
                )
              )
              if (onApplyPageFilter) {
                // 把 source session/msg 一起带给主页, 用户后来点 AI 搜索 chip × 关闭时
                // 主页能上报神策埋点 web_workgroup_picspage_click(clickItem=点击关闭AI搜索chip)
                // 里的 sessionID + msgID(spec 标注"该 clickItem 时上报")。
                // - sessionId 直接从本组件 state 读
                // - msgID 用 SSE start 落下的后端 message_id;还没收到就降级到本地 assistantMessageId
                //   (前端临时 id, legacy- 前缀的话留空)
                const localMsg = messagesRefForTrack.current.find(
                  (m) => m.id === assistantMessageId
                )
                const backendMsgId = localMsg?.messageId || ''
                onApplyPageFilter({
                  ...payload,
                  source_session_id: sessionId || '',
                  source_msg_id: backendMsgId
                })
              }
              return
            }
            if (parsed.kind === 'suggest_questions') {
              const qs = Array.isArray(d.questions)
                ? (d.questions as unknown[])
                    .filter((q) => typeof q === 'string')
                    .map((q) => String(q).trim())
                    .filter((q) => q.length > 0)
                : []
              if (qs.length > 0) {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMessageId ? { ...m, suggestions: qs } : m
                  )
                )
              }
              return
            }
            if (parsed.kind === 'tool_end') {
              const callId = d.call_id != null ? String(d.call_id) : ''
              const ok = d.ok !== false
              const resultPreview =
                d.result_preview != null ? String(d.result_preview) : undefined
              const endedAt = Date.now()
              setMessages((prev) =>
                prev.map((m) => {
                  if (m.id !== assistantMessageId) return m
                  const steps = m.progressSteps || []
                  // 精确按 call_id 配对,找不到就落到最后一条 running
                  let idx = callId
                    ? steps.findIndex((s) => s.id === callId)
                    : -1
                  if (idx < 0) {
                    for (let i = steps.length - 1; i >= 0; i--) {
                      if (steps[i].status === 'running') {
                        idx = i
                        break
                      }
                    }
                  }
                  if (idx < 0) return m
                  const nextSteps = steps.slice()
                  nextSteps[idx] = {
                    ...nextSteps[idx],
                    status: ok ? 'ok' : 'fail',
                    result_preview: resultPreview,
                    ended_at: endedAt
                  }
                  return { ...m, progressSteps: nextSteps }
                })
              )
              return
            }
            // round: 用于神策埋点 ai_b8112_completed.number2 (AI 思考轮数)。
            // 后端 agent loop 每轮起手推 progress.kind='round' data.n=<round>,
            // 这里取 n+1 作为已进入的轮数 (round 从 0 起算)。
            // 进度可视化不依赖这个,UI 不变。
            if (parsed.kind === 'round') {
              const n = typeof d.n === 'number' ? d.n : 0
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessageId ? { ...m, roundCount: n + 1 } : m
                )
              )
              return
            }
            // 其余 assistant 中间思考:不再逐条展示(进度已结构化在步骤列表里)
            return
          }
          if (item.event === 'result') {
            const parsed = JSON.parse(item.data)
            const resultText =
              typeof parsed?.result === 'string'
                ? parsed.result
                : 'AI照片王返回了空结果'
            // 兜底:assistant 文本里如果混了 markdown 表格,前端当场切走改渲染成 HTML 卡片
            const { cleaned, extraCards, hadFallback } =
              extractFallbackCards(resultText)
            if (extraCards.length > 0) {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMessageId
                    ? {
                        ...m,
                        cards: [...(m.cards || []), ...extraCards],
                        fallbackNotice: hadFallback
                          ? '已为你把表格数据转成卡片'
                          : m.fallbackNotice
                      }
                    : m
                )
              )
            }
            await animateAssistantReply(assistantMessageId, cleaned)
            return
          }
          if (item.event === 'error') {
            const parsed = JSON.parse(item.data)
            // 余额不足走友好分支: 替换 pending assistant 内容 + 不抛错(让 done 正常关流)。
            // 其他错误仍按通用失败处理。
            if (parsed?.code === 'billing/insufficient') {
              // 因何触发(c8146 string1): caller 显式来源(试试问我/历史记录/hover卡片)优先,
              // 否则按发送时消息数推断(空会话=手动输入发送消息, 已有对话=继续追问)。
              // ⚠️ b8112 sourceType 的「手动输入 / 轮播卡片 / 快捷按钮」在 c8146 trigger 枚举里没有
              //   (范 2026-06-23 只给 b8112 加了轮播卡片/快捷按钮, 未动 c8146 积分不足弹窗的 trigger),
              //   故这三者发起、积分不足时一律降级按会话状态推断, 不直接透传(否则神策 c8146 校验会拦)。
              //   TODO(待范确认 c8146 trigger 是否新增轮播卡片/快捷按钮): 若加, 改成直接透传。
              const c8146Fallback: AiC8146Trigger =
                messagesAtSendStart === 0 ? '手动输入发送消息' : '继续追问'
              const insufficientTrigger: AiC8146Trigger =
                sourceTypeOverride === '手动输入' ||
                sourceTypeOverride === '轮播卡片' ||
                sourceTypeOverride === '快捷按钮'
                  ? c8146Fallback
                  : ((sourceTypeOverride as AiC8146Trigger | undefined) ??
                    c8146Fallback)
              const insufficientAvailable =
                typeof parsed?.available === 'number'
                  ? parsed.available
                  : (wallet?.remainingLimit ?? 0)
              // 神策埋点 ai_b8112_c8146_exposure (积分不足弹窗曝光, 2026-06-11 spec)
              trackAiC8146Exposure({
                sessionID: sessionId || '',
                trigger: insufficientTrigger,
                available: insufficientAvailable,
                cost: parsed?.cost ?? 0
              })
              replacePendingMessage(assistantMessageId, (prev) => ({
                ...prev,
                content:
                  '⚠️ 积分余额不足, 请联系管理员充值后继续使用 AI 统计照片。',
                pending: false,
                runDoneAt: Date.now(),
                streamPreview: undefined,
                activityOpen: false
              }))
              setInsufficientModal({
                available: parsed?.available ?? 0,
                cost: parsed?.cost ?? 0,
                trigger: insufficientTrigger
              })
              // 神策 ai_b8112_completed: finally 里据此报 result=失败 +
              // errorMsg=billingInsufficient (此前漏标, 余额不足轮被误报成 result=成功)
              caughtErrorMsg = '余额不足'
              caughtErrorCode = 'billingInsufficient'
              // 余额不足是明确终止, 不需要重试按钮 (重试也是同样结果)
              sseTerminationSeen = true
              return
            }
            // 服务端报错: throw 进 catch 分支统一处理
            sseTerminationSeen = true
            throw new Error(parsed?.error || 'AI照片王请求失败')
          }
          if (item.event === 'done') {
            // xiaohei-claw 在 done payload 里带 cost_saving (本次扣的积分,
            // server.reportBillingAndCost 拉 agentBilling.Report 返回)。
            // 0 = 没扣 / billing 未配置 / RPC 超时降级。这里只挂到 message 上,
            // chip 显示用的还是 wallet HTTP, 不动。
            //
            // 关键修复 (2026-06-04): 历史上 done 事件只处理了 cost_saving 字段, 没把 message 的
            // pending 关掉。后端发完 done 然后正常关流, UI 还在显示 "正在 xxx X.Xs" 假装在跑,
            // 用户傻等 — 即截图里 "正在计算结果 1m46s" 那种死锁。
            // 现在 done 事件触发时同步把 pending: false + runDoneAt 落进 message, UI 立即收口。
            let costSaving: number | undefined
            try {
              const parsed = JSON.parse(item.data) as {
                ok?: boolean
                cost_saving?: number
              }
              if (typeof parsed.cost_saving === 'number') {
                costSaving = parsed.cost_saving
              }
            } catch {
              /* ignore malformed done payload */
            }
            sseTerminationSeen = true
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMessageId
                  ? {
                      ...m,
                      pending: false,
                      runDoneAt: Date.now(),
                      streamPreview: undefined,
                      ...(costSaving !== undefined ? { costSaving } : {})
                    }
                  : m
              )
            )
            return
          }
        }

        for (;;) {
          const { value, done } = await reader.read()
          if (done) break
          // 每次拿到新 chunk 刷新 watchdog 时间戳, 避免 watchdog 误把"AI 正在思考"判成"卡死";
          // heartbeat 也算 chunk, 后端每 10s 推一次, 正常路径下 lastChunkAt 会一直被刷
          lastChunkAt = Date.now()
          buffer += decoder.decode(value, { stream: true })

          const lastBoundary = buffer.lastIndexOf('\n\n')
          if (lastBoundary === -1) continue

          const complete = buffer.slice(0, lastBoundary)
          buffer = buffer.slice(lastBoundary + 2)

          const events = parseSSEChunk(complete)
          for (const evt of events) {
            await handleSseItem(evt)
          }
        }
        if (buffer.trim()) {
          for (const evt of parseSSEChunk(buffer + '\n\n')) {
            await handleSseItem(evt)
          }
        }
      } catch (error) {
        // 用户主动停止 → 替换 pending 内容为友好提示, 不报错
        // 同 abort 但 watchdog 触发 → 区分为 "timeout", 给重试按钮
        const isAbort =
          (error instanceof DOMException && error.name === 'AbortError') ||
          (error as Error)?.name === 'AbortError'
        if (isAbort && !timedOut) {
          // 用户主动点停止按钮: 显示 "已停止" + 重试按钮
          replacePendingMessage(assistantMessageId, (prev) => ({
            ...prev,
            content: prev.content || '已停止生成',
            pending: false,
            runDoneAt: Date.now(),
            streamPreview: undefined,
            activityOpen: false,
            interrupted: {
              reason: 'aborted',
              hint: '已停止生成'
            }
          }))
          caughtErrorMsg = '用户中止'
          caughtErrorCode = 'userAbort'
        } else if (isAbort && timedOut) {
          // watchdog 超时主动 abort: AI 没回应了 (网络断 / 后端 hang / SSE 流真挂)
          replacePendingMessage(assistantMessageId, (prev) => ({
            ...prev,
            content: prev.content || 'AI 似乎卡住了, 可能是网络中断',
            pending: false,
            runDoneAt: Date.now(),
            streamPreview: undefined,
            activityOpen: false,
            interrupted: {
              reason: 'timeout',
              hint: 'AI 长时间未响应, 可能是网络中断'
            }
          }))
          caughtErrorMsg = `超时 (${SSE_SILENCE_MS / 1000}s 无新数据)`
          caughtErrorCode = 'timeout'
        } else {
          console.error('AI照片王 请求失败:', error)
          replacePendingMessage(assistantMessageId, (prev) => ({
            ...prev,
            content: '连接 AI照片王 失败了，请稍后重试。',
            pending: false,
            runDoneAt: Date.now(),
            streamPreview: undefined,
            activityOpen: false,
            interrupted: {
              reason: 'error',
              hint: '连接 AI 失败'
            }
          }))
          caughtErrorMsg = (error as Error)?.message || '请求失败'
          // fetch 网络层挂掉抛 TypeError(Failed to fetch); 其余(后端 error 事件 /
          // 非 200 / 空结果)都是代码里自己 new Error 的服务端类失败
          caughtErrorCode =
            error instanceof TypeError ? 'networkError' : 'serverError'
        }
      } finally {
        window.clearInterval(watchdog)
        // 兜底: 不论是从 try 正常跑完 / catch 异常 / abort 退出, 都强制把这条 assistant message
        // 的 pending 关掉, 杜绝 "UI 还在转圈但其实早断" 的死锁。
        // 同时:
        //   - while 正常结束但没见 done/error/余额不足 (sseTerminationSeen=false) → 视为流意外关闭,
        //     标 interrupted='unexpected_close', UI 给重试按钮
        //   - 否则保留 catch 阶段已经设的 interrupted (aborted/timeout/error) 不动
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== assistantMessageId) return m
            if (!m.pending && m.interrupted) return m // catch 已经处理过, 不重复
            // 三种"用户傻等"出口需要打 interrupted 标:
            //   1. catch 路径已经处理 (aborted/timeout/error) → caughtErrorMsg 非空, 不重复
            //   2. 流意外关闭 (没收到 done 事件就 close 了) → sseTerminationSeen=false
            //   3. 协议层 done 收到了, 但业务层 message 没产出任何东西
            //      (无文本/卡片/page filter/plan/ask/suggestions) → 跑了等于没跑
            const hasMeaningfulOutput =
              (m.content && m.content.trim().length > 0) ||
              (m.cards && m.cards.length > 0) ||
              !!m.pageFilter ||
              !!m.plan ||
              !!m.ask ||
              (m.suggestions && m.suggestions.length > 0)
            let interrupted = m.interrupted
            if (!interrupted && !caughtErrorMsg) {
              if (!sseTerminationSeen) {
                interrupted = {
                  reason: 'unexpected_close',
                  hint: '回答提前结束了'
                }
              } else if (!hasMeaningfulOutput) {
                interrupted = {
                  reason: 'empty_response',
                  hint: 'AI 没给出回答, 试试重新提问'
                }
              }
            }
            return {
              ...m,
              pending: false,
              runDoneAt: m.runDoneAt ?? Date.now(),
              streamPreview: undefined,
              ...(interrupted && interrupted !== m.interrupted
                ? { interrupted }
                : {})
            }
          })
        )
        setSending(false)
        runAbortRef.current = null
        // 一次 /run 跑完(成功 / 失败 / 中止 / 余额不足)都刷一次余额, 让 chip 反映扣费后的最新值
        void fetchWallet()
        // 神策埋点 ai_b8112_completed: 统一在 finally 上报一次, 不论成功/失败/中止/余额不足。
        // - errorMsg: 空 = 成功; '用户中止' / 后端错文本 = 异常
        // - 用 messagesRefForTrack 拿最新 messages, 避免 setState 异步 + 闭包过期
        // - costSaving 取 SSE done 已塞进 message 的值 (后端 server.reportBillingAndCost)
        //
        // 关键: setTimeout(0) await 给 React 一次微/宏任务时间, 让 useEffect 同步 ref 跑完。
        // 否则 finally 是紧跟 setMessages 的同步上下文, ref 里是 stale 数据。
        await new Promise((resolve) => setTimeout(resolve, 0))
        const doneAt = Date.now()
        const finalMsg = messagesRefForTrack.current.find(
          (m) => m.id === assistantMessageId
        )
        const sentAt = finalMsg?.runStartedAt ?? runStartedAt
        const { list1, number1 } = aggregateToolCalls(finalMsg?.progressSteps)
        // 神策 result / errorMsg 都是平台 in 校验的枚举:
        //   result: 成功 / 失败 / 用户停止 / 超时 (按 caughtErrorCode 映射)
        //   errorMsg: 只在 失败/超时 时报枚举码 (billingInsufficient/timeout/
        //            networkError/serverError), 不再报中文原文
        let resultEnum: '成功' | '失败' | '用户停止' | '超时' = '成功'
        if (caughtErrorCode === 'userAbort') {
          resultEnum = '用户停止'
        } else if (caughtErrorCode === 'timeout') {
          resultEnum = '超时'
        } else if (caughtErrorCode) {
          resultEnum = '失败'
        }
        trackAiB8112Completed({
          sessionID: sessionId || '',
          msgID: finalMsg?.messageId || '',
          string1: finalMsg?.traceID || '',
          content: finalMsg?.userQuestion || serverInput,
          result: resultEnum,
          errorMsg:
            resultEnum === '失败' || resultEnum === '超时'
              ? caughtErrorCode
              : '',
          state: wallet?.remainingLimit ?? 0,
          costTime: doneAt - sentAt,
          costSaving: finalMsg?.costSaving ?? 0,
          list1,
          number1,
          list2: finalMsg?.suggestions ?? [],
          number2: finalMsg?.roundCount ?? 0,
          number3: (finalMsg?.cards || []).length,
          number4: (finalMsg?.suggestions || []).length,
          // sourceType 推断: caller 显式传 (试试问我 / 历史记录) → 用之;
          // 否则按 sendMessage 入口的 messagesRefForTrack 长度判断:
          //   - 进 sendMessage 时 messages 还是空 = 用户首次手动输入
          //   - 已有对话 (含 plan 同意 / 问答反馈 / 续问) = 继续追问
          sourceType:
            sourceTypeOverride ||
            (messagesAtSendStart === 0 ? '手动输入' : '继续追问')
        })
      }
    },
    [
      animateAssistantReply,
      replacePendingMessage,
      groupId,
      userId,
      inputValue,
      sending,
      sessionId,
      userName,
      scrollToBottom,
      onApplyPageFilter,
      attachments,
      fetchWallet
    ]
  )

  useEffect(() => {
    sendMessageRef.current = sendMessage
  }, [sendMessage])

  // hover 卡片快捷问题: open + pendingQuestion 同时满足时自动发送。
  // sourceType 必须显式传 hover卡片-预设问题, 否则空会话会被推断成"手动输入"
  // (completed.sourceType / c8146.string1 都吃这个值)
  useEffect(() => {
    if (!open || !pendingQuestion) return
    void sendMessageInternal(pendingQuestion, 'hover卡片-预设问题')
    if (onPendingQuestionSent) onPendingQuestionSent()
  }, [open, pendingQuestion, sendMessageInternal, onPendingQuestionSent])

  /**
   * 点击中断消息底部的"重试"按钮: 拿原 userQuestion 重新发起一次 sendMessage。
   * 不修改原来那条被中断的 message (保留它作为历史 + retry 入口), 新建一对 user/assistant
   * 消息追加在末尾, 等同于用户重新手敲了一遍同样的问题。
   */
  const handleRetryMessage = useCallback(
    (msg: ChatMessage) => {
      if (sending) return
      const q = (msg.userQuestion || '').trim()
      if (!q) return
      void sendMessageInternal(q)
    },
    [sending, sendMessageInternal]
  )

  /** 用户点击发送按钮(此时已变成停止图标)→ abort 当前 SSE 流。 */
  const handleStopRun = useCallback(() => {
    if (runAbortRef.current) {
      runAbortRef.current.abort()
    }
  }, [])

  // 浮窗左上角拖拽:沿 top-left 改宽高,bottom-right 锚点固定
  const startResize = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      const startW = panelSize.w
      const startH = panelSize.h
      const startX = e.clientX
      const startY = e.clientY
      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'nwse-resize'

      const handleMove = (ev: MouseEvent) => {
        const dx = startX - ev.clientX
        const dy = startY - ev.clientY
        const maxH = Math.min(PANEL_MAX_HEIGHT_CAP, window.innerHeight - 48)
        setPanelSize({
          w: Math.min(PANEL_MAX_WIDTH, Math.max(PANEL_MIN_WIDTH, startW + dx)),
          h: Math.min(maxH, Math.max(PANEL_MIN_HEIGHT, startH + dy))
        })
      }
      const handleUp = () => {
        document.body.style.userSelect = ''
        document.body.style.cursor = ''
        document.removeEventListener('mousemove', handleMove)
        document.removeEventListener('mouseup', handleUp)
      }
      document.addEventListener('mousemove', handleMove)
      document.addEventListener('mouseup', handleUp)
    },
    [panelSize.w, panelSize.h]
  )

  // 浮窗左边缘水平拖拽:只改宽度(右下锚点固定 → 左拖变宽 / 右拖变窄),不动高度。
  const startResizeLeft = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      const startW = panelSize.w
      const startX = e.clientX
      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'ew-resize'

      const handleMove = (ev: MouseEvent) => {
        const dx = startX - ev.clientX
        const newW = Math.min(
          PANEL_MAX_WIDTH,
          Math.max(PANEL_MIN_WIDTH, startW + dx)
        )
        setPanelSize((prev) => ({ ...prev, w: newW }))
      }
      const handleUp = () => {
        document.body.style.userSelect = ''
        document.body.style.cursor = ''
        document.removeEventListener('mousemove', handleMove)
        document.removeEventListener('mouseup', handleUp)
      }
      document.addEventListener('mousemove', handleMove)
      document.addEventListener('mouseup', handleUp)
    },
    [panelSize.w]
  )

  // 拖 header 移动浮窗。锚点仍是右下,这里维护一个相对偏移 (dx, dy)。
  // dx 往左为负、dy 往上为负。鼠标 move 时按鼠标 delta 累加。
  // 为避免拖出屏幕,clamp 到当前 w/h 可视范围内。
  const startDrag = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // 仅头部空白区响应;在按钮/图标上按下时由子元素 stopPropagation 拦掉
      if (e.button !== 0) return
      e.preventDefault()
      const startDx = panelOffset.dx
      const startDy = panelOffset.dy
      const startX = e.clientX
      const startY = e.clientY
      const w = panelSize.w
      const h = panelSize.h
      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'grabbing'

      const clamp = (nx: number, ny: number) => {
        // 右下锚点 (24,24) + offset(dx,dy);panel 左上角 = winW-24+dx-w, winH-24+dy-h
        const winW = window.innerWidth
        const winH = window.innerHeight
        const minDx = -(winW - w - 24) // 左极限:dx 最小到 panel 贴左边
        const maxDx = 24 // 右极限:dx 最大 24(panel 贴屏幕右边)
        const minDy = -(winH - h - 24) // 上极限
        const maxDy = 24 // 下极限
        return {
          dx: Math.max(minDx, Math.min(maxDx, nx)),
          dy: Math.max(minDy, Math.min(maxDy, ny))
        }
      }
      const handleMove = (ev: MouseEvent) => {
        const nx = startDx + (ev.clientX - startX)
        const ny = startDy + (ev.clientY - startY)
        setPanelOffset(clamp(nx, ny))
      }
      const handleUp = () => {
        document.body.style.userSelect = ''
        document.body.style.cursor = ''
        document.removeEventListener('mousemove', handleMove)
        document.removeEventListener('mouseup', handleUp)
      }
      document.addEventListener('mousemove', handleMove)
      document.addEventListener('mouseup', handleUp)
    },
    [panelOffset.dx, panelOffset.dy, panelSize.w, panelSize.h]
  )

  /** 点击最近查询 / 猜你想问按钮 = 直接发送该 query,和发消息同一路径。
   *  sourceType 透传给 ai_b8112_completed.sourceType (试试问我 / 历史记录) */
  const handleQuickAsk = useCallback(
    (q: string, sourceType?: AiB8112SourceType) => {
      if (!q || sending) return
      void sendMessageInternal(q, sourceType)
    },
    [sending, sendMessageInternal]
  )

  /**
   * 提交某条 assistant 消息的反馈到后端 /feedback。
   * 乐观更新本地 ChatMessage.feedback, 失败时回滚 + antd notification。
   * rating=0 且 comment="" 时后端会物理删, 这里前端把 feedback 字段也清空。
   */
  const submitMessageFeedback = useCallback(
    async (messageId: string, next: MessageFeedbackState): Promise<void> => {
      if (!userId || !groupId || !sessionId) return
      if (!messageId || messageId.startsWith('legacy-')) return
      let prevFeedback: MessageFeedbackState | undefined
      const optimistic: MessageFeedbackState | undefined =
        next.rating === 0 && !next.comment ? undefined : next
      setMessages((prev) =>
        prev.map((m) => {
          if (m.messageId !== messageId) return m
          prevFeedback = m.feedback
          return { ...m, feedback: optimistic }
        })
      )
      try {
        const resp = await fetch(`${AI_CLAW_BASE_URL}/feedback`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            group_id: groupId,
            session_id: sessionId,
            message_id: messageId,
            rating: next.rating,
            comment: next.comment || ''
          })
        })
        if (!resp.ok) throw new Error(`提交失败 ${resp.status}`)
      } catch (e) {
        // 回滚到提交前的状态, 并提示用户。
        // catch 变量在 strict 下是 unknown,需 narrow 后再取 message。
        setMessages((prev) =>
          prev.map((m) =>
            m.messageId === messageId ? { ...m, feedback: prevFeedback } : m
          )
        )
        const errMsg = e instanceof Error ? e.message : ''
        notification.error({
          message: '反馈提交失败',
          description: errMsg || '网络异常, 请稍后重试',
          duration: 3
        })
        throw e
      }
    },
    [userId, groupId, sessionId]
  )

  return (
    <aside
      className={
        'ai-photo-jun-panel ai-photo-jun-panel--floating' +
        (open ? '' : ' is-hidden')
      }
      style={{
        width: panelSize.w,
        height: panelSize.h,
        transform: `translate(${panelOffset.dx}px, ${panelOffset.dy}px)`
      }}
      aria-hidden={!open}
    >
      <div
        className="ai-photo-jun-panel__resize-handle"
        onMouseDown={startResize}
        aria-label="调整大小"
      />
      <div
        className="ai-photo-jun-panel__resize-handle-left"
        onMouseDown={startResizeLeft}
        aria-label="拖动调整宽度"
      />
      <div
        className="ai-photo-jun-panel__header"
        onMouseDown={startDrag}
        title=""
      >
        <div
          className="ai-photo-jun-panel__header-main"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* 标题前缀星星: 与左侧入口按钮(IconAiStar)同款同大小(28), 视觉呼应、避免单调(张璇 2026-06) */}
          <IconAiStar size={28} />
          <div className="ai-photo-jun-panel__title">AI 统计照片</div>
        </div>
        <div
          className="ai-photo-jun-panel__header-actions"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Tooltip
            title="历史会话"
            placement="bottom"
            mouseEnterDelay={0.1}
            classNames={{ root: 'ai-photo-jun-panel__header-tooltip' }}
          >
            <button
              type="button"
              className="ai-photo-jun-panel__header-btn"
              onClick={() => {
                const next = !sessionsDrawerOpen
                // 神策埋点 ai_b8112_click: 历史会话 (打开/关闭抽屉, 只在 open 边沿上报)
                if (next) {
                  trackAiB8112Click({
                    clickItem: '历史会话',
                    sessionID: sessionId || ''
                  })
                }
                setSessionsDrawerOpen(next)
                setPlanDrawerOpen(false)
                setDownloadsDrawerOpen(false)
                if (next) void fetchSessions()
              }}
              aria-label="历史会话"
            >
              <IconHistoryLine />
            </button>
          </Tooltip>
          <Tooltip
            title="开启新会话"
            placement="bottom"
            mouseEnterDelay={0.1}
            classNames={{ root: 'ai-photo-jun-panel__header-tooltip' }}
          >
            <button
              type="button"
              className="ai-photo-jun-panel__header-btn"
              onClick={() => {
                // 神策埋点 ai_b8112_click: 点击新会话
                trackAiB8112Click({
                  clickItem: '开启新会话',
                  sessionID: sessionId || ''
                })
                handleNewSession()
              }}
              disabled={compactCoolingDown || !userId || !groupId}
              aria-label="新会话"
            >
              <IconNewChatLine />
            </button>
          </Tooltip>
          {/* 2026-06-11 设计走查:「···」菜单下线(执行计划入口移除),下载任务按钮直出;
              默认黑色,有未读(还没点开看)任务时变蓝 + 新增数量角标,点开即恢复 */}
          <Tooltip
            title="下载任务"
            placement="bottom"
            mouseEnterDelay={0.1}
            classNames={{ root: 'ai-photo-jun-panel__header-tooltip' }}
          >
            <button
              type="button"
              className={
                'ai-photo-jun-panel__header-btn' +
                (unreadDownload ? ' is-download-active' : '')
              }
              onClick={() => {
                if (!downloadsDrawerOpen) {
                  // 神策埋点 ai_b8112_click: 下载与生成任务 (只在打开边沿上报)
                  trackAiB8112Click({
                    clickItem: '下载与生成任务',
                    sessionID: sessionId || ''
                  })
                  // 神策埋点 ai_b8112_c8132_exposure: 二级页曝光带任务数统计
                  trackAiC8132Exposure({
                    sessionID: sessionId || '',
                    total: downloads.length,
                    pending: downloads.filter((d) => d.status === 'pending')
                      .length,
                    done: downloads.filter((d) => d.status === 'done').length,
                    failed: downloads.filter((d) => d.status === 'error').length
                  })
                } else {
                  // 神策埋点 ai_b8112_c8132_click: 关闭二级页
                  trackAiC8132Click({
                    sessionID: sessionId || '',
                    clickItem: '关闭'
                  })
                }
                setDownloadsDrawerOpen((v) => !v)
                setPlanDrawerOpen(false)
                setSessionsDrawerOpen(false)
                setUnreadDownload(false)
              }}
              aria-label="下载任务"
            >
              <IconDownloadLine />
              {unreadDownload && (
                <span className="badge-count" aria-hidden>
                  {unreadDownloadCount > 99 ? '99+' : unreadDownloadCount}
                </span>
              )}
            </button>
          </Tooltip>
          <Tooltip
            title="关闭"
            placement="bottom"
            mouseEnterDelay={0.1}
            classNames={{ root: 'ai-photo-jun-panel__header-tooltip' }}
          >
            <button
              type="button"
              className="ai-photo-jun-panel__header-btn"
              onClick={() => {
                // 神策埋点 ai_b8112_click: 点击关闭面板
                trackAiB8112Click({
                  clickItem: '关闭',
                  sessionID: sessionId || ''
                })
                if (onMinimize) onMinimize()
              }}
              aria-label="关闭"
            >
              <IconCloseLine />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* 内测申请入口 banner: 所有用户展示；点右侧 × 本会话内关闭 */}
      {!betaBannerClosed && (
        <div
          className="ai-beta-banner"
          onClick={() => {
            // 神策埋点 ai_b8112_click: 搜索画面引导卡点击(立即申请进内测弹窗)
            trackAiB8112Click({
              clickItem: '点击卡片项',
              sessionID: sessionId || '',
              typeName: '搜索画面引导卡',
              place: '立即申请'
            })
            setBetaModalSource('ai_photo_banner')
            setBetaModalVisible(true)
          }}
          role="button"
        >
          <span className="ai-beta-banner__icon" aria-hidden>
            <IconSearchDoc size={17} />
          </span>
          <span className="ai-beta-banner__text">
            <span className="ai-beta-banner__title">搜索画面中的内容</span>
            <span className="ai-beta-banner__desc">
              戴安全帽、人脸、单据，一搜就出
            </span>
          </span>
          <button type="button" className="ai-beta-banner__btn">
            立即申请
          </button>
          <button
            type="button"
            className="ai-beta-banner__close"
            aria-label="关闭"
            onClick={(e) => {
              e.stopPropagation()
              setBetaBannerClosed(true)
            }}
          >
            ✕
          </button>
        </div>
      )}

      <div
        className="ai-photo-jun-panel__messages"
        ref={scrollRef}
        onScroll={handleMessagesScroll}
      >
        {loadingContext ? (
          <div className="ai-photo-jun-panel__loading">
            <Spin size="small" />
            <span>正在拉取最近上下文...</span>
          </div>
        ) : messages.length === 0 ? (
          <EmptyStateBlock
            recentQueries={recentQueries}
            expanded={recentExpanded}
            onToggleExpand={() => setRecentExpanded((v) => !v)}
            onPick={handleQuickAsk}
            disabled={sending}
            sessionID={sessionId}
            userName={userName}
            showOnboarding={showOnboarding}
            presets={presetQuestions}
            onboardingCards={onboardingCards}
          />
        ) : (
          messages.map((item) => {
            const hasActivity =
              item.role === 'assistant' &&
              ((item.progressSteps && item.progressSteps.length > 0) ||
                item.pending ||
                !!item.streamPreview)
            const activityOpen = !!item.activityOpen
            // 神策埋点 rankNum: 第几个 AI 回答 (1-based, 只数 assistant)
            const assistantIndex =
              item.role === 'assistant'
                ? messages
                    .slice(0, messages.indexOf(item) + 1)
                    .filter((m) => m.role === 'assistant').length
                : 0
            // 本条 assistant 消息要渲染成富预览卡片的文件(publish_artifact xlsx);
            // 按消息挂载(live SSE download_task 当场挂 / 历史 attachments.downloads 恢复),
            // 不再靠正文里的文件链接匹配 —— 正文没贴 url 也能渲染。
            const previewCards = item.previewFiles || []
            // 这些文件已成卡片,markdown 里对应链接不再转 chip(去重)
            const cardSkipUrls =
              previewCards.length > 0
                ? new Set(previewCards.map((f) => f.url))
                : undefined
            return (
              <div
                key={item.id}
                data-ai-msg-id={item.id}
                className={`ai-photo-jun-panel__message ai-photo-jun-panel__message--${item.role}`}
              >
                <div className="ai-photo-jun-panel__bubble">
                  {hasActivity && (
                    <ActivityBlock
                      message={item}
                      open={activityOpen}
                      onToggle={() => toggleActivity(item.id)}
                      onShowTrace={(m) => setTraceModalMessage(m)}
                    />
                  )}
                  {/* 结构化卡片优先展示在 markdown 之上 */}
                  {item.role === 'assistant' &&
                    item.cards &&
                    item.cards.length > 0 && (
                      <div className="ai-photo-jun-panel__cards">
                        {item.cards.map((c, i) =>
                          renderCard(c, i, onApplyPageFilter, onApplyNlQuery, {
                            sessionId,
                            msgId: item.messageId || '',
                            rankNum: assistantIndex
                          })
                        )}
                      </div>
                    )}
                  {item.role === 'assistant' && item.pageFilter && (
                    <PageFilterCard
                      payload={item.pageFilter}
                      onReapply={onApplyPageFilter}
                      trackCtx={{
                        sessionId: sessionId || '',
                        msgId: item.messageId || '',
                        rankNum: assistantIndex
                      }}
                    />
                  )}
                  {item.fallbackNotice && (
                    <div className="ai-photo-jun-panel__fallback-notice">
                      <IconLightbulb className="fallback-notice-icon" />{' '}
                      {item.fallbackNotice}
                    </div>
                  )}
                  {item.role === 'assistant' ? (
                    <>
                      <div
                        className="ai-photo-jun-panel__markdown"
                        // 事件委托:enhanceDownloadLinksInHtml 把文件 chip 渲染成
                        // <a data-dl-url data-dl-name>,这里拦截点击交给 triggerArtifactDownload
                        // 分流(PC 客户端走 IPC 桥,浏览器走原生 <a download>)。
                        onClick={(e) => {
                          const anchor = (e.target as HTMLElement).closest(
                            'a.ai-claw-download'
                          ) as HTMLAnchorElement | null
                          if (!anchor) return
                          e.preventDefault()
                          const url = anchor.dataset.dlUrl
                          const name = anchor.dataset.dlName
                          if (url && checkVipBeforeDownload())
                            void chargeThenDownload(url, name)
                        }}
                        dangerouslySetInnerHTML={{
                          __html: enhanceDownloadLinksInHtml(
                            enhanceImagesInHtml(
                              md.render(
                                (item.content || '')
                                  // 隐藏消息中暴露的 task_id（后端有时会把 task_id 写进回复文案）
                                  .replace(
                                    /\s*task_id:\s*[a-f0-9-]{20,}\s*/gi,
                                    ''
                                  )
                              )
                            ),
                            cardSkipUrls,
                            // 本条消息有富预览卡时, 正文里的下载链接全去掉(预览卡是唯一入口)
                            previewCards.length > 0
                          )
                        }}
                      />
                      {previewCards.map((f) => (
                        <UniversalFilePreviewCard
                          key={f.url}
                          file={f}
                          fitPreviewToWindow
                          downloads={downloads}
                          onBeforeDownload={checkVipBeforeDownload}
                          onDownload={(f) =>
                            void chargeThenDownload(f.url, f.name)
                          }
                          // 预览锁仍按"非会员"(downloadLocked 同时控预览, 见 UniversalFilePreviewCard);
                          // 下载的 VVIP 门走 onBeforeDownload=checkVipBeforeDownload, 不动预览引流
                          downloadLocked={!vipKind}
                          trackCtx={{
                            sessionId: sessionId || '',
                            msgId: item.messageId || '',
                            rankNum: assistantIndex
                          }}
                        />
                      ))}
                    </>
                  ) : (
                    <>
                      {item.messageAttachments &&
                        item.messageAttachments.length > 0 && (
                          <div className="ai-photo-jun-panel__user-attachments">
                            {item.messageAttachments.map((att) =>
                              att.isImage ? (
                                <a
                                  key={att.id}
                                  href={att.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ai-photo-jun-panel__user-attachment is-image"
                                >
                                  <img
                                    src={att.url}
                                    alt={att.name}
                                    loading="lazy"
                                  />
                                </a>
                              ) : (
                                <a
                                  key={att.id}
                                  href={att.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ai-photo-jun-panel__user-attachment is-file"
                                >
                                  <PaperClipOutlined /> {att.name}
                                </a>
                              )
                            )}
                          </div>
                        )}
                      {item.content && (
                        <div className="ai-photo-jun-panel__plain">
                          {item.content}
                        </div>
                      )}
                    </>
                  )}
                  {item.ask && (
                    <InteractiveAskCard
                      messageId={item.id}
                      ask={item.ask}
                      onAnswer={handleAskAnswer}
                      disabled={sending}
                    />
                  )}
                  {item.role === 'assistant' && item.plan && (
                    <PlanApprovalCard
                      messageId={item.id}
                      plan={item.plan}
                      onApprove={handlePlanApprove}
                      onSubmitEdit={handlePlanSubmitEdit}
                      disabled={sending}
                    />
                  )}
                  {item.role === 'assistant' &&
                    item.suggestions &&
                    item.suggestions.length > 0 &&
                    !item.pending && (
                      <SuggestionsCard
                        items={item.suggestions}
                        onPick={handleQuickAsk}
                        disabled={sending}
                        sessionID={sessionId}
                        msgID={item.messageId || ''}
                        rankNum={assistantIndex}
                      />
                    )}
                  {/* 中断/失败/超时态的重试入口。
                      pending=true 时不展示 (还在跑); userQuestion 缺失时也不展示
                      (典型 legacy 历史消息没存 userQuestion, 没法重发)。
                      点击 → handleRetryMessage 用 userQuestion 走 sendMessageInternal
                      重新发起一轮, 旧 message 保留在历史里。 */}
                  {item.role === 'assistant' &&
                    item.interrupted &&
                    !item.pending &&
                    !!item.userQuestion && (
                      <div className="ai-photo-jun-panel__interrupted-bar">
                        <span className="ai-photo-jun-panel__interrupted-hint">
                          <InfoCircleOutlined className="ai-photo-jun-panel__interrupted-icon" />
                          {item.interrupted.hint}
                        </span>
                        <button
                          type="button"
                          className="ai-photo-jun-panel__retry-btn"
                          disabled={sending}
                          onClick={() => {
                            // 神策 ai_b8112_click place=重试 (spec 把重试归在点击卡片项下,
                            // 但中断重试条不属于任何卡片类型, typeName 平台枚举无对应值 → 留空,
                            // 需平台补登记或调整归属)
                            trackAiB8112Click({
                              clickItem: '点击卡片项',
                              sessionID: sessionId || '',
                              msgID: item.messageId || '',
                              rankNum: assistantIndex,
                              place: '重试'
                            })
                            handleRetryMessage(item)
                          }}
                        >
                          <ReloadOutlined /> 重试
                        </button>
                      </div>
                    )}
                  {/* 反馈控件: 只在 assistant 回答完整 + 有 backend message_id (非 legacy-) 时展示。
                      pending 期间不让点, ask/plan 等暂停态的 assistant 也不展示
                      (没回答完, 反馈无意义)。 */}
                  {item.role === 'assistant' &&
                    !item.pending &&
                    !item.ask &&
                    !item.plan &&
                    item.messageId &&
                    !item.messageId.startsWith('legacy-') && (
                      <MessageFeedback
                        state={item.feedback}
                        onSubmit={(next) =>
                          submitMessageFeedback(item.messageId!, next)
                        }
                        sessionID={sessionId}
                        msgID={item.messageId || ''}
                        rankNum={assistantIndex}
                      />
                    )}
                </div>
              </div>
            )
          })
        )}
      </div>

      {(planDrawerOpen || downloadsDrawerOpen || sessionsDrawerOpen) && (
        <PanelDrawer
          title={
            planDrawerOpen
              ? '执行计划'
              : downloadsDrawerOpen
                ? '下载 / 生成任务'
                : '历史会话'
          }
          onClose={() => {
            setPlanDrawerOpen(false)
            setDownloadsDrawerOpen(false)
            setSessionsDrawerOpen(false)
          }}
        >
          {planDrawerOpen && (
            <PlanDrawerBody
              messages={messages}
              onScrollTo={(msgId) => {
                setPlanDrawerOpen(false)
                // 切到浮窗主列表,滚到对应 plan 消息
                window.requestAnimationFrame(() => {
                  const node = document.querySelector(
                    `[data-ai-msg-id="${msgId}"]`
                  ) as HTMLElement | null
                  if (node) node.scrollIntoView({ block: 'center' })
                })
              }}
            />
          )}
          {downloadsDrawerOpen && (
            <DownloadDrawerBody
              items={downloads}
              sessionID={sessionId || ''}
              onBeforeDownload={checkVipBeforeDownload}
              onDownload={(url, filename, taskId) =>
                void chargeThenDownload(url, filename, taskId)
              }
            />
          )}
          {sessionsDrawerOpen && (
            <SessionListDrawerBody
              loading={sessionsLoading}
              items={sessions}
              currentSessionId={sessionId}
              onPick={handleSwitchSession}
            />
          )}
        </PanelDrawer>
      )}

      <div className="ai-photo-jun-panel__footer">
        {/* 输入框上方快捷按钮行: 仅在 OnboardingCarousel 没渲染时出, 同一份 onboardingCards 裁决,
            同一份 query/label, 同一套埋点 (点击试问 + 试试问我 sourceType)。
            轮播渲染条件: messages.length===0 && showOnboarding && onboardingCards !== null && length>0,
            所以这里只要把"轮播渲染条件"取反 + 仍要求 onboardingCards 有可见卡。 */}
        {onboardingCards !== null &&
          onboardingCards.length > 0 &&
          !(messages.length === 0 && showOnboarding) && (
            <OnboardingShortcutRow
              items={onboardingCards}
              disabled={sending}
              onShortcutClick={(query, label, index1Based) => {
                // 2026-06-23 spec: clickItem=点击快捷按钮, string1=query, string2=cardLabel(label 已是 cardLabel)
                trackAiB8112Click({
                  clickItem: '点击快捷按钮',
                  sessionID: sessionId,
                  string1: query,
                  string2: label,
                  number1: index1Based
                })
                handleQuickAsk(query, '快捷按钮')
              }}
            />
          )}
        {attachments.length > 0 && (
          <div className="ai-photo-jun-panel__attachments">
            {attachments.map((a) => (
              <div key={a.id} className="ai-photo-jun-panel__attachment-wrap">
                <div
                  className={
                    'ai-photo-jun-panel__attachment' +
                    (a.uploading ? ' is-uploading' : '') +
                    (a.error ? ' is-error' : '')
                  }
                  title={a.error || `${a.name} · ${formatFileSize(a.size)}`}
                >
                  {a.error ? (
                    <ExclamationCircleFilled className="ai-photo-jun-panel__attachment-thumb as-icon as-error" />
                  ) : a.previewUrl ? (
                    <img
                      className="ai-photo-jun-panel__attachment-thumb"
                      src={a.previewUrl}
                      alt=""
                    />
                  ) : (
                    <PaperClipOutlined className="ai-photo-jun-panel__attachment-thumb as-icon" />
                  )}
                  <span className="ai-photo-jun-panel__attachment-name">
                    {a.name}
                  </span>
                  {a.uploading && <Spin size="small" />}
                  <span
                    className="ai-photo-jun-panel__attachment-remove"
                    onClick={() => {
                      // 神策埋点 ai_b8112_click: 移除附件
                      trackAiB8112Click({
                        clickItem: '移除附件',
                        sessionID: sessionId || ''
                      })
                      removeAttachment(a.id)
                    }}
                    role="button"
                    aria-label="移除附件"
                  >
                    ×
                  </span>
                </div>
                {a.error && a.file && (
                  <button
                    type="button"
                    className="ai-photo-jun-panel__attachment-retry"
                    onClick={() => retryUpload(a.id)}
                    aria-label="重新上传"
                  >
                    上传失败请重试
                    <ReloadOutlined />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="ai-photo-jun-panel__input-row">
          {/* Row 1:输入区,内容超过一行时自动向上撑高,最大 5 行(对应卡片 max-height 180);
              超过后内部滚动。最长 1000 字。 */}
          <TextArea
            ref={textAreaRef}
            value={inputValue}
            autoSize={{ minRows: 1, maxRows: 5 }}
            maxLength={1000}
            onChange={(e) => setInputValue(e.target.value)}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault()
                sendMessage()
              }
            }}
            placeholder={placeholderPool[placeholderIdx]}
          />
          <input
            type="file"
            ref={fileInputRef}
            multiple
            accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          {/* Row 2:左 upload + 右 send */}
          <div className="ai-photo-jun-panel__input-actions">
            <button
              type="button"
              className="ai-photo-jun-panel__upload-btn"
              onClick={() => {
                // 神策埋点 ai_b8112_click: 点击上传附件
                trackAiB8112Click({
                  clickItem: '点击上传附件',
                  sessionID: sessionId || ''
                })
                handlePickFile()
              }}
              disabled={sending}
              title="上传文件/照片(最大 500MB)"
              aria-label="上传文件"
            >
              <PaperClipOutlined />
            </button>
            <button
              type="button"
              className="ai-photo-jun-panel__send-btn"
              onClick={() => {
                if (sending) {
                  // 神策埋点 ai_b8112_click: 点击停止 (跑动态文本框右上的按钮)
                  trackAiB8112Click({
                    clickItem: '点击停止',
                    sessionID: sessionId || ''
                  })
                  handleStopRun()
                } else {
                  // 神策埋点 ai_b8112_click: 点击发送
                  // content/number4/folderTypeList 跟实际 send body 一致
                  const folderTypeList = attachments.map((a) =>
                    isImageFile(a.name)
                      ? 'image'
                      : a.name.split('.').pop() || ''
                  )
                  trackAiB8112Click({
                    clickItem: '点击发送',
                    sessionID: sessionId || '',
                    content: inputValue,
                    number4: attachments.length,
                    folderTypeList
                  })
                  sendMessage()
                }
              }}
              disabled={
                // 仅在"非生成中 + 输入空"或"还有附件在上传"时禁用。
                // 生成中(sending=true)按钮可点 → 触发停止。
                sending
                  ? false
                  : (!inputValue.trim() && attachments.length === 0) ||
                    attachments.some((a) => a.uploading)
              }
              title={sending ? '停止生成' : '发送'}
              aria-label={sending ? '停止生成' : '发送'}
            >
              {sending ? (
                <svg
                  viewBox="0 0 16 16"
                  width="12"
                  height="12"
                  fill="currentColor"
                  aria-hidden
                >
                  <rect x="3" y="3" width="10" height="10" rx="1.5" />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 16 16"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  {/* 向上箭头:竖线 + 两道斜线构成箭头 */}
                  <path d="M8 13 V3" />
                  <path d="M3.5 7.5 L8 3 L12.5 7.5" />
                </svg>
              )}
            </button>
          </div>
        </div>
        {/* 积分提示条 + hover 弹出"我的积分"卡片 */}
        {wallet &&
          (() => {
            const tipInfo = getCreditTipInfo(
              vipLevel,
              wallet.remainingLimit,
              wallet.totalLimit
            )
            return (
              <Popover
                trigger="hover"
                open={walletPopoverVisible}
                onOpenChange={(v) => {
                  if (v) {
                    trackAiB8112Click({
                      clickItem: 'hover积分',
                      sessionID: sessionId || ''
                    })
                    // 我的积分弹窗曝光(c8145), 只在 hover 打开这一路报, 不再叠 onExpose 双报
                    trackAiC8145Exposure({
                      sessionID: sessionId || '',
                      trigger: c8145Trigger(),
                      usageState: usageStateToC8145(currentUsageState())
                    })
                  } else {
                    trackAiC8145Click({
                      sessionID: sessionId || '',
                      clickItem: '关闭弹窗',
                      usageState: usageStateToC8145(currentUsageState())
                    })
                  }
                  setWalletPopoverVisible(v)
                }}
                placement="topLeft"
                classNames={{ root: 'ai-photo-jun-panel__wallet-popover' }}
                content={
                  <PointsStatusCard
                    vipKind={vipKind}
                    total={wallet.totalLimit}
                    used={wallet.consumed}
                    perQueryCost={CREDITS_PER_QUERY}
                    onCtaClick={(ctx) => {
                      trackAiC8145Click({
                        sessionID: sessionId || '',
                        clickItem:
                          ctx.action === 'addon' ? '增购积分包' : '升级会员',
                        usageState: usageStateToC8145(currentUsageState())
                      })
                      setWalletPopoverVisible(false)
                      if (ctx.action === 'addon') {
                        openPointsPack('我的积分弹窗')
                      } else {
                        // 升级:定位到比当前高一档的目标档位,而不是默认落在基础会员
                        payment.openVipByKind(
                          getUpgradeTargetVipKind(vipKind),
                          AI_PHOTO_KING_SOURCE_DETAIL,
                          false,
                          aiPhotoPaywallCustomContent({
                            vipKind,
                            usedCredits: wallet?.consumed ?? 0,
                            trackSource: '我的积分弹窗'
                          })
                        )
                      }
                    }}
                    onContactClick={() => {
                      trackAiC8145Click({
                        sessionID: sessionId || '',
                        clickItem: '联系客服',
                        usageState: usageStateToC8145(currentUsageState())
                      })
                      setWalletPopoverVisible(false)
                      customerAgentDrawer.open({})
                    }}
                  />
                }
              >
                <div className="ai-photo-jun-panel__credit-tip">
                  {tipInfo.segments.map((seg, i) => {
                    if (seg.clickAction) {
                      return (
                        <span
                          key={i}
                          className="credit-tip-seg is-cta"
                          onClick={(e) => {
                            e.stopPropagation()
                            trackAiB8112Click({
                              clickItem:
                                seg.clickAction === 'upgrade'
                                  ? '提示条-升级会员'
                                  : '提示条-增购积分',
                              sessionID: sessionId || '',
                              string5: usageStateToB8112(currentUsageState())
                            })
                            if (seg.clickAction === 'purchase') {
                              openPointsPack('会话页面底部积分提示条')
                            } else {
                              // 升级:定位到比当前高一档的目标档位,而不是默认落在基础会员
                              payment.openVipByKind(
                                getUpgradeTargetVipKind(vipKind),
                                AI_PHOTO_KING_SOURCE_DETAIL,
                                false,
                                aiPhotoPaywallCustomContent({
                                  vipKind,
                                  usedCredits: wallet?.consumed ?? 0,
                                  trackSource: '会话页面底部积分提示条'
                                })
                              )
                            }
                          }}
                        >
                          {seg.text}
                        </span>
                      )
                    }
                    return (
                      <span
                        key={i}
                        className={
                          'credit-tip-seg' +
                          (seg.style === 'bold-orange' ? ' is-highlight' : '')
                        }
                      >
                        {seg.text}
                      </span>
                    )
                  })}
                </div>
              </Popover>
            )
          })()}
      </div>
      {/* "调用链路"诊断 modal — 隐藏入口(连击 ActivityBlock summary 5 次唤起) */}
      {traceModalMessage && (
        <TraceDetailModal
          message={traceModalMessage}
          sessionID={sessionId}
          userID={userId}
          groupID={groupId}
          onClose={() => setTraceModalMessage(null)}
        />
      )}
      <PointsPackModal
        visible={packVisible}
        onClose={() => {
          // vip_b8148_click 关闭 (2026-06-11 spec)
          if (packSource) {
            trackVipB8148Click({
              clickItem: '关闭',
              defaultPack: packDefaultRef.current,
              sourceType: packSource
            })
          }
          setPackVisible(false)
        }}
        source={AI_PHOTO_KING_SOURCE_DETAIL}
        onExpose={(pack) => {
          packDefaultRef.current = pack?.name ?? ''
          if (packSource) {
            trackVipB8148Exposure({
              defaultPack: packDefaultRef.current,
              sourceType: packSource
            })
          }
        }}
        onSelectPack={(pack) => {
          if (packSource) {
            trackVipB8148Click({
              clickItem: '切换套餐',
              defaultPack: packDefaultRef.current,
              sourceType: packSource,
              pack: pack.name
            })
          }
        }}
        onChannelChange={(channel, pack) => {
          if (packSource) {
            trackVipB8148Click({
              clickItem: '切换支付方式',
              defaultPack: packDefaultRef.current,
              sourceType: packSource,
              pack: pack?.name ?? '',
              channel:
                channel === paymentConfig.channelType.WeChat ? '微信' : '支付宝'
            })
          }
        }}
        onAgreementClick={() => {
          if (packSource) {
            trackVipB8148Click({
              clickItem: '查看会员协议',
              defaultPack: packDefaultRef.current,
              sourceType: packSource
            })
          }
        }}
        onRefreshQRCode={() => {
          // 仅二维码失效后用户手动点刷新时报; 切支付方式不会误入这里
          if (packSource) {
            trackVipB8148Click({
              clickItem: '刷新二维码',
              defaultPack: packDefaultRef.current,
              sourceType: packSource
            })
          }
        }}
        onPaymentSuccess={async () => {
          await fetchWallet()
        }}
      />
      {insufficientModal &&
        ReactDOM.createPortal(
          <div className="ai-insufficient-modal-mask">
            <div
              className="ai-insufficient-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ai-insufficient-modal__icon">
                <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                  <circle cx="28" cy="28" r="28" fill="#FFF3E0" />
                  <path
                    d="M28 16l12 21H16L28 16z"
                    fill="#FF9800"
                    stroke="#FF9800"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  <text
                    x="28"
                    y="34"
                    textAnchor="middle"
                    fill="#fff"
                    fontSize="16"
                    fontWeight="bold"
                  >
                    !
                  </text>
                </svg>
              </div>
              <div className="ai-insufficient-modal__title">
                积分不足，本次查询无法完成
              </div>
              <div className="ai-insufficient-modal__desc">
                当前积分不足，{vipKind ? '升级会员' : '开通会员'}
                获取更多积分，团队共享
              </div>
              <div className="ai-insufficient-modal__tiers">
                {INSUFFICIENT_TIERS.map((tier) => (
                  <div className="ai-insufficient-modal__tier" key={tier.key}>
                    <div className="ai-insufficient-modal__tier-main">
                      <span className="ai-insufficient-modal__tier-name">
                        {tier.name}
                        {vipKind === tier.vipKind && (
                          <span className="ai-insufficient-modal__tier-current">
                            当前
                          </span>
                        )}
                      </span>
                      <span className="ai-insufficient-modal__tier-desc">
                        {tier.desc}
                      </span>
                    </div>
                    <div className="ai-insufficient-modal__tier-aside">
                      <span className="ai-insufficient-modal__tier-credit">
                        {tier.credit}
                      </span>
                      <span className="ai-insufficient-modal__tier-token">
                        {tier.token} 团队共享
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="ai-insufficient-modal__info">
                <div className="ai-insufficient-modal__info-row">
                  <span>当前余额</span>
                  <span>
                    <strong className="is-danger">
                      {insufficientModal.available.toLocaleString()}
                    </strong>{' '}
                    积分
                  </span>
                </div>
              </div>
              <div className="ai-insufficient-modal__actions">
                {/* SVIP / EVIP 已是顶级,不再展示"开通会员",只给"增购积分"主按钮;
                    其他情况(非会员 / VIP / VVIP)保留双按钮 */}
                {vipKind === CONFIG.VIP_KIND.SVIP ||
                vipKind === CONFIG.VIP_KIND.EVIP ? (
                  <button
                    type="button"
                    className="ai-insufficient-modal__btn is-primary is-full"
                    onClick={() => {
                      // 神策埋点 ai_b8112_c8146_click clickItem=增购积分
                      trackAiC8146Click({
                        sessionID: sessionId || '',
                        clickItem: '增购积分',
                        trigger: insufficientModal.trigger,
                        available: insufficientModal.available,
                        cost: insufficientModal.cost
                      })
                      setInsufficientModal(null)
                      openPointsPack('积分不足弹窗')
                    }}
                  >
                    增购积分
                  </button>
                ) : (
                  <>
                    {vipKind ? (
                      <button
                        type="button"
                        className="ai-insufficient-modal__btn is-secondary"
                        onClick={() => {
                          // 神策埋点 ai_b8112_c8146_click clickItem=增购积分
                          trackAiC8146Click({
                            sessionID: sessionId || '',
                            clickItem: '增购积分',
                            trigger: insufficientModal.trigger,
                            available: insufficientModal.available,
                            cost: insufficientModal.cost
                          })
                          setInsufficientModal(null)
                          openPointsPack('积分不足弹窗')
                        }}
                      >
                        增购积分
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="ai-insufficient-modal__btn is-secondary"
                        onClick={() => {
                          // 神策埋点 ai_b8112_c8146_click clickItem=我知道了
                          trackAiC8146Click({
                            sessionID: sessionId || '',
                            clickItem: '我知道了',
                            trigger: insufficientModal.trigger,
                            available: insufficientModal.available,
                            cost: insufficientModal.cost
                          })
                          setInsufficientModal(null)
                        }}
                      >
                        我知道了
                      </button>
                    )}
                    <button
                      type="button"
                      className="ai-insufficient-modal__btn is-primary"
                      onClick={() => {
                        // 神策埋点 ai_b8112_c8146_click clickItem=开通会员
                        trackAiC8146Click({
                          sessionID: sessionId || '',
                          clickItem: '开通会员',
                          trigger: insufficientModal.trigger,
                          available: insufficientModal.available,
                          cost: insufficientModal.cost
                        })
                        setInsufficientModal(null)
                        payment.openVipByKind(
                          getUpgradeTargetVipKind(vipKind),
                          AI_PHOTO_KING_SOURCE_DETAIL,
                          false,
                          aiPhotoPaywallCustomContent({
                            vipKind,
                            usedCredits: wallet?.consumed ?? 0
                          })
                        )
                      }}
                    >
                      开通会员
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
      <AiPhotoBetaModal
        visible={betaModalVisible}
        source={betaModalSource}
        sessionID={sessionId || ''}
        total={betaQuota.total}
        remaining={betaQuota.remaining}
        onClose={() => setBetaModalVisible(false)}
        onContactService={() => {
          setBetaModalVisible(false)
          customerAgentDrawer.open({})
        }}
      />
    </aside>
  )
}

/**
 * EmptyStateBlock —— 新会话/无消息时的空态。
 * 顶部:欢迎语(sparkle + 问候 + 团队照片数量说明)
 * 中段:最近查询(有数据时才展示)
 * 底部:试试问我(静态推荐问题)
 */
const EmptyStateBlock: React.FC<{
  recentQueries: RecentQuery[]
  expanded: boolean
  onToggleExpand: () => void
  /** 第二个参数 sourceType 给神策埋点 ai_b8112_completed.sourceType,
   *  区分点击来源: '试试问我' (静态推荐) / '历史记录' (recentQueries) */
  onPick: (q: string, sourceType?: AiB8112SourceType) => void
  disabled: boolean
  /** 神策埋点 ai_b8112_click 需要 sessionID, 透传给本组件里的 click handler */
  sessionID: string
  userName?: string
  /** 仅首次打开面板时展示轮播卡片 */
  showOnboarding?: boolean
  /** 「试试问我」展示用的问题（来自 /preset_questions）；空数组则回退到 DEFAULT_PRESETS */
  presets: string[]
  /**
   * OnboardingCarousel 展示的完整卡列表 (含后端 query + 本地素材, 来自 /preset_questions.onboarding_cards)。
   * null = 还没拉到 → 不渲染轮播 (避免 3 张全闪一下再被 gate 掉)。
   */
  onboardingCards: OnboardingCardItem[] | null
}> = ({
  recentQueries,
  expanded,
  onToggleExpand,
  onPick,
  disabled,
  sessionID,
  userName,
  showOnboarding,
  presets,
  onboardingCards
}) => {
  const hasRecent = recentQueries.length > 0
  const visible = expanded ? recentQueries : recentQueries.slice(0, 3)
  const canExpand = recentQueries.length > 3
  // 后端返回了团队定制问题就用，否则回退到本地静态兜底
  const shownPresets = presets.length > 0 ? presets : DEFAULT_PRESETS
  return (
    <div className="ai-photo-jun-panel__empty-state">
      {/* 欢迎语区:sparkle + 标题同行 + 简介小字 */}
      <div className="ai-photo-jun-panel__welcome">
        <div className="welcome-heading">
          <span className="welcome-icon" aria-hidden>
            👋
          </span>
          <span className="welcome-title">
            {userName ? `${userName.slice(-2)}，你好` : '你好'}
          </span>
        </div>
        <div className="welcome-sub">
          你可以问我照片数据，让我找照片或者做总结
        </div>
      </div>

      {showOnboarding && onboardingCards !== null && (
        <OnboardingCarousel
          cards={onboardingCards}
          disabled={disabled}
          onCardClick={(query, label, index1Based) => {
            // 2026-06-23 spec: clickItem=点击轮播卡片, string1=query, string2=cardLabel(label 即 cardLabel)
            trackAiB8112Click({
              clickItem: '点击轮播卡片',
              sessionID,
              string1: query,
              string2: label,
              number1: index1Based
            })
            onPick(query, '轮播卡片')
          }}
        />
      )}

      {hasRecent && (
        <div className="ai-photo-jun-panel__recent">
          <div className="ai-photo-jun-panel__recent-header">
            <span className="recent-title">最近查询</span>
            {canExpand && (
              <button
                type="button"
                className="recent-expand"
                onClick={onToggleExpand}
              >
                {expanded ? '收起 ˅' : '全部 ›'}
              </button>
            )}
          </div>
          <div
            className={
              'ai-photo-jun-panel__recent-list' +
              (expanded ? ' is-scrollable' : '')
            }
          >
            {visible.map((r, i) => (
              <button
                key={`${i}-${r.query}`}
                type="button"
                className="ai-photo-jun-panel__recent-item"
                onClick={() => {
                  // 神策埋点 ai_b8112_click: 点击历史记录 (string2=内容, number2=第几条 1-based)
                  trackAiB8112Click({
                    clickItem: '点击历史记录',
                    sessionID,
                    string2: r.query,
                    number2: i + 1
                  })
                  onPick(r.query, '历史记录')
                }}
                disabled={disabled}
                title={r.query}
              >
                <span className="recent-q">{r.query}</span>
                <span className="recent-t" aria-hidden>
                  {relTime(r.created_at)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="ai-photo-jun-panel__how-to-ask">
        <div className="ask-title">试试问我</div>
        <div className="ask-presets">
          {shownPresets.map((p, idx) => (
            <button
              key={p}
              type="button"
              className="ai-photo-jun-panel__preset"
              onClick={() => {
                // 神策埋点 ai_b8112_click: 点击试问 (string1=内容, number1=第几个 1-based)
                trackAiB8112Click({
                  clickItem: '点击试问',
                  sessionID,
                  string1: p,
                  number1: idx + 1
                })
                onPick(p, '试试问我')
              }}
              disabled={disabled}
              title={p}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

/** 新会话空态下的默认示例问题。点击 = 直接发送。 */
const DEFAULT_PRESETS: string[] = [
  '统计本月每个人的拍照天数',
  '本月所有人共拍了多少张',
  '本月有几个人拍照，分别拍了多少张',
  '本月每个地点拍了多少张',
  '按日期汇总上月每天施工内容',
  '导出本月照片台账'
]

/**
 * PageFilterCard —— apply_page_filter 事件到达后,气泡内"已同步到主页"回显小卡。
 * 显示 nl_query + total + 缩略图 3-5 张。缩略图点击打开原图新标签,顶部 chip 点击 = 再次触发
 * onApplyPageFilter(同一 payload),确保主页 filter 没被其他操作覆盖时还能一键回到这个结果。
 */
const PageFilterCard: React.FC<{
  payload: PageFilterPayload
  onReapply?: (payload: PageFilterPayload) => void
  /** 神策 ai_b8112_click(点击卡片项) 上下文; 不传则不上报 */
  trackCtx?: CardTrackContext
}> = ({ payload, onReapply, trackCtx }) => {
  const thumbs = (payload.preview_thumb_urls || []).slice(0, 5)
  // 「照片地图」浮窗开关(入口在缩略图条末尾,地图复用当前主页筛选)
  const [mapVisible, setMapVisible] = useState(false)
  // 照片地图 = VVIP 专属 + 免费体验一次,统一走 useMapEntryGate
  const { openMapGuarded, handleMapClose } = useMapEntryGate()
  // 顶部 chip / 缩略图 → ai_b8112_click 点击卡片项 (typeName 平台枚举
  // PageFilterCard顶部chip / PageFilterCard下方缩略图)
  const trackPageFilterClick = (
    typeName: 'PageFilterCard顶部chip' | 'PageFilterCard下方缩略图',
    place: '进入查看' | '预览' | '打开地图'
  ) => {
    if (!trackCtx) return
    trackAiB8112Click({
      clickItem: '点击卡片项',
      sessionID: trackCtx.sessionId,
      msgID: trackCtx.msgId,
      rankNum: trackCtx.rankNum,
      typeName,
      place
    })
  }
  return (
    <div className="ai-photo-jun-panel__page-filter">
      <button
        type="button"
        className="ai-photo-jun-panel__page-filter-header"
        onClick={() => {
          trackPageFilterClick('PageFilterCard顶部chip', '进入查看')
          if (onReapply) onReapply(payload)
        }}
        title="点击重新把主页筛选同步到这个范围"
      >
        <span className="pf-icon" aria-hidden>
          <IconSearch />
        </span>
        <span className="pf-text">
          <span className="pf-query">{payload.nl_query}</span>
          {typeof payload.total === 'number' && payload.total > 0 && (
            <span className="pf-count">· {payload.total} 张</span>
          )}
        </span>
        <span className="pf-action">
          可在左侧查看
          <span className="pf-action-arrow" aria-hidden>
            ›
          </span>
        </span>
      </button>
      {(thumbs.length > 0 ||
        (typeof payload.total === 'number' && payload.total > 0)) && (
        <div className="ai-photo-jun-panel__page-filter-thumbs">
          {thumbs.map((u, i) => (
            <a
              key={`${i}-${u}`}
              href={u}
              target="_blank"
              rel="noopener noreferrer"
              className="pf-thumb"
              title="点开看原图"
              onClick={() =>
                trackPageFilterClick('PageFilterCard下方缩略图', '预览')
              }
            >
              <img src={u} loading="lazy" alt="" />
            </a>
          ))}
          {/* 「照片地图」入口:跟缩略图同一条;VVIP 以下首次免费体验、用过引导开通(useMapEntryGate) */}
          <button
            type="button"
            className="pf-thumb pf-map-entry"
            title="在地图上查看这些照片"
            onClick={() => {
              // ai_b8112_click: 点击卡片项(缩略图卡)打开地图(范 2026-06-23 spec place=打开地图)
              trackPageFilterClick('PageFilterCard下方缩略图', '打开地图')
              openMapGuarded(() => setMapVisible(true))
            }}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
              <path
                fill="currentColor"
                d="M12 2a7 7 0 0 0-7 7c0 4.9 7 13 7 13s7-8.1 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z"
              />
            </svg>
            <span>照片地图</span>
          </button>
        </div>
      )}
      <PhotoMapDrawer
        visible={mapVisible}
        onClose={() => handleMapClose(() => setMapVisible(false))}
        mapFilter={structuredToMapFilter(payload)}
      />
    </div>
  )
}

/**
 * MessageFeedback —— 一条 assistant 消息底部的反馈控件: 👍 / 👎 + 💬 评论。
 *
 * 行为约束:
 *  - 同一 message 同一用户只有一份反馈, 后端按 (user_id, message_id) upsert。
 *  - 点击已选的图标 = 撤销 (rating 切回 0)。
 *  - 评论是可选的, 可单独存在 (rating=0 + comment 非空), 也可附在 👍/👎 上。
 *  - rating=0 且 comment="" 时, 后端会物理删该条反馈 (撤销动作)。
 *  - 父组件保证只在 messageId 存在且非 'legacy-' 开头时才渲染本组件。
 */
const MessageFeedback: React.FC<{
  state?: MessageFeedbackState
  disabled?: boolean
  onSubmit: (next: MessageFeedbackState) => Promise<void>
  /** 神策埋点 ai_b8112_click 用 */
  sessionID: string
  msgID: string
  rankNum: number
}> = ({ state, disabled, onSubmit, sessionID, msgID, rankNum }) => {
  const rating: FeedbackRating = state?.rating ?? 0
  const existingComment = state?.comment || ''
  const [composerOpen, setComposerOpen] = useState(false)
  const [draft, setDraft] = useState(existingComment)
  const [busy, setBusy] = useState(false)
  const composerRef = useRef<HTMLDivElement | null>(null)
  // 父级反馈状态变化(刷新拉回 / 别处覆盖)时, 同步 draft 到最新 comment
  useEffect(() => {
    setDraft(existingComment)
  }, [existingComment])
  // composer 出现时自动滚动消息流让输入框完全可见(避免被面板底部输入条挡住)
  // scrollIntoView 默认只保证元素在视口内,但 __messages 视口下面紧贴 __footer 输入条,
  // 反馈输入框会被遮住底部。改为直接把所在的滚动容器 scrollTop 拉到底,稳。
  useEffect(() => {
    if (!composerOpen) return
    const node = composerRef.current
    if (!node) return
    // 找最近的可滚动祖先(__messages 容器)
    let scroller: HTMLElement | null = node.parentElement
    while (scroller) {
      const style = getComputedStyle(scroller)
      if (
        /(auto|scroll)/.test(style.overflowY) &&
        scroller.scrollHeight > scroller.clientHeight
      ) {
        break
      }
      scroller = scroller.parentElement
    }
    const raf = requestAnimationFrame(() => {
      if (scroller) {
        // 直接滚到底,让 composer 紧贴 __messages 容器底部
        scroller.scrollTo({
          top: scroller.scrollHeight,
          behavior: 'smooth'
        })
      } else {
        node.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }
    })
    return () => cancelAnimationFrame(raf)
  }, [composerOpen])

  const updateRating = async (target: FeedbackRating) => {
    if (disabled || busy) return
    // 点已选 = 取消, 落 0; 否则切到 target。
    const nextRating: FeedbackRating = rating === target ? 0 : target
    // 神策埋点 ai_b8112_click: 点赞/点踩/取消点赞/取消点踩
    const clickItem: '点赞' | '点踩' | '取消点赞' | '取消点踩' =
      nextRating === 1
        ? '点赞'
        : nextRating === 2
          ? '点踩'
          : rating === 1
            ? '取消点赞'
            : '取消点踩'
    trackAiB8112Click({ clickItem, sessionID, msgID, rankNum })
    setBusy(true)
    try {
      await onSubmit({ rating: nextRating, comment: existingComment })
      // 点踩后自动展开反馈输入框,引导用户填写具体问题(已有 comment 不重复展开)
      if (nextRating === 2 && !existingComment) {
        setComposerOpen(true)
      } else if (nextRating !== 2) {
        // 取消点踩 / 点赞 / 取消点赞 → 关闭反馈输入框(若它正展开)
        setComposerOpen(false)
      }
    } finally {
      setBusy(false)
    }
  }

  const submitComment = async () => {
    if (disabled || busy) return
    const trimmed = draft.trim()
    if (trimmed === existingComment) {
      setComposerOpen(false)
      return
    }
    // 神策埋点 ai_b8112_click: 提交反馈 (string4=评论文本)
    trackAiB8112Click({
      clickItem: '提交反馈',
      sessionID,
      msgID,
      rankNum,
      string4: trimmed
    })
    setBusy(true)
    try {
      await onSubmit({ rating, comment: trimmed })
      setComposerOpen(false)
    } finally {
      setBusy(false)
    }
  }

  const cancelComment = () => {
    setDraft(existingComment)
    setComposerOpen(false)
  }

  return (
    <div className="ai-photo-jun-panel__feedback">
      <div className="ai-photo-jun-panel__feedback-bar">
        <Tooltip
          title={rating === 1 ? '取消点赞' : '点赞'}
          placement="bottom"
          mouseEnterDelay={0.1}
          mouseLeaveDelay={0}
          classNames={{ root: 'ai-photo-jun-panel__header-tooltip' }}
        >
          {/* 用 span 包一层:disabled button 不响应 mouse 事件,会让 antd
              Tooltip 收不到 mouseLeave 一直挂着;包一层让事件落在 span 上。 */}
          <span className="ai-photo-jun-panel__feedback-btn-wrap">
            <button
              type="button"
              className={
                'ai-photo-jun-panel__feedback-btn' +
                (rating === 1 ? ' is-active is-up' : '')
              }
              onClick={() => updateRating(1)}
              disabled={disabled || busy}
              aria-label={rating === 1 ? '取消点赞' : '点赞'}
            >
              <IconThumbUp filled={rating === 1} />
            </button>
          </span>
        </Tooltip>
        <Tooltip
          title={rating === 2 ? '取消点踩' : '点踩'}
          placement="bottom"
          mouseEnterDelay={0.1}
          mouseLeaveDelay={0}
          classNames={{ root: 'ai-photo-jun-panel__header-tooltip' }}
        >
          <span className="ai-photo-jun-panel__feedback-btn-wrap">
            <button
              type="button"
              className={
                'ai-photo-jun-panel__feedback-btn' +
                (rating === 2 ? ' is-active is-down' : '')
              }
              onClick={() => updateRating(2)}
              disabled={disabled || busy}
              aria-label={rating === 2 ? '取消点踩' : '点踩'}
            >
              <IconThumbDown filled={rating === 2} />
            </button>
          </span>
        </Tooltip>
        <Tooltip
          title="反馈问题"
          placement="bottom"
          mouseEnterDelay={0.1}
          mouseLeaveDelay={0}
          classNames={{ root: 'ai-photo-jun-panel__header-tooltip' }}
        >
          <span className="ai-photo-jun-panel__feedback-btn-wrap">
            <button
              type="button"
              className={
                'ai-photo-jun-panel__feedback-btn' +
                (existingComment ? ' has-comment' : '')
              }
              onClick={() => {
                // 神策埋点 ai_b8112_click: 反馈问题 (打开/关闭评论框)
                if (!composerOpen) {
                  trackAiB8112Click({
                    clickItem: '反馈问题',
                    sessionID,
                    msgID,
                    rankNum
                  })
                }
                setComposerOpen((v) => !v)
              }}
              disabled={disabled || busy}
              aria-label="反馈问题"
            >
              <IconChat />
            </button>
          </span>
        </Tooltip>
        {existingComment && !composerOpen && (
          <span
            className="ai-photo-jun-panel__feedback-comment"
            title={existingComment}
          >
            {existingComment}
          </span>
        )}
      </div>
      {composerOpen && (
        <div
          className="ai-photo-jun-panel__feedback-composer"
          ref={composerRef}
        >
          <TextArea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="哪里不对？告诉我，下次帮你找得更准"
            autoSize={{ minRows: 2, maxRows: 4 }}
            maxLength={1000}
            disabled={disabled || busy}
          />
          <div className="ai-photo-jun-panel__feedback-actions">
            <button
              type="button"
              className="ai-photo-jun-panel__feedback-action is-secondary"
              onClick={cancelComment}
              disabled={busy}
            >
              取消
            </button>
            <button
              type="button"
              className="ai-photo-jun-panel__feedback-action is-primary"
              onClick={submitComment}
              disabled={disabled || busy}
            >
              提交
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * SuggestionsCard —— "猜你想问"按钮组。
 * 气泡底部,assistant 回答落定后从 SSE suggest_questions 事件填入。
 * 每轮对话都可能有,每条点击 = 直接作为下一条 user message 发送。
 */
const SuggestionsCard: React.FC<{
  items: string[]
  onPick: (q: string) => void
  disabled: boolean
  /** 神策埋点 ai_b8112_click 用 */
  sessionID: string
  msgID: string
  /** 当前 assistant 消息的 rankNum (从 1 起计数, messages 里第几条 assistant) */
  rankNum: number
}> = ({ items, onPick, disabled, sessionID, msgID, rankNum }) => (
  <div className="ai-photo-jun-panel__suggestions">
    <div className="ai-photo-jun-panel__suggestions-title">你可以继续问</div>
    <div className="ai-photo-jun-panel__suggestions-list">
      {items.map((q, i) => (
        <button
          key={`${i}-${q}`}
          type="button"
          // 第一条 = 推荐度最高,加 --top 修饰类,背景高亮成品牌蓝(见 .scss)
          className={`ai-photo-jun-panel__suggestion${
            i === 0 ? ' ai-photo-jun-panel__suggestion--top' : ''
          }`}
          onClick={() => {
            // 神策埋点 ai_b8112_click: 点击继续追问 (string3=内容, number3=第几个 1-based)
            trackAiB8112Click({
              clickItem: '点击继续追问',
              sessionID,
              msgID,
              string3: q,
              number3: i + 1,
              rankNum
            })
            onPick(q)
          }}
          disabled={disabled}
          title={q}
        >
          {q}
        </button>
      ))}
    </div>
  </div>
)

/** 相对时间展示:刚刚 / N 分钟前 / N 小时前 / 昨天 / N 天前 / YYYY-MM-DD */
function relTime(iso: string): string {
  if (!iso) return ''
  const t = new Date(iso).getTime()
  if (!isFinite(t)) return ''
  const diff = Math.max(0, Date.now() - t)
  const m = Math.floor(diff / 60000)
  if (m < 1) return '刚刚'
  if (m < 60) return `${m} 分钟前`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h} 小时前`
  const day = Math.floor(h / 24)
  if (day === 1) return '昨天'
  if (day < 7) return `${day} 天前`
  return new Date(iso).toLocaleDateString('zh-CN')
}

/**
 * ActivityBlock —— 折叠态:一行摘要 chip;展开态:步骤列表 + 流式思考预览。
 * 默认折叠,点击 chip 切展开。运行结束后 animateAssistantReply 会强制 activityOpen=false。
 */
const ActivityBlock: React.FC<{
  message: ChatMessage
  open: boolean
  onToggle: () => void
  /**
   * 隐藏入口 — 连击 summary 5 次(3s 内)调用此回调,弹出"调用链路"诊断 modal。
   * 普通用户不会触发,开发/PM/运营知道这个手势的人才能看到。
   */
  onShowTrace?: (message: ChatMessage) => void
}> = ({ message, open, onToggle, onShowTrace }) => {
  const summary = activitySummary(message)
  const steps = message.progressSteps || []
  // 每秒 tick 让"运行中"步骤的耗时实时显示。仅在展开 + 有 running step 时启用,
  // 已完成步骤不依赖 tick(它们的 ended_at 固定)。
  const [, setTick] = useState(0)
  const hasRunning = steps.some((s) => s.status === 'running')
  useEffect(() => {
    if (!open || !hasRunning) return
    const id = setInterval(() => setTick((t) => t + 1), 1000)
    return () => clearInterval(id)
  }, [open, hasRunning])
  // 连击计数:3s 内连续 5 次点击 summary 触发 onShowTrace
  const clickCountRef = useRef(0)
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleSummaryClick = () => {
    clickCountRef.current += 1
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current)
    clickTimerRef.current = setTimeout(() => {
      clickCountRef.current = 0
    }, 3000)
    if (clickCountRef.current >= 5) {
      clickCountRef.current = 0
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current)
        clickTimerRef.current = null
      }
      if (onShowTrace) onShowTrace(message)
      return
    }
    // 前 4 次点击仍走原 toggle 折叠/展开
    onToggle()
  }
  // Motion 风:summary chip 单行 + 可点 chevron 展开;body 默认收起
  // 5 次连击 summary 仍可唤起隐藏的"调用链路"诊断 modal,给开发/运营兜底
  return (
    <div
      className={
        'ai-photo-jun-panel__activity' +
        (open ? ' is-open' : ' is-collapsed') +
        (message.pending ? ' is-running' : ' is-done')
      }
      aria-label="执行过程"
    >
      <button
        type="button"
        className="ai-photo-jun-panel__activity-summary"
        onClick={handleSummaryClick}
        title={open ? '收起过程' : '看 AI 是怎么帮你做的'}
      >
        <span className="summary-icon" aria-hidden>
          {message.pending ? (
            <IconRunningDot size={10} />
          ) : (
            <span className="summary-icon-check">✓</span>
          )}
        </span>
        <span className="summary-text">{summary || 'AI 帮你做的事'}</span>
        <span className="summary-caret">›</span>
      </button>
      {open && (
        <div className="ai-photo-jun-panel__activity-body">
          {message.streamPreview && (
            <div className="ai-photo-jun-panel__stream-delta">
              <IconThinking className="stream-delta-icon" />{' '}
              {message.streamPreview}
            </div>
          )}
          {steps.length === 0 && !message.streamPreview && (
            <div className="ai-photo-jun-panel__activity-empty">正在思考…</div>
          )}
          {steps.map((s, i) => {
            // 步骤文案:**统一用 running 形态**(去掉末尾省略号),让用户能看到"在做什么"
            // 而不是统一的"已完成/找好了"笼统词(刘乾隆 2026-04-28 反馈"思考过程啥都看不出来")。
            // 视觉上靠左侧 step-icon 区分 running/ok/fail 状态,文案专心传业务信息。
            //   running: "正在搜「北京」的照片…"
            //   done   : "正在搜「北京」的照片"(末尾省略号去掉,前面加 ✓ 视觉)
            const baseText = s.label.running || s.label.done
            const text =
              s.status === 'running'
                ? baseText
                : baseText.replace(/[…….\s]+$/, '')
            const isLast = i === steps.length - 1
            // 累计 offset = 从**用户点发送的那一刻**到本步终点的总时长(刘乾隆 2026-04-28 要求"计时从发送开始")。
            // baseline 优先用 message.runStartedAt(发送瞬间记下),没有再 fallback 到 steps[0]?.started_at。
            // 这样能算上 SSE 建立 + 第一个 round 之前的等待时间,跟用户视角对齐。
            // 单步耗时(几 ms ~ 几百 ms)用户看了无感,弃用,改累计 offset。
            // running 用 now 作为右端(每秒 tick 触发刷新);完成态用 ended_at;缺则兜底显示空白。
            const baselineTs = message.runStartedAt ?? steps[0]?.started_at
            let durationText = ''
            if (baselineTs) {
              const endTs =
                s.status === 'running' ? Date.now() : (s.ended_at ?? Date.now())
              const ms = endTs - baselineTs
              if (ms >= 0) durationText = formatStepDuration(ms)
            }
            return (
              <div
                key={s.id}
                className={
                  `ai-photo-jun-panel__activity-line status-${s.status}` +
                  (isLast ? ' is-last' : '')
                }
              >
                <span className="step-icon" aria-hidden />
                <span className="step-text">{text}</span>
                {s.detail && <span className="step-detail">{s.detail}</span>}
                {durationText && (
                  <span className="step-duration">{durationText}</span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * TraceDetailModal —— "调用链路"隐藏诊断入口。
 *
 * 触发方式:在 ActivityBlock 头部"搞定 · 共 N 步 · 用时 X.Xs"chip 上 3s 内连击 5 次。
 * 普通用户不知道这手势,所以默认不打扰;开发/PM/运营知道方法的人能秒开看完整链路。
 *
 * 数据来源:全部用现成的 SSE 事件捕获到的 `ChatMessage.progressSteps`(已扩展到含
 * 原始 args/result_preview/started_at/ended_at)。零后端改动。
 */
const TraceDetailModal: React.FC<{
  message: ChatMessage
  sessionID: string
  userID: string
  groupID: string
  onClose: () => void
}> = ({ message, sessionID, userID, groupID, onClose }) => {
  const steps = message.progressSteps || []
  const traceID = message.traceID || ''
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [copyHint, setCopyHint] = useState<string>('')

  const showHint = (text: string) => {
    setCopyHint(text)
    setTimeout(() => setCopyHint(''), 1500)
  }
  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      showHint(`${label} 已复制`)
    } catch {
      showHint('复制失败，请手动选中')
    }
  }
  // 复 user 这条消息时构造的 curl(方便 dev 快速重放):传 input + session_id + user/group
  const userInput =
    message.role === 'assistant'
      ? // assistant 消息找上一条 user 消息会比较麻烦,这里假设 modal 弹时 message 关联的会话一致
        ''
      : message.content
  const curlSnippet = `curl 'https://webapitest.xhey.top/web/xiaohei-claw/run' \\\n  -H 'accept: text/event-stream' \\\n  -H 'content-type: application/json' \\\n  -b 'USER=ff8cd22a-a74b-48c8-961d-eb6e0fd010b3' \\\n  -H 'origin: https://pic.xhey.top' \\\n  --data-raw '${JSON.stringify(
    {
      input: userInput || '<在此填上轮 user 消息文本>',
      session_id: sessionID,
      user_id: userID,
      group_id: groupID
    }
  )}'`

  // Kibana 跳转(预填 trace_id 关键词;cookie 由用户在 Kibana 自己续)
  const kibanaURL = traceID
    ? `https://kibana-sz-alpha.xhey.top/app/discover#/?_a=(query:(language:kuery,query:'message:%22${encodeURIComponent(
        traceID
      )}%22'))`
    : ''

  // 总耗时:首步 started_at → 末步 ended_at (兜底用 ts)
  const firstStart =
    steps.find((s) => s.started_at)?.started_at ?? steps[0]?.ts ?? 0
  const lastEnd =
    [...steps].reverse().find((s) => s.ended_at)?.ended_at ??
    steps[steps.length - 1]?.ts ??
    firstStart
  const totalMs = lastEnd - firstStart

  const fmtMs = (ms?: number) => {
    if (ms == null || isNaN(ms)) return '-'
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(2)}s`
  }
  const fmtOffset = (ts?: number) => {
    if (!ts || !firstStart) return '0.00s'
    return `+${((ts - firstStart) / 1000).toFixed(2)}s`
  }

  return (
    <div className="ai-photo-jun-panel__trace-modal" role="dialog" aria-modal>
      <div className="trace-modal-head">
        <div className="trace-modal-title">调用链路 · 诊断视图</div>
        <button
          type="button"
          className="trace-modal-close"
          onClick={onClose}
          aria-label="关闭"
        >
          ×
        </button>
      </div>
      <div className="trace-modal-meta">
        <div className="meta-row">
          <span className="meta-label">trace_id</span>
          <span className="meta-value">{traceID || '(本轮无 trace_id)'}</span>
          {traceID && (
            <button
              type="button"
              className="meta-btn"
              onClick={() => copy(traceID, 'trace_id')}
            >
              复制
            </button>
          )}
          {kibanaURL && (
            <a
              className="meta-btn"
              href={kibanaURL}
              target="_blank"
              rel="noreferrer"
            >
              Kibana
            </a>
          )}
        </div>
        <div className="meta-row">
          <span className="meta-label">session</span>
          <span className="meta-value">{sessionID}</span>
          <button
            type="button"
            className="meta-btn"
            onClick={() => copy(sessionID, 'session_id')}
          >
            复制
          </button>
        </div>
        <div className="meta-row">
          <span className="meta-label">总耗时</span>
          <span className="meta-value">{fmtMs(totalMs)}</span>
          <span className="meta-label">步骤</span>
          <span className="meta-value">{steps.length}</span>
        </div>
        <div className="meta-row">
          <button
            type="button"
            className="meta-btn meta-btn--primary"
            onClick={() => copy(curlSnippet, 'curl 命令')}
          >
            复制 curl(重放本会话)
          </button>
          {copyHint && <span className="meta-hint">{copyHint}</span>}
        </div>
      </div>
      <div className="trace-modal-body">
        {steps.length === 0 && (
          <div className="trace-empty">本轮没有抓到工具调用。</div>
        )}
        {steps.map((s, i) => {
          const isOpen = !!expanded[s.id]
          const dur =
            s.started_at && s.ended_at ? s.ended_at - s.started_at : undefined
          const statusIcon =
            s.status === 'ok' ? '✓' : s.status === 'fail' ? '✗' : '…'
          return (
            <div key={s.id} className={`trace-step status-${s.status}`}>
              <button
                type="button"
                className="trace-step-head"
                onClick={() =>
                  setExpanded((prev) => ({ ...prev, [s.id]: !prev[s.id] }))
                }
              >
                <span className="step-idx">{i + 1}</span>
                <span className="step-status">{statusIcon}</span>
                <span className="step-tool">{s.tool_name || s.label.done}</span>
                <span className="step-offset">{fmtOffset(s.started_at)}</span>
                <span className="step-dur">{fmtMs(dur)}</span>
                {s.round != null && (
                  <span className="step-round">round {s.round}</span>
                )}
                <span className="step-toggle">{isOpen ? '−' : '+'}</span>
              </button>
              {isOpen && (
                <div className="trace-step-body">
                  {s.args_raw && (
                    <div className="kv">
                      <div className="kv-label">
                        args
                        <button
                          type="button"
                          className="kv-copy"
                          onClick={() => copy(s.args_raw || '', 'args')}
                        >
                          复制
                        </button>
                      </div>
                      <pre className="kv-pre">{s.args_raw}</pre>
                    </div>
                  )}
                  {s.result_preview && (
                    <div className="kv">
                      <div className="kv-label">
                        result_preview
                        <button
                          type="button"
                          className="kv-copy"
                          onClick={() => copy(s.result_preview || '', 'result')}
                        >
                          复制
                        </button>
                      </div>
                      <pre className="kv-pre">{s.result_preview}</pre>
                    </div>
                  )}
                  {!s.args_raw && !s.result_preview && (
                    <div className="kv-empty">
                      无 args / result(可能是中间事件)
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * InteractiveAskCard —— 后端 ask_user_question 事件的 UI 渲染。
 */
/**
 * PlanApprovalCard —— propose_plan 事件的气泡内渲染。
 * 两按钮:同意执行(绿色,high risk 时红色警告) / 我要调整(灰色)。
 * answered 非空 = 只读态(已作答)。
 */
const PlanApprovalCard: React.FC<{
  messageId: string
  plan: PlanPayload
  onApprove: (messageId: string) => void
  /** 点 "我要调整" 进入编辑态后,提交时把改后的 steps 传出去 */
  onSubmitEdit: (messageId: string, editedSteps: PlanStep[]) => void
  disabled: boolean
}> = ({ messageId, plan, onApprove, onSubmitEdit, disabled }) => {
  const answered = plan.answered
  const riskClass = plan.risk_level ? ` risk-${plan.risk_level}` : ''
  // 编辑态本地草稿:点 "我要调整" 进入,points "取消调整" 退出,点 "提交" 触发 onSubmitEdit
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<PlanStep[]>(plan.steps)
  const enterEdit = () => {
    setDraft(plan.steps.map((s) => ({ ...s })))
    setEditing(true)
  }
  const cancelEdit = () => {
    setEditing(false)
    setDraft(plan.steps)
  }
  const updateStep = (
    idx: number,
    field: 'title' | 'description',
    v: string
  ) => {
    setDraft((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, [field]: v } : s))
    )
  }
  const submitEdit = () => {
    if (disabled) return
    // 至少保留一个非空 title 的步骤,空 title 自动用原文本兜底
    const clean = draft.map((s, i) => ({
      title: (s.title || '').trim() || plan.steps[i]?.title || '步骤',
      description: (s.description || '').trim() || undefined
    }))
    setEditing(false)
    onSubmitEdit(messageId, clean)
  }
  return (
    <div
      className={
        'ai-photo-jun-panel__plan-card' +
        riskClass +
        (editing ? ' is-editing' : '')
      }
    >
      <div className="ai-photo-jun-panel__plan-header">
        <span className="plan-icon" aria-hidden>
          <IconClipboard />
        </span>
        <span className="plan-title">执行计划</span>
        {plan.estimated_time && (
          <span className="plan-meta">· {plan.estimated_time}</span>
        )}
        {plan.risk_level === 'high' && (
          <span className="plan-risk">高风险</span>
        )}
      </div>
      {plan.summary && (
        <div className="ai-photo-jun-panel__plan-summary">{plan.summary}</div>
      )}
      <ol className="ai-photo-jun-panel__plan-steps">
        {(editing ? draft : plan.steps).map((s, i) => (
          <li key={i} className="plan-step">
            {editing ? (
              <>
                <input
                  className="plan-step-title-input"
                  value={s.title}
                  disabled={disabled}
                  placeholder="步骤标题"
                  onChange={(e) => updateStep(i, 'title', e.target.value)}
                />
                <textarea
                  className="plan-step-desc-input"
                  value={s.description ?? ''}
                  disabled={disabled}
                  placeholder="补充说明（可选）"
                  rows={2}
                  onChange={(e) => updateStep(i, 'description', e.target.value)}
                />
              </>
            ) : (
              <>
                <span className="plan-step-title">{s.title}</span>
                {s.description && (
                  <span className="plan-step-desc">{s.description}</span>
                )}
              </>
            )}
          </li>
        ))}
      </ol>
      {!answered && !editing && (
        <div className="ai-photo-jun-panel__plan-actions">
          <button
            type="button"
            className={
              'ai-photo-jun-panel__plan-btn is-approve' +
              (plan.risk_level === 'high' ? ' is-danger' : '')
            }
            disabled={disabled}
            onClick={() => onApprove(messageId)}
          >
            ✓ 同意执行
          </button>
          <button
            type="button"
            className="ai-photo-jun-panel__plan-btn is-reject"
            disabled={disabled}
            onClick={enterEdit}
          >
            ✎ 我要调整
          </button>
        </div>
      )}
      {!answered && editing && (
        <div className="ai-photo-jun-panel__plan-actions">
          <button
            type="button"
            className="ai-photo-jun-panel__plan-btn is-cancel"
            disabled={disabled}
            onClick={cancelEdit}
          >
            取消调整
          </button>
          <button
            type="button"
            className="ai-photo-jun-panel__plan-btn is-approve"
            disabled={disabled}
            onClick={submitEdit}
          >
            提交
          </button>
        </div>
      )}
      {answered && (
        <div className="ai-photo-jun-panel__plan-answered">
          {answered === 'approved' ? '✓ 已同意执行' : '✎ 已按调整提交'}
        </div>
      )}
    </div>
  )
}

/**
 * PanelDrawer —— 覆盖在消息列表上的抽屉容器。点击标题右边 × 关闭。
 */
const PanelDrawer: React.FC<{
  title: string
  onClose: () => void
  children?: React.ReactNode
}> = ({ title, onClose, children }) => (
  <div className="ai-photo-jun-panel__drawer" aria-modal="true">
    <div className="ai-photo-jun-panel__drawer-header">
      <span className="drawer-title">{title}</span>
      <Tooltip
        title="关闭"
        placement="bottom"
        mouseEnterDelay={0.1}
        classNames={{ root: 'ai-photo-jun-panel__header-tooltip' }}
      >
        <button
          type="button"
          className="ai-photo-jun-panel__header-btn drawer-close"
          onClick={onClose}
          aria-label="关闭抽屉"
        >
          <IconCloseLine />
        </button>
      </Tooltip>
    </div>
    <div className="ai-photo-jun-panel__drawer-body">{children}</div>
  </div>
)

/**
 * SessionListDrawerBody —— "历史会话"抽屉。
 * - 按时间分组(今天 / 过去 7 天 / 更早)
 * - 标题优先用 LLM 生成的 title;为空时 fallback 到 first_input(截到 60 字)
 * - 点条目 → 切换到那个 session;当前 session 高亮但不响应点击
 */
const SessionListDrawerBody: React.FC<{
  loading: boolean
  items: SessionPreview[]
  currentSessionId: string
  onPick: (sessionID: string) => void
}> = ({ loading, items, currentSessionId, onPick }) => {
  if (loading) {
    return (
      <div className="ai-photo-jun-panel__drawer-empty">
        <Spin size="small" /> 正在加载...
      </div>
    )
  }
  if (items.length === 0) {
    return (
      <div className="ai-photo-jun-panel__drawer-empty">
        <div className="ai-photo-jun-panel__drawer-empty-title">
          还没有历史会话
        </div>
        <div>产生会话消息后会，自动出现在这里</div>
      </div>
    )
  }
  // 分组: 今天 / 过去 7 天 / 更早
  const now = Date.now()
  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  const startOfTodayMs = startOfToday.getTime()
  const sevenDaysAgo = startOfTodayMs - 7 * 24 * 60 * 60 * 1000
  const groups: Array<{ label: string; items: SessionPreview[] }> = [
    { label: '今天', items: [] },
    { label: '过去 7 天', items: [] },
    { label: '更早', items: [] }
  ]
  items.forEach((it) => {
    const ts = new Date(it.last_at).getTime()
    if (Number.isNaN(ts)) {
      groups[2].items.push(it)
    } else if (ts >= startOfTodayMs) {
      groups[0].items.push(it)
    } else if (ts >= sevenDaysAgo) {
      groups[1].items.push(it)
    } else {
      groups[2].items.push(it)
    }
  })
  const formatTimeShort = (iso: string): string => {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    const ms = now - d.getTime()
    if (ms < 60_000) return '刚刚'
    if (ms < 3600_000) return `${Math.floor(ms / 60_000)} 分钟前`
    if (d.getTime() >= startOfTodayMs) {
      return `${String(d.getHours()).padStart(2, '0')}:${String(
        d.getMinutes()
      ).padStart(2, '0')}`
    }
    return `${d.getMonth() + 1}/${d.getDate()}`
  }
  return (
    <div className="ai-photo-jun-panel__session-list">
      {groups.map(
        (g) =>
          g.items.length > 0 && (
            <div key={g.label} className="session-group">
              <div className="session-group-label">{g.label}</div>
              {g.items.map((it) => {
                const isCurrent = it.session_id === currentSessionId
                const display = (it.title || it.first_input || '新会话').trim()
                return (
                  <button
                    key={it.session_id}
                    type="button"
                    className={'session-row' + (isCurrent ? ' is-current' : '')}
                    onClick={() => !isCurrent && onPick(it.session_id)}
                    disabled={isCurrent}
                    title={display}
                  >
                    <div className="session-row-title">
                      {display}
                      {isCurrent && (
                        <span className="session-row-current"> · 当前</span>
                      )}
                    </div>
                    <div className="session-row-meta">
                      {formatTimeShort(it.last_at)} · {it.message_count} 条
                    </div>
                  </button>
                )
              })}
            </div>
          )
      )}
    </div>
  )
}

/**
 * PlanDrawerBody —— 抽屉里"执行计划"列表。
 * 从 messages[] 扫出所有带 plan 的 assistant 消息,按最新在上展示。
 * 点击条目 → 抽屉关掉 + 滚到主聊天里对应消息。
 */
const PlanDrawerBody: React.FC<{
  messages: ChatMessage[]
  onScrollTo: (msgId: string) => void
}> = ({ messages, onScrollTo }) => {
  const items = messages
    .filter((m) => m.role === 'assistant' && m.plan)
    .map((m) => ({ id: m.id, plan: m.plan as PlanPayload }))
    .reverse()
  if (items.length === 0) {
    return (
      <div className="ai-photo-jun-panel__drawer-empty">
        本次还没有要你确认的计划。AI 遇到多步操作时，会在这里先把方案给你过目。
      </div>
    )
  }
  return (
    <div className="ai-photo-jun-panel__plan-list">
      {items.map((it) => {
        const status =
          it.plan.answered === 'approved'
            ? '已执行'
            : it.plan.answered === 'rejected'
              ? '已调整'
              : '待确认'
        return (
          <button
            key={it.id}
            type="button"
            className={
              'ai-photo-jun-panel__plan-row' +
              (it.plan.answered ? ' is-done' : ' is-pending')
            }
            onClick={() => onScrollTo(it.id)}
          >
            <div className="plan-row-head">
              <span className="plan-row-summary">{it.plan.summary}</span>
              <span className="plan-row-status">{status}</span>
            </div>
            <div className="plan-row-meta">
              {it.plan.steps.length} 步
              {it.plan.estimated_time && ` · ${it.plan.estimated_time}`}
            </div>
          </button>
        )
      })}
    </div>
  )
}

/**
 * DownloadDrawerBody —— 抽屉里"下载 / 生成任务"列表。
 * 展示 pending(旋转图标)/ done(缩略图 + 下载链接)/ error(红字)。
 */
const DownloadDrawerBody: React.FC<{
  items: DownloadTask[]
  /** 当前会话 ID(c8132 埋点) */
  sessionID?: string
  onBeforeDownload?: (isExempt?: boolean) => boolean
  /** 下载动作接管(下载前扣积分):传了 → 用它真正下载;不传 → 默认 triggerArtifactDownload */
  onDownload?: (url: string, filename?: string, taskId?: string) => void
}> = ({ items, sessionID = '', onBeforeDownload, onDownload }) => {
  const vipKind = useVipKind()
  const payment = usePayment()
  /** c8132 点击埋点(下载链接/原图/会员专属任务), idx 为列表序号(1-based) */
  const trackItemClick = (
    clickItem: '点击下载链接' | '点击原图' | '点击会员专属任务',
    t: DownloadTask,
    idx: number
  ) => {
    trackAiC8132Click({
      sessionID,
      clickItem,
      taskType:
        t.kind === 'image'
          ? 'image'
          : fileNameToTaskType(t.filename || t.url || ''),
      index: idx + 1,
      fileName: t.filename || t.prompt || '',
      taskID: t.task_id,
      msgID: ''
    })
  }
  if (items.length === 0) {
    return (
      <div className="ai-photo-jun-panel__drawer-empty">
        <div className="ai-photo-jun-panel__drawer-empty-title">
          还没有下载记录
        </div>
        <div>AI 帮你画图/出报表时，文件会出现在这里</div>
      </div>
    )
  }
  return (
    <div className="ai-photo-jun-panel__download-list">
      {items.map((t, idx) => {
        const isFile = t.kind === 'file'
        const isImage = t.kind === 'image'
        const openLabel = isFile ? '下载' : '打开原图'
        // 产品 2026-06 调整:全部下载都要会员,不再按导出类型豁免(isExempt 恒 false)
        const isExempt = false
        // 已就绪的任务非会员一律锁;生成中(pending)还不能下载,不锁
        const isMemberLocked = t.status === 'done' && !vipKind
        return (
          <div
            key={t.task_id}
            className={
              'ai-photo-jun-panel__download-row is-' +
              t.status +
              ' kind-' +
              t.kind
            }
          >
            {t.status === 'done' && t.url && isImage && (
              <a
                className="dl-thumb"
                href={t.url}
                onClick={(e) => {
                  e.preventDefault()
                  trackItemClick('点击原图', t, idx)
                  // onBeforeDownload = checkVipBeforeDownload,非会员跳高级会员(VVIP)
                  if (onBeforeDownload && onBeforeDownload(isExempt))
                    window.open(t.url, '_blank', 'noopener,noreferrer')
                }}
              >
                <img src={t.url} alt="" loading="lazy" />
              </a>
            )}
            {t.status === 'done' &&
              isFile &&
              (() => {
                // file 类下:扩展名是图片(png/jpg 等) → 用真实 url 缩略图(产品要求);
                // 其他 → 按扩展名展示彩色 colorful icon + 底色 + 边框
                const fileName = t.filename || t.url || ''
                const showRealImage = !!t.url && isImageByName(fileName)
                const style = resolveFileThumbStyle(fileName)
                return (
                  <a
                    className="dl-thumb dl-thumb--file"
                    href={t.url || '#'}
                    aria-label={`下载 ${t.filename}`}
                    style={
                      showRealImage
                        ? undefined
                        : {
                            background: style.bgColor,
                            borderColor: style.borderColor
                          }
                    }
                    onClick={(e) => {
                      e.preventDefault()
                      trackItemClick('点击下载链接', t, idx)
                      // onBeforeDownload = checkVipBeforeDownload,非会员跳高级会员(VVIP)
                      if (onBeforeDownload && onBeforeDownload(isExempt)) {
                        if (onDownload)
                          onDownload(t.url!, t.filename, t.task_id)
                        else void triggerArtifactDownload(t.url!, t.filename)
                      }
                    }}
                  >
                    {showRealImage ? (
                      <img src={t.url} alt="" loading="lazy" />
                    ) : (
                      <img
                        src={style.iconSrc}
                        alt=""
                        className="dl-thumb__file-icon"
                      />
                    )}
                  </a>
                )
              })()}
            {t.status !== 'done' && (
              <div className="dl-thumb dl-thumb--placeholder">
                {t.status === 'pending' ? <Spin size="small" /> : '✗'}
              </div>
            )}
            <div className="dl-info">
              <div className="dl-prompt" title={t.prompt}>
                {t.prompt}
              </div>
              <div className="dl-meta">
                {t.status === 'pending' && (
                  <span className="dl-meta-text">生成中...</span>
                )}
                {t.status === 'done' && (
                  <>
                    <span className="dl-meta-text">
                      已生成
                      {t.provider ? ` · ${t.provider}` : ''}
                    </span>
                    {/* 统一蓝色 CTA 胶囊按钮(文案/icon 一致),非会员跳开通页,
                        会员 file 走 chargeThenDownload 扣积分,会员 image 直接打开原图 */}
                    {t.url && (
                      <a
                        className="dl-link dl-link--vip"
                        role="button"
                        onClick={(e) => {
                          e.preventDefault()
                          if (isMemberLocked) {
                            // 神策埋点 ai_b8112_c8132_click: 点击会员专属任务
                            trackItemClick('点击会员专属任务', t, idx)
                            // 文件下载的会员门是 VVIP(checkVipBeforeDownload 同口径),
                            // 跳开通页统一定位到高级会员,避免跳基础会员后用户开通了仍下不了
                            payment.openVipByKind(
                              CONFIG.VIP_KIND.VVIP,
                              AI_PHOTO_KING_SOURCE_DETAIL,
                              false,
                              aiPhotoPaywallCustomContent({
                                vipKind,
                                usedCredits: 0,
                                trackSource: '下载二级页面会员专属任务'
                              })
                            )
                            return
                          }
                          if (isFile) {
                            trackItemClick('点击下载链接', t, idx)
                            if (
                              onBeforeDownload &&
                              onBeforeDownload(isExempt)
                            ) {
                              if (onDownload)
                                onDownload(t.url!, t.filename, t.task_id)
                              else
                                void triggerArtifactDownload(t.url!, t.filename)
                            }
                            return
                          }
                          // 图片类:会员校验后新窗口打开原图(不走扣费链路)
                          trackItemClick('点击原图', t, idx)
                          if (onBeforeDownload && onBeforeDownload(isExempt))
                            window.open(t.url, '_blank', 'noopener,noreferrer')
                        }}
                      >
                        {isFile ? '下载文件' : '查看大图'} <IconVipCrown />
                      </a>
                    )}
                  </>
                )}
                {t.status === 'error' && (
                  <span className="dl-meta-text dl-err">
                    失败:{t.err_msg || '未知错误'}
                  </span>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

const InteractiveAskCard: React.FC<{
  messageId: string
  ask: AskPayload
  onAnswer: (
    messageId: string,
    answers: Array<{ question: AskQuestion; labels: string[] }>
  ) => void
  disabled: boolean
}> = ({ messageId, ask, onAnswer, disabled }) => {
  const [pending, setPending] = useState<Record<string, Set<string>>>({})
  const [customDraft, setCustomDraft] = useState<Record<string, string>>({})
  const [customText, setCustomText] = useState<Record<string, string>>({})
  const [editingCustom, setEditingCustom] = useState<Record<string, boolean>>(
    {}
  )

  const hasAnswered = !!ask.answered && Object.keys(ask.answered).length > 0
  const allAnswered = ask.questions.every((q) => {
    const s = pending[q.question]
    return s && s.size > 0
  })

  const selectLabel = (q: AskQuestion, label: string) => {
    setPending((prev) => {
      const next = new Set(prev[q.question] || [])
      if (q.multi_select) {
        if (next.has(label)) next.delete(label)
        else next.add(label)
      } else {
        next.clear()
        next.add(label)
      }
      return { ...prev, [q.question]: next }
    })
  }

  const enterCustomEdit = (q: AskQuestion) => {
    if (disabled || hasAnswered) return
    setEditingCustom((prev) => ({ ...prev, [q.question]: true }))
    setCustomDraft((prev) => ({
      ...prev,
      [q.question]: prev[q.question] ?? customText[q.question] ?? ''
    }))
    // 单选模式:进入自定义编辑视为"切换到自定义这条",清空其他选项的选中态;
    // 已有自定义文本作为保留(commit 时若 draft 为空,旧文本仍在 customText 中)。
    // 多选模式:保持其他选项的选中,自定义只是其中一条。
    if (!q.multi_select) {
      const keepCustom = customText[q.question]
      setPending((prev) => {
        const next = new Set<string>()
        if (keepCustom) next.add(keepCustom)
        return { ...prev, [q.question]: next }
      })
    }
  }

  const commitCustomEdit = (q: AskQuestion) => {
    const draft = (customDraft[q.question] || '').trim()
    setEditingCustom((prev) => ({ ...prev, [q.question]: false }))
    if (!draft) return
    const prevCustom = customText[q.question]
    if (prevCustom && prevCustom !== draft) {
      setPending((prev) => {
        const next = new Set(prev[q.question] || [])
        next.delete(prevCustom)
        return { ...prev, [q.question]: next }
      })
    }
    setCustomText((prev) => ({ ...prev, [q.question]: draft }))
    selectLabel(q, draft)
  }

  const clearCustom = (q: AskQuestion) => {
    if (disabled || hasAnswered) return
    const text = customText[q.question]
    // 清掉已确认的自定义文本及其选中态;同时回到 editing 态(空 input),
    // 视觉上仍是"选中自定义"——蓝边框输入框聚焦,允许用户立刻重新输入。
    // 单选模式下不再额外恢复其他选项的选中,因为用户的意图是"还要用自定义"。
    setPending((prev) => {
      const next = new Set(prev[q.question] || [])
      if (text) next.delete(text)
      return { ...prev, [q.question]: next }
    })
    setCustomText((prev) => ({ ...prev, [q.question]: '' }))
    setCustomDraft((prev) => ({ ...prev, [q.question]: '' }))
    setEditingCustom((prev) => ({ ...prev, [q.question]: true }))
  }

  const handleSubmit = () => {
    if (disabled || hasAnswered || !allAnswered) return
    const answers = ask.questions.map((q) => ({
      question: q,
      labels: Array.from(pending[q.question] || [])
    }))
    onAnswer(messageId, answers)
  }

  return (
    <div className="ai-photo-jun-panel__ask-card">
      {ask.questions.map((q) => {
        const answered = ask.answered?.[q.question]
        const selectedSet = pending[q.question] || new Set<string>()
        const readonly = disabled || hasAnswered
        return (
          <div className="ai-photo-jun-panel__ask-question" key={q.question}>
            <div className="ai-photo-jun-panel__ask-header">{q.header}</div>
            <div className="ai-photo-jun-panel__ask-title">{q.question}</div>
            <div className="ai-photo-jun-panel__ask-options">
              {q.options.map((opt) => {
                const selected = selectedSet.has(opt.label)
                return (
                  <button
                    key={opt.label}
                    type="button"
                    className={
                      'ai-photo-jun-panel__ask-option' +
                      (selected ? ' is-selected' : '') +
                      (hasAnswered ? ' is-answered' : '')
                    }
                    disabled={readonly}
                    onClick={() => {
                      if (readonly) return
                      selectLabel(q, opt.label)
                    }}
                    title={opt.description}
                  >
                    {opt.label}
                  </button>
                )
              })}

              {renderCustomSlot({
                q,
                readonly,
                editing: !!editingCustom[q.question],
                draft: customDraft[q.question] ?? '',
                text: customText[q.question] ?? '',
                selected: !!(
                  customText[q.question] &&
                  selectedSet.has(customText[q.question])
                ),
                onDraftChange: (v) =>
                  setCustomDraft((prev) => ({ ...prev, [q.question]: v })),
                onEnterEdit: () => enterCustomEdit(q),
                onCommit: () => commitCustomEdit(q),
                onCancelEdit: () => {
                  setEditingCustom((prev) => ({
                    ...prev,
                    [q.question]: false
                  }))
                  setCustomDraft((prev) => ({
                    ...prev,
                    [q.question]: customText[q.question] ?? ''
                  }))
                },
                onToggle: () =>
                  customText[q.question] &&
                  selectLabel(q, customText[q.question]),
                onClear: () => clearCustom(q)
              })}
            </div>

            {answered && (
              <div className="ai-photo-jun-panel__ask-answered">
                已选:{answered}
              </div>
            )}
          </div>
        )
      })}

      {!hasAnswered && (
        <div className="ai-photo-jun-panel__ask-footer">
          <button
            type="button"
            className="ai-photo-jun-panel__ask-submit"
            disabled={disabled || !allAnswered}
            onClick={handleSubmit}
          >
            {allAnswered
              ? '提交回答'
              : `还有 ${
                  ask.questions.length -
                  ask.questions.filter(
                    (q) => (pending[q.question]?.size ?? 0) > 0
                  ).length
                } 个问题未选`}
          </button>
        </div>
      )}
    </div>
  )
}

const renderCustomSlot = ({
  q,
  readonly,
  editing,
  draft,
  text,
  selected,
  onDraftChange,
  onEnterEdit,
  onCommit,
  onCancelEdit,
  onToggle,
  onClear
}: {
  q: AskQuestion
  readonly: boolean
  editing: boolean
  draft: string
  text: string
  selected: boolean
  onDraftChange: (v: string) => void
  onEnterEdit: () => void
  onCommit: () => void
  onCancelEdit: () => void
  onToggle: () => void
  onClear: () => void
}) => {
  void q
  if (editing) {
    return (
      <span
        key="__custom__"
        className="ai-photo-jun-panel__ask-option ai-photo-jun-panel__ask-option--editing"
      >
        <input
          className="ai-photo-jun-panel__ask-custom-input"
          autoFocus
          value={draft}
          disabled={readonly}
          placeholder="输入自定义内容，回车确认"
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onCommit()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              onCancelEdit()
            }
          }}
          onBlur={onCommit}
        />
      </span>
    )
  }
  if (text) {
    return (
      <span
        key="__custom__"
        className={
          'ai-photo-jun-panel__ask-option ai-photo-jun-panel__ask-option--custom' +
          (selected ? ' is-selected' : '')
        }
      >
        <button
          type="button"
          className="ai-photo-jun-panel__ask-custom-text"
          disabled={readonly}
          onClick={() => {
            if (readonly) return
            onToggle()
          }}
          title={text}
        >
          {text}
        </button>
        {!readonly && (
          <span
            className="ai-photo-jun-panel__ask-custom-clear"
            onClick={(e) => {
              e.stopPropagation()
              onClear()
            }}
            aria-label="清除自定义"
            role="button"
          >
            ×
          </span>
        )}
      </span>
    )
  }
  return (
    <button
      key="__custom__"
      type="button"
      className="ai-photo-jun-panel__ask-option ai-photo-jun-panel__ask-option--custom-idle"
      disabled={readonly}
      onClick={() => {
        if (readonly) return
        onEnterEdit()
      }}
    >
      自定义
    </button>
  )
}

/** 卡片埋点上下文 (神策 ai_b8112_click clickItem=点击卡片项 用) */
interface CardTrackContext {
  sessionId: string
  /** 本 card 所在 assistant 消息的后端 messageId */
  msgId: string
  /** 第几个 AI 回答(1-based, 跟 SuggestionsCard rankNum 同语义) */
  rankNum: number
}

function renderCard(
  card: CardPayload,
  key: number,
  onApplyPageFilter?: (payload: PageFilterPayload) => void,
  onApplyNlQuery?: (text: string) => void,
  trackCtx?: CardTrackContext
) {
  switch (card.type) {
    case 'ranking':
      return (
        <RankingCard
          key={key}
          card={card}
          onApplyPageFilter={onApplyPageFilter}
          trackCtx={trackCtx}
        />
      )
    case 'stat':
      return (
        <StatCard
          key={key}
          card={card}
          onApplyNlQuery={onApplyNlQuery}
          onApplyPageFilter={onApplyPageFilter}
          trackCtx={trackCtx}
        />
      )
    case 'html':
      return <HtmlCard key={key} card={card} />
    default:
      return null
  }
}

const RankingCard: React.FC<{
  card: CardRanking
  onApplyPageFilter?: (payload: PageFilterPayload) => void
  trackCtx?: CardTrackContext
}> = ({ card, onApplyPageFilter, trackCtx }) => {
  // 「照片地图」浮窗开关 + 这张卡的整体范围(顶层口径,不含某一行的维度差异),给地图用
  const [mapVisible, setMapVisible] = useState(false)
  // 照片地图 = VVIP 专属 + 免费体验一次,统一走 useMapEntryGate
  const { openMapGuarded, handleMapClose } = useMapEntryGate()
  const cardScope: FilterStructured = {
    keywords: card.keywords,
    user_ids: card.user_ids,
    exclude_user_ids: card.exclude_user_ids,
    department_ids: card.department_ids,
    start_time: card.start_time || card.time_start,
    end_time: card.end_time || card.time_end,
    source_types: card.source_types,
    media_types: card.media_types,
    watermarks: card.watermarks,
    locations: card.locations,
    location_key: card.location_key,
    exclude_keywords: card.exclude_keywords,
    exist_watermark: card.exist_watermark,
    label_ids: card.label_ids,
    label_match_all: card.label_match_all
  }
  // 通用可点击逻辑:item 带 filter_payload(任意 V5 维度) 或 user_id(老兼容) 时可点击。
  // 点击 = card 顶层口径(时间/关键词等) + item 维度差异 合并后跳主页 filter。
  const itemFilter = (it: RankingItem): FilterStructured | null => {
    if (it.filter_payload) return it.filter_payload
    if (it.user_id) return { user_ids: [it.user_id] }
    return null
  }
  // 兜底: LLM 偶尔会把地名也塞进 keywords(prompt 改了但难 100% 保证),
  // 跟 locations.name 重合的 keywords 自动剔除,避免"水印精确匹配 + 地点"双重过滤跳出 0 张。
  const sanitizeKeywords = (
    keywords: string[] | undefined,
    locations: FilterLocation[] | undefined
  ): string[] | undefined => {
    if (!keywords || keywords.length === 0) return keywords
    if (!locations || locations.length === 0) return keywords
    const locNames = new Set(locations.map((l) => l.name).filter(Boolean))
    const filtered = keywords.filter((k) => !locNames.has(k))
    return filtered.length > 0 ? filtered : undefined
  }
  // 排行气球:每个排行项(人/天/部门/城市…任意维度)按它自己的范围拉照片打到地图,按名次着色。
  // 行级范围 = 卡片顶层口径(基底) + 本项维度差异(itemFilter,覆盖),与点击跳转(handlePick)同口径。
  // - 地点维度(itf 带 locations)跳过 → 留给地图「区县→POI 地点聚合」模式,不做"地点按地点上色"。
  // - 其余维度(user_ids/部门/时间/水印…)进气球。userID 字段在地图里承载「排行项 key」(名次串)。
  // - 日期名「2026-06-02 08:00:00」截到「2026-06-02」,免得气球/侧栏太长。
  const balloonRankItems = card.items
    .map((it) => {
      const itf = itemFilter(it)
      if (!itf) return null
      if (itf.locations && itf.locations.length > 0) return null
      const rowScope: FilterStructured = { ...cardScope, ...itf }
      rowScope.keywords = sanitizeKeywords(
        rowScope.keywords,
        rowScope.locations
      )
      const dateMatch = it.name.match(/^(\d{4}-\d{2}-\d{2})[ T]/)
      return {
        rank: it.rank,
        name: dateMatch ? dateMatch[1] : it.name,
        userID: `rank-${it.rank}`,
        mapFilter: structuredToMapFilter(rowScope)
      }
    })
    .filter((x): x is NonNullable<typeof x> => !!x)
  const handlePick = (it: RankingItem) => {
    const itf = itemFilter(it)
    if (!onApplyPageFilter || !itf) return
    // 神策埋点 ai_b8112_click: 点击卡片项 (typeName 用平台中文枚举「排行表」)
    trackAiB8112Click({
      clickItem: '点击卡片项',
      sessionID: trackCtx?.sessionId || '',
      msgID: trackCtx?.msgId || '',
      rankNum: trackCtx?.rankNum || 0,
      typeName: '排行表',
      place: '进入查看'
    })
    const totalNum = Number(it.value.replace(/[,，]/g, ''))
    const scopeLabel = card.subtitle || card.title || ''
    const nlQuery = scopeLabel
      ? `${scopeLabel} · ${it.name}`
      : `${it.name}的照片`
    // 兼容:旧版 ranking 用 time_start/time_end,新版 backend 也可能用 start_time/end_time(继承自 FilterStructured)
    const startTime = card.start_time || card.time_start
    const endTime = card.end_time || card.time_end
    // 合并顺序: 卡片顶层口径 (基底) → item.filter_payload (覆盖维度差异)
    // 历史漏点:之前没把 card.user_ids 列进基底,所以"黄强近30天地点 Top10"这种**顶层限定某个人 +
    //   item 维度=地点**的 ranking,点 item 跳主页时 user_ids 直接丢了,变成全员在某地点统计
    //   (2026-04-29 实测 96 张 → 跳过去 249 张,即所有人在北城国际中心的总数)
    const merged: FilterStructured = {
      start_time: startTime,
      end_time: endTime,
      keywords: card.keywords,
      user_ids: card.user_ids,
      department_ids: card.department_ids,
      exclude_user_ids: card.exclude_user_ids,
      source_types: card.source_types,
      media_types: card.media_types,
      watermarks: card.watermarks,
      locations: card.locations,
      location_key: card.location_key,
      exclude_keywords: card.exclude_keywords,
      exist_watermark: card.exist_watermark,
      label_ids: card.label_ids,
      label_match_all: card.label_match_all,
      ...itf
    }
    // 兜底: 剔除"被误塞为 keywords 的地名"
    merged.keywords = sanitizeKeywords(merged.keywords, merged.locations)
    // 按"成员"维度的 ranking, 把 it.name(真人姓名)随 user_id 一起带过去,
    // 让主页"筛选人员"chip 显示正确人名而不是 nl_query 字符串(本周回归过)
    const displayNames =
      merged.user_ids && merged.user_ids.length > 0 && it.user_id
        ? [it.name]
        : undefined
    onApplyPageFilter({
      nl_query: nlQuery,
      ...merged,
      user_display_names: displayNames,
      total: isFinite(totalNum) ? totalNum : undefined
    })
  }
  return (
    <div className="ai-photo-jun-panel__card ai-photo-jun-panel__card-ranking">
      {/* 标题行:左标题 + 右侧「照片地图」入口(陈丹阳建议从底部挪到标题行右侧空白处) */}
      <div className="ai-photo-jun-panel__card-ranking-titlebar">
        <div className="ai-photo-jun-panel__card-title" title={card.title}>
          {card.title}
        </div>
        <button
          type="button"
          className="ai-photo-jun-panel__card-map-entry"
          title="在地图上查看"
          onClick={() => {
            // ai_b8112_click: 点击卡片项(排行表)打开地图(范 2026-06-23 spec place=打开地图)
            if (trackCtx) {
              trackAiB8112Click({
                clickItem: '点击卡片项',
                sessionID: trackCtx.sessionId,
                msgID: trackCtx.msgId,
                rankNum: trackCtx.rankNum,
                typeName: '排行表',
                place: '打开地图'
              })
            }
            openMapGuarded(() => setMapVisible(true))
          }}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
            <path
              fill="currentColor"
              d="M12 2a7 7 0 0 0-7 7c0 4.9 7 13 7 13s7-8.1 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z"
            />
          </svg>
          在地图上查看
        </button>
      </div>
      {card.subtitle && (
        <div className="ai-photo-jun-panel__card-subtitle">{card.subtitle}</div>
      )}
      <div className="ai-photo-jun-panel__card-ranking-head">
        <span className="r-col-rank">排名</span>
        <span className="r-col-name">姓名</span>
        <span className="r-col-value">数值</span>
      </div>
      {card.items.map((it) => {
        const clickable = !!(itemFilter(it) && onApplyPageFilter)
        return (
          <div
            key={`${it.rank}-${it.name}`}
            className="ai-photo-jun-panel__card-ranking-row"
          >
            <span className="r-col-rank">{renderRank(it.rank)}</span>
            <span className="r-col-name" title={it.name}>
              {it.name}
            </span>
            <span className="r-col-value">
              {clickable ? (
                <button
                  type="button"
                  className="r-col-value-link"
                  onClick={() => handlePick(it)}
                  title={`查看 ${it.name} 的照片`}
                >
                  <strong>{it.value}</strong>
                  {it.unit && <span className="r-col-unit">{it.unit}</span>}
                </button>
              ) : (
                <>
                  <strong>{it.value}</strong>
                  {it.unit && <span className="r-col-unit">{it.unit}</span>}
                </>
              )}
            </span>
          </div>
        )
      })}
      {card.items.some((it) => it.hint) && (
        <div className="ai-photo-jun-panel__card-ranking-hints">
          {card.items
            .filter((it) => it.hint)
            .map((it) => (
              <div key={it.rank} className="hint-line">
                · {it.name}:{it.hint}
              </div>
            ))}
        </div>
      )}
      <PhotoMapDrawer
        visible={mapVisible}
        onClose={() => handleMapClose(() => setMapVisible(false))}
        mapFilter={structuredToMapFilter(cardScope)}
        rankItems={balloonRankItems}
      />
    </div>
  )
}

function renderRank(r: number) {
  if (r === 1)
    return (
      <span className="r-medal r-gold">
        <MedalIcon rank={1} />
      </span>
    )
  if (r === 2)
    return (
      <span className="r-medal r-silver">
        <MedalIcon rank={2} />
      </span>
    )
  if (r === 3)
    return (
      <span className="r-medal r-bronze">
        <MedalIcon rank={3} />
      </span>
    )
  return <span className="r-num">{r}</span>
}

const StatCard: React.FC<{
  card: CardStat
  onApplyNlQuery?: (text: string) => void
  onApplyPageFilter?: (payload: PageFilterPayload) => void
  trackCtx?: CardTrackContext
}> = ({ card, onApplyNlQuery, onApplyPageFilter, trackCtx }) => {
  // 「照片地图」浮窗开关 + 门控(与 RankingCard 同款:VVIP 专属 + 免费体验一次)
  const [mapVisible, setMapVisible] = useState(false)
  const { openMapGuarded, handleMapClose } = useMapEntryGate()
  // 兼容后端 render_card 一直发的 time_start/time_end 旧字段名 — 任一存在即视为有时间口径
  const startTime = card.start_time || card.time_start
  const endTime = card.end_time || card.time_end
  // 优先结构化路径：后端给了任一 V5 filter 维度 + 父组件接了 onApplyPageFilter，
  // 直接走主页结构化筛选(完整 12 字段),绕开后端 /search/filter NLP 接口的非确定性。
  // 兜底 NLP 路径：只有 nl_query 时回退到 onApplyNlQuery（旧行为）。
  const hasStructured = !!(
    startTime ||
    endTime ||
    (card.user_ids && card.user_ids.length) ||
    (card.exclude_user_ids && card.exclude_user_ids.length) ||
    (card.keywords && card.keywords.length) ||
    (card.department_ids && card.department_ids.length) ||
    (card.source_types && card.source_types.length) ||
    (card.media_types && card.media_types.length) ||
    (card.watermarks && card.watermarks.length) ||
    (card.locations && card.locations.length) ||
    card.location_key ||
    (card.exclude_keywords && card.exclude_keywords.length) ||
    card.exist_watermark ||
    (card.label_ids && card.label_ids.length)
  )
  const clickable = !!(
    (hasStructured && onApplyPageFilter) ||
    (card.nl_query && onApplyNlQuery)
  )
  // 这张卡背后的完整筛选范围:点大数字跳主页 / 点「在地图上查看」共用同一口径
  const cardScope: FilterStructured = {
    keywords: card.keywords,
    user_ids: card.user_ids,
    exclude_user_ids: card.exclude_user_ids,
    department_ids: card.department_ids,
    start_time: startTime,
    end_time: endTime,
    source_types: card.source_types,
    media_types: card.media_types,
    watermarks: card.watermarks,
    locations: card.locations,
    location_key: card.location_key,
    exclude_keywords: card.exclude_keywords,
    exist_watermark: card.exist_watermark,
    label_ids: card.label_ids,
    label_match_all: card.label_match_all
  }
  const handleClick = () => {
    if (!clickable) return
    // 神策埋点 ai_b8112_click: 点击卡片项 (typeName 用平台中文枚举「数值卡」)
    trackAiB8112Click({
      clickItem: '点击卡片项',
      sessionID: trackCtx?.sessionId || '',
      msgID: trackCtx?.msgId || '',
      rankNum: trackCtx?.rankNum || 0,
      typeName: '数值卡',
      place: '进入查看'
    })
    if (hasStructured && onApplyPageFilter) {
      onApplyPageFilter({
        nl_query: card.nl_query || card.title,
        keywords: card.keywords,
        user_ids: card.user_ids,
        exclude_user_ids: card.exclude_user_ids,
        department_ids: card.department_ids,
        start_time: startTime,
        end_time: endTime,
        source_types: card.source_types,
        media_types: card.media_types,
        watermarks: card.watermarks,
        locations: card.locations,
        location_key: card.location_key,
        exclude_keywords: card.exclude_keywords,
        exist_watermark: card.exist_watermark,
        label_ids: card.label_ids,
        label_match_all: card.label_match_all
      })
      return
    }
    if (card.nl_query && onApplyNlQuery) onApplyNlQuery(card.nl_query)
  }
  return (
    <div className="ai-photo-jun-panel__card ai-photo-jun-panel__card-stat">
      {/* 标题行:左标题 + 右侧「照片地图」入口;仅当这张卡带照片筛选维度时显示(与大数字可点击同条件) */}
      <div className="ai-photo-jun-panel__card-stat-titlebar">
        <div className="ai-photo-jun-panel__card-title" title={card.title}>
          {card.title}
        </div>
        {hasStructured && (
          <button
            type="button"
            className="ai-photo-jun-panel__card-map-entry"
            title="在地图上查看"
            onClick={() => openMapGuarded(() => setMapVisible(true))}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
              <path
                fill="currentColor"
                d="M12 2a7 7 0 0 0-7 7c0 4.9 7 13 7 13s7-8.1 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z"
              />
            </svg>
            在地图上查看
          </button>
        )}
      </div>
      {card.subtitle && (
        <div className="ai-photo-jun-panel__card-subtitle">{card.subtitle}</div>
      )}
      <div className="ai-photo-jun-panel__card-stat-value">
        {clickable ? (
          <button
            type="button"
            className="r-col-value-link"
            onClick={handleClick}
            title={`在主页查看这 ${card.value} 张照片`}
          >
            <strong>{card.value}</strong>
            {card.unit && <span className="s-unit">{card.unit}</span>}
          </button>
        ) : (
          <>
            <strong>{card.value}</strong>
            {card.unit && <span className="s-unit">{card.unit}</span>}
          </>
        )}
      </div>
      {card.trend && (
        <div
          className={
            'ai-photo-jun-panel__card-stat-trend t-' + card.trend.direction
          }
        >
          {card.trend.direction === 'up'
            ? '▲'
            : card.trend.direction === 'down'
              ? '▼'
              : '—'}{' '}
          {card.trend.text}
        </div>
      )}
      <PhotoMapDrawer
        visible={mapVisible}
        onClose={() => handleMapClose(() => setMapVisible(false))}
        mapFilter={structuredToMapFilter(cardScope)}
      />
    </div>
  )
}

const HtmlCard: React.FC<{ card: CardHtml }> = ({ card }) => {
  const srcDoc =
    card.html +
    `\n<script>try{setTimeout(function(){parent.postMessage({__claw_iframe_height:document.body.scrollHeight},'*')},50)}catch(e){}</script>`
  const [height, setHeight] = useState<number>(120)
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const h = (e.data as { __claw_iframe_height?: number })
        ?.__claw_iframe_height
      if (typeof h === 'number' && h > 0 && h < 4000) {
        setHeight(h + 8)
      }
    }
    window.addEventListener('message', onMsg)
    return () => window.removeEventListener('message', onMsg)
  }, [])
  return (
    <div className="ai-photo-jun-panel__card ai-photo-jun-panel__card-html">
      <iframe
        title="embedded"
        sandbox="allow-same-origin allow-scripts"
        srcDoc={srcDoc}
        style={{
          width: '100%',
          height: `${height}px`,
          border: 'none',
          background: 'transparent'
        }}
      />
    </div>
  )
}

export default AiPhotoJunPanel
